/**
 * TUVI - Módulo de Cotizaciones
 * Sistema completo de cotización con selección de actividades, planos y cálculos automáticos
 */

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNotification } from '../../context/NotificationContext';
import { useAuth } from '../../context/AuthContext';
import client from '../../services/graphqlClient';
import * as XLSX from 'xlsx';
import CotizacionPDF from './CotizacionPDF';
import CotizacionViejaPDF from './CotizacionViejaPDF';
import ActualizarBases from './ActualizarBases';
import './Cotizaciones.css';

// Incremento de actividades con el que nace una cotizacion NUEVA (y la copia de
// una Base). El valor vigente se administra en Cotizaciones con permiso y se lee
// del servidor (incrementoCotizacionNueva); este 25 es solo el respaldo mientras
// carga. Las que ya existen conservan el suyo.
const INCREMENTO_NUEVA = 25;
const ESTADOS_BASE = ['base', 'base_cotizador_web'];

// ===== Historial de cambios (auditoría de cotizaciones) =====
// De dónde salió el cambio. Sirve para distinguir una edición manual de un
// recálculo automático, que es justo lo que updated_by no permitía ver.
const ETIQUETA_ORIGEN = {
  creacion: 'Creación',
  edicion: 'Edición manual',
  cambio_estado: 'Cambio de estado',
  ficha_ffe: 'Ficha FFE',
  actualizar_bases: 'Actualizar bases',
  vinculo_proyecto: 'Vínculo a proyecto',
};
const ETIQUETA_CAMPO = {
  valor_final: 'Valor final', subtotal: 'Subtotal', aiu_valor: 'AIU', iva_valor: 'IVA',
  aiu_porcentaje: '% AIU', descuento: 'Descuento', valor_final_descuento: 'Valor con descuento',
  incremento_global: 'Incremento global', estado: 'Estado', nombre_cliente: 'Cliente',
  cedula_cliente: 'Cédula', telefono_cliente: 'Teléfono', correo_cliente: 'Correo',
  proyecto_nombre: 'Proyecto', apartamento: 'Apartamento', direccion: 'Dirección',
  area_m2: 'Área m²', proyecto_id: 'Proyecto vinculado', plano_id: 'Plano', fecha: 'Fecha', notas: 'Notas',
  creacion: 'Creación',
};
const CAMPOS_MONEDA = new Set([
  'valor_final', 'subtotal', 'aiu_valor', 'iva_valor', 'descuento', 'valor_final_descuento',
]);
const formatValorHist = (campo, valor) => {
  if (valor === null || valor === undefined || valor === '') return '—';
  if (CAMPOS_MONEDA.has(campo)) {
    const n = Number(valor);
    if (Number.isFinite(n)) return '$' + Math.round(n).toLocaleString('es-CO');
  }
  return String(valor);
};

// Normaliza texto: elimina tildes, minúsculas, colapsa espacios
const normalizarTxt = (s) => (s || '').toString()
  .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
  .toLowerCase().trim().replace(/\s+/g, ' ');

// Selector de Plano buscable (acepta pegar, ignora tildes/mayúsculas)
const PlanoSelect = ({ value, onChange, planos }) => {
  const [busqueda, setBusqueda] = useState('');
  const [abierto, setAbierto] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setAbierto(false); };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const seleccionado = planos.find(p => String(p.id) === String(value));

  const filtrados = useMemo(() => {
    if (!busqueda) return planos;
    const palabras = normalizarTxt(busqueda).split(' ').filter(Boolean);
    return planos.filter(p => {
      const texto = normalizarTxt(p.nombre || '');
      return palabras.every(w => texto.includes(w));
    });
  }, [busqueda, planos]);

  return (
    <div className="plano-select-buscable" ref={ref}>
      <button
        type="button"
        className="plano-select-trigger"
        onClick={() => setAbierto(a => !a)}
      >
        {seleccionado ? seleccionado.nombre : <span className="plano-select-placeholder">-- Seleccionar plano --</span>}
        <span className="plano-select-arrow">{abierto ? '▲' : '▼'}</span>
      </button>
      {abierto && (
        <div className="plano-select-dropdown">
          <input
            type="text"
            className="plano-select-search"
            placeholder="Buscar plano..."
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            autoFocus
          />
          <div className="plano-select-list">
            <button
              type="button"
              className={`plano-select-item ${!value ? 'selected' : ''}`}
              onClick={() => { onChange(''); setAbierto(false); setBusqueda(''); }}
            >
              <em>-- Sin plano --</em>
            </button>
            {filtrados.length === 0 ? (
              <div className="plano-select-empty">Sin resultados</div>
            ) : filtrados.map(p => (
              <button
                type="button"
                key={p.id}
                className={`plano-select-item ${String(value) === String(p.id) ? 'selected' : ''}`}
                onClick={() => { onChange(String(p.id)); setAbierto(false); setBusqueda(''); }}
              >
                {p.nombre}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Función para obtener fecha actual en zona horaria de Colombia
const getFechaColombia = () => {
  return new Date().toLocaleDateString('en-CA', { timeZone: 'America/Bogota' });
};

// Formatear número como moneda colombiana
const formatCurrency = (value) => {
  if (!value && value !== 0) return '';
  return new Intl.NumberFormat('es-CO', {
    style: 'currency',
    currency: 'COP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
};

// Opciones de baño que entrega la constructora
const OPCIONES_BANO = [
  { id: 'conserva_todos', label: 'Se conservan todos los elementos entregados por la constructora en el área del baño' },
  { id: 'conserva_enchape', label: 'Se conserva enchape de piso y paredes' },
  { id: 'conserva_sanitario', label: 'Se conserva sanitario' },
  { id: 'cambia_puerta', label: 'Se cambia puerta de baño' },
  { id: 'conserva_lavamanos_griferia', label: 'Se conserva lavamanos y grifería' },
  { id: 'cambia_enchape_completo', label: 'Se cambia enchape completo' },
  { id: 'conserva_regadera', label: 'Se conserva regadera y mezclador' },
  { id: 'cambia_lavamanos', label: 'Se cambia solo lavamanos' },
  { id: 'conserva_accesorios', label: 'Se conserva Kit de accesorios cerámicos' },
  { id: 'renueva_completo', label: 'Se renueva baño completamente (Aparatos sanitarios, griferías y enchapes)' }
];

// Formas de cocina disponibles
const FORMAS_COCINA = [
  { id: 'Lineal', label: 'Lineal', imagen: '/Formas de cocina/Lineal.png' },
  { id: 'Lineal+Isla', label: 'Lineal + Isla', imagen: '/Formas de cocina/Lineal+Isla.png' },
  { id: 'EnL', label: 'En L', imagen: '/Formas de cocina/EnL.png' },
  { id: 'EnC', label: 'En C', imagen: '/Formas de cocina/EnC.png' }
];

const Cotizaciones = () => {
  const { success, error } = useNotification();
  const { user, tienePermiso } = useAuth();
  // El incremento de actividades solo lo ve quien tenga el permiso; el valor se
  // sigue aplicando igual para todos, unicamente se oculta el campo.
  const puedeVerIncremento = tienePermiso('documentos', 'cotizaciones-incremento');
  // El descuento no puede pasar del 3% del SUBTOTAL (sin AIU ni IVA) salvo
  // que el usuario tenga el permiso especial. El backend lo vuelve a validar.
  const puedeDescuentoLibre = tienePermiso('documentos', 'cotizaciones-descuento');
  // Bases: aprobación comercial y técnica, cada una con su permiso individual
  const puedeAprobarComercial = tienePermiso('documentos', 'cotizaciones-aprobacion-comercial');
  const puedeAprobarTecnica = tienePermiso('documentos', 'cotizaciones-aprobacion-tecnica');
  const [aprobandoBase, setAprobandoBase] = useState(null);
  const marcarAprobacionBase = async (cot, tipo) => {
    const campo = `aprobacion_${tipo}_at`;
    const aprobado = !cot[campo];
    setAprobandoBase(`${cot.id}-${tipo}`);
    try {
      const r = (await client.mutate('mutation($id: Int!, $t: String!, $a: Boolean!) { aprobarBaseCotizacion(id: $id, tipo: $t, aprobado: $a) { success message } }', { id: cot.id, t: tipo, a: aprobado })).aprobarBaseCotizacion;
      if (!r.success) { error(r.message); return; }
      const ahora = new Date().toISOString();
      const nombre = user?.nombre_completo || user?.username || '';
      const patch = { [campo]: aprobado ? ahora : null, [`aprobacion_${tipo}_por`]: aprobado ? nombre : null };
      const upd = prev => prev.map(c => (c.id === cot.id ? { ...c, ...patch } : c));
      setCotizaciones(upd); setCotizacionesWeb(upd); setCotizacionesEspeciales(upd);
      success(r.message);
    } catch (e) { error('No se pudo guardar la aprobación'); }
    finally { setAprobandoBase(null); }
  };
  const renderAprobacionesBase = (cot) => {
    if (!['base', 'base_cotizador_web'].includes(cot.estado)) return null;
    const item = (tipo, etiqueta, puede) => {
      const at = cot[`aprobacion_${tipo}_at`];
      const por = cot[`aprobacion_${tipo}_por`];
      const titulo = at
        ? `${etiqueta}: aprobada por ${por || '—'} el ${new Date(at).toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: 'numeric' })}${puede ? ' (clic para retirar)' : ''}`
        : `${etiqueta}: pendiente${puede ? ' (clic para aprobar)' : ' · requiere permiso'}`;
      return (
        <button
          type="button"
          className={`cot-aprob ${at ? 'ok' : ''} ${puede ? 'puede' : ''}`}
          title={titulo}
          disabled={!puede || aprobandoBase === `${cot.id}-${tipo}`}
          onClick={() => marcarAprobacionBase(cot, tipo)}
        >
          <span className="cot-aprob-box">{at ? '✓' : ''}</span>{etiqueta}
        </button>
      );
    };
    return (
      <div className="cot-aprob-wrap">
        {item('comercial', 'Aprob. comercial', puedeAprobarComercial)}
        {item('tecnica', 'Aprob. técnica', puedeAprobarTecnica)}
      </div>
    );
  };
  // Por defecto el valor unitario viene fijo de la matriz; con este permiso se
  // puede sobreescribir en la cotizacion.
  const puedeEditarValorUnitario = tienePermiso('documentos', 'cotizaciones-valor-unitario');
  // Bloquear actividades para que nadie las pueda sacar de la cotizacion.
  const puedeBloquear = tienePermiso('documentos', 'cotizaciones-bloquear');
  // Cambiar el incremento con el que nacen las cotizaciones nuevas.
  const puedeEditarIncrementoDefecto = tienePermiso('documentos', 'cotizaciones-incremento-defecto');
  const [incrementoDefecto, setIncrementoDefecto] = useState(INCREMENTO_NUEVA);
  const [incDefectoEditando, setIncDefectoEditando] = useState(null); // texto mientras se edita
  const [guardandoIncDefecto, setGuardandoIncDefecto] = useState(false);

  // Tipo de la cotización que se está creando o editando: 'nueva' (normal) o
  // 'especial'. La especial se guarda en su propia pestaña, no lleva el bloque
  // del baño que entrega la constructora y deja repetir una misma actividad.
  const [tipoCotizacion, setTipoCotizacion] = useState('nueva');
  const [modalNueva, setModalNueva] = useState(false);
  const esEspecial = tipoCotizacion === 'especial';

  useEffect(() => {
    client.query('{ incrementoCotizacionNueva }')
      .then(d => { if (d && d.incrementoCotizacionNueva != null) setIncrementoDefecto(Number(d.incrementoCotizacionNueva)); })
      .catch(() => { /* se queda el respaldo */ });
  }, []);

  const guardarIncrementoDefecto = async () => {
    const v = parseFloat(String(incDefectoEditando).replace(',', '.'));
    if (!Number.isFinite(v) || v < 0 || v > 100) { error('El incremento debe estar entre 0 % y 100 %'); return; }
    setGuardandoIncDefecto(true);
    try {
      const r = await client.mutate('mutation($v: Float!) { actualizarIncrementoCotizacionNueva(valor: $v) { success message } }', { v });
      if (r.actualizarIncrementoCotizacionNueva.success) {
        setIncrementoDefecto(v);
        setIncDefectoEditando(null);
        success(r.actualizarIncrementoCotizacionNueva.message);
      } else error(r.actualizarIncrementoCotizacionNueva.message);
    } catch (e) { error('No se pudo guardar: ' + (e.message || '')); }
    finally { setGuardandoIncDefecto(false); }
  };

  const [vista, setVista] = useState('lista'); // lista, crear, editar
  const [cotizaciones, setCotizaciones] = useState([]);
  const [planos, setPlanos] = useState([]);
  // capitulos ya no se usa (se reemplazó por categorías)
  const [loading, setLoading] = useState(false);
  const [cotizacionEditando, setCotizacionEditando] = useState(null);

  // Estado del formulario
  const [formData, setFormData] = useState({
    fecha: getFechaColombia(),
    nombre_cliente: '',
    cedula_cliente: '',
    telefono_cliente: '',
    correo_cliente: '',
    zona_barrio: '',
    zona: '',
    proyecto_nombre: '',
    apartamento: '',
    area_m2: '',
    direccion: '',
    habitaciones: '',
    banos: '',
    bano_social_alto: '',
    bano_social_ancho: '',
    bano_ppal_alto: '',
    bano_ppal_ancho: '',
    balcon: '',
    altura_ducha: '',
    numero_puertas: '',
    metros_zocalo: '',
    plano_id: '',
    forma_cocina: '',
    opciones_bano: {},
    incremento_global: INCREMENTO_NUEVA,
    notas: '',
    descuento: 0,
    plan_pagos: [
      { descripcion: 'Separacion', porcentaje: 10 },
      { descripcion: 'Inicio de obra', porcentaje: 40 },
      { descripcion: 'Avance de obra', porcentaje: 20 },
      { descripcion: 'Saldo final', porcentaje: 20 },
      { descripcion: 'Entrega', porcentaje: 10 }
    ]
  });

  // Actividades seleccionadas
  const [actividadesSeleccionadas, setActividadesSeleccionadas] = useState([]);

  // Estado para el selector de actividades (3 niveles: categoría → espacio → actividades)
  const [categoriaActiva, setCategoriaActiva] = useState('');
  const [espacioActivo, setEspacioActivo] = useState('');
  const [espaciosDisponibles, setEspaciosDisponibles] = useState([]);
  const [todosLosEspacios, setTodosLosEspacios] = useState([]);
  const [actividadesDisponibles, setActividadesDisponibles] = useState([]);
  const [buscarActividad, setBuscarActividad] = useState('');
  const [loadingActividades, setLoadingActividades] = useState(false);
  // Opciones por subcategoría de espacio (matriz): una actividad con subcategoría se
  // puede cambiar por otra del mismo espacio y subcategoría, de menor a mayor valor.
  const [opcionesSubcat, setOpcionesSubcat] = useState([]);
  const [modalOpciones, setModalOpciones] = useState(null); // índice global de la actividad
  const debounceRef = useRef(null);
  const debounceFiltroBuscarRef = useRef(null);
  const [buscarTexto, setBuscarTexto] = useState('');

  // PDF generation
  const [cotizacionPDF, setCotizacionPDF] = useState(null);
  const [generandoPDF, setGenerandoPDF] = useState(false);
  const pdfContainerRef = useRef(null);
  // Modal "¿Para qué necesitas el PDF?" (Para Enviar / Para Imprimir)
  const [pdfModalCot, setPdfModalCot] = useState(null);

  // Excel generation
  const [generandoExcel, setGenerandoExcel] = useState(false);

  // Orden personalizado de categorías
  const ORDEN_CATEGORIAS = [
    'PRELIMINARES',
    'MUROS',
    'REDES ELECTRICAS',
    'PISOS',
    'ENCHAPES Y REVOQUE',
    'ESTUCO, PINTURA Y DRYWALL',
    'CARPINTERIA EN MADERA',
    'MESONES',
    'APARATOS SANITARIOS Y GRIFERIAS',
    'ELECTRODOMESTICOS',
    'VIDRIOS',
    'ACTIVIDADES ESPECIALES',
    'CARPINTERIA ESPECIAL',
    'FINALES'
  ];

  // Mapeo de cantidad_predeterminada a campos del formulario
  const CANT_PRED_MAPPING = {
    'Área del inmueble': 'area_m2',
    'Número de baños': 'banos',
    'Área baño social': '_calc_area_bano_social',
    'Área baño principal': '_calc_area_bano_ppal',
    'Balcón': 'balcon',
    'Altura ducha': 'altura_ducha',
    'Número de puertas': 'numero_puertas',
    'Metros de zócalo': 'metros_zocalo'
  };

  // Valores calculados
  const [totales, setTotales] = useState({
    subtotal: 0,
    aiu_porcentaje: 15,
    aiu_valor: 0,
    iva_valor: 0,
    valor_final: 0,
    valor_final_descuento: 0
  });

  // Filtros para lista
  const [filtros, setFiltros] = useState({
    buscar: '',
    estado: ''
  });
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // AbortController ref para cancelar peticiones al desmontar
  const abortRef = useRef(null);

  // Tab activo en la lista
  const [tabLista, setTabLista] = useState('nuevas');

  // Cotizaciones viejas
  const [cotizacionesViejas, setCotizacionesViejas] = useState([]);
  const [loadingViejas, setLoadingViejas] = useState(false);
  const [pageViejas, setPageViejas] = useState(1);
  const [totalPagesViejas, setTotalPagesViejas] = useState(1);
  const [buscarViejas, setBuscarViejas] = useState('');
  const [filtroViejasBuscar, setFiltroViejasBuscar] = useState('');
  const [filtroViejasEstado, setFiltroViejasEstado] = useState('');
  const debounceViejasRef = useRef(null);

  // Cotizaciones del Cotizador Web (estado 'cotizador_web')
  const [cotizacionesWeb, setCotizacionesWeb] = useState([]);
  const [loadingWeb, setLoadingWeb] = useState(false);
  const [pageWeb, setPageWeb] = useState(1);
  const [totalPagesWeb, setTotalPagesWeb] = useState(1);
  const [buscarWeb, setBuscarWeb] = useState('');
  const [filtroWebBuscar, setFiltroWebBuscar] = useState('');
  const debounceWebRef = useRef(null);

  // Cotizaciones Especiales (tipo 'especial'), en su propia pestaña
  const [cotizacionesEspeciales, setCotizacionesEspeciales] = useState([]);
  const [loadingEsp, setLoadingEsp] = useState(false);
  const [pageEsp, setPageEsp] = useState(1);
  const [totalPagesEsp, setTotalPagesEsp] = useState(1);
  const [buscarEsp, setBuscarEsp] = useState('');
  const [filtroEspBuscar, setFiltroEspBuscar] = useState('');
  const debounceEspRef = useRef(null);

  // Preview de cotización vieja importada
  const [previewVieja, setPreviewVieja] = useState(null); // { cliente, actividades, totales }
  const [guardandoVieja, setGuardandoVieja] = useState(false);
  const [actualizandoViejaId, setActualizandoViejaId] = useState(null); // ID de la cotización que se está actualizando

  // Autoguardado
  const [autoguardando, setAutoguardando] = useState(false);
  const [ultimoAutoguardado, setUltimoAutoguardado] = useState(null);
  const autoguardadoRef = useRef(null);
  const autoguardadoHabilitado = useRef(false); // Se activa después de la carga inicial
  const autoguardadoEnCurso = useRef(false); // Evita ejecuciones simultáneas
  const autoguardadoAbortRef = useRef(null); // AbortController para cancelar request en vuelo
  const cargandoCotizacionRef = useRef(false); // Evita recálculo de precios al cargar edición

  // Detalle de cotización vieja (solo lectura)
  const [detalleVieja, setDetalleVieja] = useState(null);
  const [loadingDetalleVieja, setLoadingDetalleVieja] = useState(false);

  // Refs para inputs de actualizar Excel por fila
  const updateFileRefs = useRef({});

  // Modal de confirmación para marcar como base (actualizar precios)
  const [modalBase, setModalBase] = useState({ visible: false, cotId: null, loading: false, tipo: null });

  // Zonas (lista administrable desde el propio modulo)
  const [zonas, setZonas] = useState([]);
  const [modalZonas, setModalZonas] = useState({ visible: false, filas: [], cargando: false, nueva: '', editandoId: null });

  const cargarZonas = useCallback(async () => {
    try {
      const d = await client.query('{ zonas { id nombre activo orden en_uso } }');
      setZonas(d.zonas || []);
    } catch { /* si falla, el desplegable queda vacio */ }
  }, []);

  const cargarZonasAdmin = async () => {
    setModalZonas(m => ({ ...m, cargando: true }));
    try {
      const d = await client.query('{ zonas(incluirInactivas: true) { id nombre activo orden en_uso } }');
      setModalZonas(m => ({ ...m, filas: d.zonas || [], cargando: false }));
    } catch (e) {
      error('No se pudieron cargar las zonas');
      setModalZonas(m => ({ ...m, cargando: false }));
    }
  };

  const abrirModalZonas = async () => {
    setModalZonas({ visible: true, filas: [], cargando: true, nueva: '', editandoId: null });
    await cargarZonasAdmin();
  };

  const crearZona = async () => {
    const nombre = modalZonas.nueva.trim();
    if (!nombre) return;
    try {
      const r = await client.mutate(`mutation($n: String!) { crearZona(nombre: $n) { success message } }`, { n: nombre });
      if (r.crearZona.success) setModalZonas(m => ({ ...m, nueva: '' }));
      await trasCambioZona(r.crearZona);
    } catch (e) { error('Error al crear la zona'); }
  };

  const renombrarZona = async (zona, valor) => {
    setModalZonas(m => ({ ...m, editandoId: null }));
    const nombre = (valor || '').trim();
    if (!nombre || nombre === zona.nombre) return;
    try {
      const r = await client.mutate(`mutation($id: Int!, $n: String) { editarZona(id: $id, nombre: $n) { success message } }`, { id: zona.id, n: nombre });
      await trasCambioZona(r.editarZona);
    } catch (e) { error('Error al renombrar la zona'); }
  };

  const toggleZona = async (zona) => {
    try {
      const r = await client.mutate(`mutation($id: Int!, $a: Boolean) { editarZona(id: $id, activo: $a) { success message } }`, { id: zona.id, a: !zona.activo });
      await trasCambioZona(r.editarZona);
    } catch (e) { error('Error al cambiar el estado de la zona'); }
  };

  const eliminarZona = async (zona) => {
    if (!window.confirm(`¿Eliminar la zona "${zona.nombre}"?`)) return;
    try {
      const r = await client.mutate(`mutation($id: Int!) { eliminarZona(id: $id) { success message } }`, { id: zona.id });
      await trasCambioZona(r.eliminarZona);
    } catch (e) { error('Error al eliminar la zona'); }
  };

  const trasCambioZona = async (res) => {
    if (res?.success) { success(res.message); await cargarZonasAdmin(); await cargarZonas(); }
    else error(res?.message || 'No se pudo completar la acción');
  };

  // Historial de auditoria de una cotizacion (quien cambio que y cuando)
  const [histModal, setHistModal] = useState({ visible: false, cot: null, filas: [], cargando: false });
  const abrirHistorial = async (cot) => {
    setHistModal({ visible: true, cot, filas: [], cargando: true });
    try {
      const d = await client.query(
        `query($id: Int!) { historialCotizacion(cotizacion_id: $id) { id campo valor_anterior valor_nuevo origen nota usuario_nombre fecha_cambio } }`,
        { id: cot.id }
      );
      setHistModal(m => ({ ...m, filas: d.historialCotizacion || [], cargando: false }));
    } catch {
      error('No se pudo cargar el historial');
      setHistModal(m => ({ ...m, cargando: false }));
    }
  };
  const [modalContratado, setModalContratado] = useState(null); // { id, tipo, fechaInicio }
  // Modal de motivo de perdida al rechazar una cotizacion.
  const [modalRechazo, setModalRechazo] = useState({ visible: false, id: null, tipo: null, motivo: '', detalle: '', guardando: false });
  // Modal "¿venta por canal propio?" al marcar una cotización como vendida.
  const [modalVenta, setModalVenta] = useState({ visible: false, cot: null, tipo: 'nueva', loading: false });
  const [fechaInicioContratado, setFechaInicioContratado] = useState('');

  // PDF de cotización vieja
  const [cotizacionViejaPDF, setCotizacionViejaPDF] = useState(null);
  const [generandoPDFVieja, setGenerandoPDFVieja] = useState(false);
  const pdfViejaContainerRef = useRef(null);

  // Cargar datos iniciales (planos y espacios solo una vez; cotizaciones se cargan via filtros)
  useEffect(() => {
    client.query('{ matrizOpcionesSubcategoria { id codigo actividad categoria unidad valor_unitario cantidad_default cantidad_predeterminada incremento_actividades factor_desperdicio factor_multiplicador espacio capitulo subcategoria_espacio } }')
      .then(d => setOpcionesSubcat(d.matrizOpcionesSubcategoria || []))
      .catch(() => setOpcionesSubcat([]));
  }, []);

  useEffect(() => {
    cargarPlanos();
    cargarTodosLosEspacios();
    return () => {
      if (abortRef.current) abortRef.current.abort();
    };
  }, []);

  // Recalcular cantidades dinámicamente cuando cambian campos del formulario
  // Solo afecta actividades que NO fueron editadas manualmente
  useEffect(() => {
    if (cargandoCotizacionRef.current) return; // Skip durante carga de edición
    if (actividadesSeleccionadas.length === 0) return;

    setActividadesSeleccionadas(prev => {
      let cambio = false;
      const nuevas = prev.map(act => {
        if (act.cantidad_manual) return act; // Respetar edición manual
        const campoRef = CANT_PRED_MAPPING[act.cantidad_predeterminada];
        if (!campoRef) return act;

        const nuevaCantidad = parseFloat(formData[campoRef]) || 0;
        if (nuevaCantidad === parseFloat(act.cantidad)) return act;

        cambio = true;
        const vu = parseFloat(act.valor_unitario) || 0;
        const inc = parseFloat(act.incremento) || 0;
        const desp = parseFloat(act.factor_desperdicio) || 1;
        const mult = parseFloat(act.factor_multiplicador) || 1;
        const nuevoTotal = Math.floor(vu * (1 + inc / 100) * desp * mult) * nuevaCantidad;

        return { ...act, cantidad: nuevaCantidad, valor_total: nuevoTotal };
      });

      return cambio ? nuevas : prev;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.area_m2, formData.banos, formData.bano_social_alto, formData.bano_social_ancho, formData.bano_ppal_alto, formData.bano_ppal_ancho, formData.balcon, formData.altura_ducha, formData.numero_puertas, formData.metros_zocalo]);

  // Debounce para búsqueda en la lista de cotizaciones
  useEffect(() => {
    if (debounceFiltroBuscarRef.current) {
      clearTimeout(debounceFiltroBuscarRef.current);
    }
    debounceFiltroBuscarRef.current = setTimeout(() => {
      setFiltros(prev => ({ ...prev, buscar: buscarTexto }));
    }, 500);
    return () => {
      if (debounceFiltroBuscarRef.current) {
        clearTimeout(debounceFiltroBuscarRef.current);
      }
    };
  }, [buscarTexto]);

  // Recargar cotizaciones cuando cambian filtros (incluye mount inicial)
  useEffect(() => {
    cargarCotizaciones();
  }, [filtros, page]);

  // Zonas del desplegable (lista administrable)
  useEffect(() => { cargarZonas(); }, [cargarZonas]);

  // Debounce para búsqueda en cotizaciones viejas
  useEffect(() => {
    if (debounceViejasRef.current) clearTimeout(debounceViejasRef.current);
    debounceViejasRef.current = setTimeout(() => {
      setFiltroViejasBuscar(buscarViejas);
    }, 500);
    return () => { if (debounceViejasRef.current) clearTimeout(debounceViejasRef.current); };
  }, [buscarViejas]);

  // Cargar cotizaciones viejas cuando cambia filtro o página
  useEffect(() => {
    if (tabLista === 'viejas') cargarCotizacionesViejas();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtroViejasBuscar, filtroViejasEstado, pageViejas, tabLista]);

  // Debounce para búsqueda en cotizaciones web
  useEffect(() => {
    if (debounceWebRef.current) clearTimeout(debounceWebRef.current);
    debounceWebRef.current = setTimeout(() => { setFiltroWebBuscar(buscarWeb); }, 500);
    return () => { if (debounceWebRef.current) clearTimeout(debounceWebRef.current); };
  }, [buscarWeb]);

  // Cargar cotizaciones del cotizador web cuando cambia filtro/página/pestaña
  useEffect(() => {
    if (tabLista === 'web') cargarCotizacionesWeb();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtroWebBuscar, pageWeb, tabLista]);

  // Búsqueda con debounce en la pestaña de especiales
  useEffect(() => {
    if (debounceEspRef.current) clearTimeout(debounceEspRef.current);
    debounceEspRef.current = setTimeout(() => { setFiltroEspBuscar(buscarEsp); }, 500);
    return () => { if (debounceEspRef.current) clearTimeout(debounceEspRef.current); };
  }, [buscarEsp]);

  useEffect(() => {
    if (tabLista === 'especiales') cargarCotizacionesEspeciales();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtroEspBuscar, pageEsp, tabLista]);

  // Calcular totales cuando cambian las actividades o el descuento
  // Skip si estamos cargando una cotización (usaremos los totales de la BD)
  useEffect(() => {
    if (cargandoCotizacionRef.current) return;
    calcularTotales();
  }, [actividadesSeleccionadas, formData.descuento]);

  // Autoguardado: disparar con debounce cuando cambian datos del formulario o actividades
  useEffect(() => {
    if (vista !== 'crear' && vista !== 'editar') return;
    if (!autoguardadoHabilitado.current) return;

    if (autoguardadoRef.current) clearTimeout(autoguardadoRef.current);
    autoguardadoRef.current = setTimeout(() => {
      autoguardar();
    }, 3000); // 3 segundos de inactividad

    return () => {
      if (autoguardadoRef.current) clearTimeout(autoguardadoRef.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData, actividadesSeleccionadas]);

  // Habilitar autoguardado con delay tras entrar a crear/editar (evita trigger en carga inicial)
  useEffect(() => {
    if (vista === 'crear' || vista === 'editar') {
      autoguardadoHabilitado.current = false;
      const timer = setTimeout(() => {
        autoguardadoHabilitado.current = true;
      }, 2000);
      return () => clearTimeout(timer);
    } else {
      autoguardadoHabilitado.current = false;
      setUltimoAutoguardado(null);
    }
  }, [vista]);

  // Recalcular valor_unitario y valor_total de todas las actividades cuando cambie el incremento global
  // SOLO cuando el usuario cambia manualmente el incremento, NO al cargar una cotización existente
  const prevIncGlobalRef = useRef(parseFloat(formData.incremento_global) || 0);
  // Si el formulario viene de "Duplicar", el consecutivo de la cotización origen:
  // se manda al crear para que el historial registre de dónde salió.
  const duplicadaDeRef = useRef(null);
  useEffect(() => {
    if (cargandoCotizacionRef.current) {
      // Sincronizar el ref con el valor cargado sin recalcular
      prevIncGlobalRef.current = parseFloat(formData.incremento_global) || 0;
      return;
    }
    const incGlobal = parseFloat(formData.incremento_global) || 0;
    // Solo recalcular si el valor realmente cambió respecto al anterior
    if (incGlobal === prevIncGlobalRef.current) return;
    prevIncGlobalRef.current = incGlobal;

    setActividadesSeleccionadas(prev => {
      if (prev.length === 0) return prev;
      return prev.map(act => {
        const base = parseFloat(act.valor_unitario_base) || parseFloat(act.valor_unitario) || 0;
        const inc = parseFloat(act.incremento) || 0;
        const desp = parseFloat(act.factor_desperdicio) || 1;
        const mult = parseFloat(act.factor_multiplicador) || 1;
        const cantidad = parseFloat(act.cantidad) || 0;
        const vuMostrado = Math.floor(base * (1 + incGlobal / 100));
        const valorTotal = Math.floor(vuMostrado * (1 + inc / 100) * desp * mult) * cantidad;
        return {
          ...act,
          valor_unitario_base: base,
          valor_unitario: vuMostrado,
          valor_total: valorTotal
        };
      });
    });
  }, [formData.incremento_global]);

  // Cargar espacios cuando cambia la categoría
  useEffect(() => {
    if (categoriaActiva) {
      cargarEspacios(categoriaActiva);
      setEspacioActivo('');
      setActividadesDisponibles([]);
      setBuscarActividad('');
    } else {
      setEspaciosDisponibles([]);
      setEspacioActivo('');
      setActividadesDisponibles([]);
    }
  }, [categoriaActiva]);

  // Cargar actividades cuando cambia el espacio o búsqueda
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      // Cargar si hay categoría+espacio seleccionados, o si hay búsqueda por texto
      if ((categoriaActiva && espacioActivo) || buscarActividad) {
        cargarActividadesMatriz();
      }
    }, 400);
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [buscarActividad, espacioActivo]);

  // Auto-seleccionar espacio si solo hay uno, o TODOS si no hay ninguno
  useEffect(() => {
    if (espaciosDisponibles.length === 1) {
      setEspacioActivo(espaciosDisponibles[0]);
    }
  }, [espaciosDisponibles]);

  const exportarExcelCotizaciones = async () => {
    try {
      // Traer TODAS las cotizaciones sin paginación
      const gqlData = await client.query(`{ cotizaciones(limit: 99999) { cotizaciones { id consecutivo fecha nombre_cliente cedula_cliente telefono_cliente correo_cliente proyecto_nombre apartamento area_m2 subtotal aiu_valor iva_valor descuento valor_final valor_final_descuento incremento_global estado total_actividades plano_nombre creado_por_nombre modificado_por_nombre created_at updated_at proyecto_id } } }`);
      if (!gqlData.cotizaciones?.cotizaciones?.length) { error('No hay cotizaciones para exportar'); return; }
      var todasCotizaciones = gqlData.cotizaciones.cotizaciones;
      // Estado del proyecto vinculado, para saber cuáles contratadas ya pasaron a ejecución
      var estadoProyecto = {};
      try {
        const dp = await client.query('{ proyectos { id estado } }');
        (dp.proyectos || []).forEach(pr => { estadoProyecto[pr.id] = pr.estado; });
      } catch (e) { /* sin estados de proyecto: la columna sale vacía */ }
    } catch (err) { error('Error al obtener cotizaciones'); return; }

    // Contratadas: ¿ya se enviaron a ejecución desde la Ficha FFE?
    const ETAPA = {
      en_ejecucion: 'Sí - En ejecución', entregado_con_detalles: 'Sí - Entregado con detalles',
      cobrado_liquidado: 'Sí - Entregado y en cobro', entregado_liquidado: 'Sí - Entregado y liquidado', posventa: 'Sí - Posventa',
    };
    const enEjecucion = (cot) => {
      if (cot.estado !== 'contratado') return '';
      if (!cot.proyecto_id) return 'No - Sin proyecto';
      return ETAPA[estadoProyecto[cot.proyecto_id]] || 'No - En Ficha FFE';
    };

    const datos = todasCotizaciones.map(cot => ({
      '#': cot.consecutivo,
      'Fecha': cot.fecha,
      'Cliente': cot.nombre_cliente,
      'Cédula': cot.cedula_cliente || '',
      'Teléfono': cot.telefono_cliente || '',
      'Correo': cot.correo_cliente || '',
      'Proyecto': cot.proyecto_nombre,
      'Apartamento': cot.apartamento || '',
      'Plano': cot.plano_nombre || '',
      'Área m²': cot.area_m2 || '',
      'Subtotal': parseFloat(cot.subtotal) || 0,
      'AIU': parseFloat(cot.aiu_valor) || 0,
      'IVA': parseFloat(cot.iva_valor) || 0,
      'Descuento': parseFloat(cot.descuento) || 0,
      'Valor Final': parseFloat(cot.valor_final_descuento || cot.valor_final) || 0,
      'Estado': cot.estado?.toUpperCase(),
      'En ejecución': enEjecucion(cot),
      'Actividades': cot.total_actividades || 0,
      'Creado por': cot.creado_por_nombre || '-',
      'Fecha Creación': cot.created_at ? new Date(cot.created_at).toLocaleString('es-CO') : '-',
      'Modificado por': cot.modificado_por_nombre || '-',
      'Última Modificación': cot.updated_at ? new Date(cot.updated_at).toLocaleString('es-CO') : '-',
      'Incremento Actividades (%)': parseFloat(cot.incremento_global) || 0
    }));

    const ws = XLSX.utils.json_to_sheet(datos);

    // Anchos de columna
    ws['!cols'] = [
      { wch: 6 }, { wch: 12 }, { wch: 25 }, { wch: 15 }, { wch: 14 }, { wch: 25 },
      { wch: 22 }, { wch: 12 }, { wch: 20 }, { wch: 8 },
      { wch: 16 }, { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 18 },
      { wch: 14 }, { wch: 26 }, { wch: 12 }, { wch: 16 }, { wch: 20 }, { wch: 16 }, { wch: 20 },
      { wch: 24 }
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Cotizaciones');
    XLSX.writeFile(wb, `Cotizaciones_TUVI_${new Date().toISOString().slice(0, 10)}.xlsx`);
    success('Excel exportado');
  };

  const cargarCotizaciones = async () => {
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      setLoading(true);
      const vars = { page, limit: 20 };
      if (filtros.buscar) vars.buscar = filtros.buscar;
      if (filtros.estado) vars.estado = filtros.estado;
      const data = await client.query(`query($page: Int, $limit: Int, $buscar: String, $estado: String) { cotizaciones(page: $page, limit: $limit, buscar: $buscar, estado: $estado) { cotizaciones { id consecutivo tipo fecha estado nombre_cliente cedula_cliente telefono_cliente correo_cliente proyecto_nombre apartamento area_m2 habitaciones banos subtotal aiu_valor iva_valor valor_final valor_final_descuento descuento plano_nombre total_actividades avance_ffe proyecto_id created_at updated_at created_by updated_by creado_por_nombre modificado_por_nombre aprobacion_comercial_at aprobacion_comercial_por aprobacion_tecnica_at aprobacion_tecnica_por } totalPages } }`, vars);
      setCotizaciones(data.cotizaciones.cotizaciones || []);
      setTotalPages(data.cotizaciones.totalPages || 1);
    } catch (err) {
      if (err.name !== 'AbortError') error('Error al cargar cotizaciones');
    } finally {
      setLoading(false);
    }
  };

  const cargarCotizacionesViejas = async () => {
    try {
      setLoadingViejas(true);
      const vars = { page: pageViejas, limit: 20 };
      if (filtroViejasBuscar) vars.buscar = filtroViejasBuscar;
      if (filtroViejasEstado) vars.estado = filtroViejasEstado;
      const data = await client.query(`query($page: Int, $limit: Int, $buscar: String, $estado: String) { cotizacionesViejas(page: $page, limit: $limit, buscar: $buscar, estado: $estado) { cotizaciones { id consecutivo fecha nombre_cliente telefono_cliente correo_cliente proyecto_nombre apartamento area_m2 subtotal valor_final estado avance_ffe proyecto_id total_actividades } totalPages } }`, vars);
      setCotizacionesViejas(data.cotizacionesViejas.cotizaciones || []);
      setTotalPagesViejas(data.cotizacionesViejas.totalPages || 1);
    } catch (err) {
      console.error('Error cargando cotizaciones viejas:', err);
    } finally {
      setLoadingViejas(false);
    }
  };

  const cargarCotizacionesWeb = async () => {
    try {
      setLoadingWeb(true);
      const vars = { page: pageWeb, limit: 20, estado: 'cotizador_web' };
      if (filtroWebBuscar) vars.buscar = filtroWebBuscar;
      const data = await client.query(`query($page: Int, $limit: Int, $buscar: String, $estado: String) { cotizaciones(page: $page, limit: $limit, buscar: $buscar, estado: $estado) { cotizaciones { id consecutivo tipo fecha estado nombre_cliente cedula_cliente telefono_cliente correo_cliente proyecto_nombre apartamento area_m2 habitaciones banos subtotal aiu_valor iva_valor valor_final valor_final_descuento descuento plano_nombre total_actividades avance_ffe proyecto_id created_at updated_at created_by updated_by creado_por_nombre modificado_por_nombre aprobacion_comercial_at aprobacion_comercial_por aprobacion_tecnica_at aprobacion_tecnica_por } totalPages } }`, vars);
      setCotizacionesWeb(data.cotizaciones.cotizaciones || []);
      setTotalPagesWeb(data.cotizaciones.totalPages || 1);
    } catch (err) {
      console.error('Error cargando cotizaciones web:', err);
    } finally {
      setLoadingWeb(false);
    }
  };

  const cargarCotizacionesEspeciales = async () => {
    try {
      setLoadingEsp(true);
      const vars = { page: pageEsp, limit: 20, tipo: 'especial' };
      if (filtroEspBuscar) vars.buscar = filtroEspBuscar;
      const data = await client.query(`query($page: Int, $limit: Int, $buscar: String, $tipo: String) { cotizaciones(page: $page, limit: $limit, buscar: $buscar, tipo: $tipo) { cotizaciones { id consecutivo tipo fecha estado nombre_cliente cedula_cliente telefono_cliente correo_cliente proyecto_nombre apartamento area_m2 habitaciones banos subtotal aiu_valor iva_valor valor_final valor_final_descuento descuento plano_nombre total_actividades avance_ffe proyecto_id created_at updated_at created_by updated_by creado_por_nombre modificado_por_nombre aprobacion_comercial_at aprobacion_comercial_por aprobacion_tecnica_at aprobacion_tecnica_por } totalPages } }`, vars);
      setCotizacionesEspeciales(data.cotizaciones.cotizaciones || []);
      setTotalPagesEsp(data.cotizaciones.totalPages || 1);
    } catch (err) {
      console.error('Error cargando cotizaciones especiales:', err);
    } finally {
      setLoadingEsp(false);
    }
  };

  const cargarPlanos = async () => {
    try {
      const data = await client.query(`{ planos(activo: true) { id nombre url_imagen orden } }`);
      setPlanos(data.planos || []);
    } catch (err) {
      console.error('Error cargando planos:', err);
    }
  };

  const cargarEspacios = async (categoria) => {
    try {
      const data = await client.query(`query($cat: String) { matrizEspaciosPorCategoria(categoria: $cat) }`, { cat: categoria });
      setEspaciosDisponibles(data.matrizEspaciosPorCategoria || []);
    } catch (err) {
      console.error('Error cargando espacios:', err);
    }
  };

  const cargarTodosLosEspacios = async () => {
    try {
      const data = await client.query(`{ matrizEspaciosPorCategoria }`);
      setTodosLosEspacios(data.matrizEspaciosPorCategoria || []);
    } catch (err) {
      console.error('Error cargando todos los espacios:', err);
    }
  };

  const calcularValorFinalActividad = (act) => {
    const vu = parseFloat(act.valor_unitario) || 0;
    const inc = parseFloat(act.incremento_actividades) || 0;
    const desp = parseFloat(act.factor_desperdicio) || 1;
    const mult = parseFloat(act.factor_multiplicador) || 1;
    return vu * (1 + inc / 100) * desp * mult;
  };

  const actividadesAbortRef = useRef(null);
  const cargarActividadesMatriz = async () => {
    // Permitir búsqueda sin categoria/espacio si hay texto de búsqueda
    if (!buscarActividad && (!categoriaActiva || !espacioActivo)) return;
    if (actividadesAbortRef.current) actividadesAbortRef.current.abort();
    const controller = new AbortController();
    actividadesAbortRef.current = controller;

    try {
      setLoadingActividades(true);
      const params = new URLSearchParams({
        matriz: '1',
        categoria: categoriaActiva,
        espacio: espacioActivo
      });
      if (buscarActividad) {
        params.append('buscar', buscarActividad);
      }

      const vars = { categoria: categoriaActiva, espacio: espacioActivo };
      if (buscarActividad) vars.buscar = buscarActividad;
      const data = await client.query(`query($categoria: String, $espacio: String, $buscar: String) { matrizActividadesBuscar(categoria: $categoria, espacio: $espacio, buscar: $buscar) { id codigo actividad categoria unidad valor_unitario cantidad_default cantidad_predeterminada incremento_actividades factor_desperdicio factor_multiplicador espacio capitulo } }`, vars);
      const ordenadas = (data.matrizActividadesBuscar || []).sort((a, b) => {
        return calcularValorFinalActividad(a) - calcularValorFinalActividad(b);
      });
      setActividadesDisponibles(ordenadas);
    } catch (err) {
      if (err.name !== 'AbortError') console.error('Error cargando actividades:', err);
    } finally {
      setLoadingActividades(false);
    }
  };

  const calcularTotales = useCallback(() => {
    const subtotal = actividadesSeleccionadas.reduce((sum, act) => {
      return sum + (parseFloat(act.valor_total) || 0);
    }, 0);

    const aiu_porcentaje = 15;
    const aiu_valor = subtotal * (aiu_porcentaje / 100);
    const iva_valor = subtotal * 0.19 * 0.05;
    const valor_final = subtotal + aiu_valor + iva_valor;
    const descuento = parseFloat(formData.descuento) || 0;
    const valor_final_descuento = valor_final - descuento;

    setTotales({
      subtotal,
      aiu_porcentaje,
      aiu_valor,
      iva_valor,
      valor_final,
      valor_final_descuento
    });
  }, [actividadesSeleccionadas, formData.descuento]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Tope del descuento: 3% del subtotal (sin AIU ni IVA).
  const topeDescuento = Math.floor((Number(totales.subtotal) || 0) * 0.03);
  const descuentoExcedido = !puedeDescuentoLibre
    && (Number(formData.descuento) || 0) > topeDescuento
    && topeDescuento > 0;

  // Sin permiso, el campo no deja pasar del tope: se recorta al escribir.
  const handleDescuentoChange = (e) => {
    const bruto = Number(e.target.value);
    const limpio = Number.isFinite(bruto) ? Math.max(0, bruto) : 0;
    const valor = puedeDescuentoLibre ? limpio : Math.min(limpio, topeDescuento);
    setFormData(prev => ({ ...prev, descuento: valor }));
  };

  const handleOpcionBanoChange = (opcionId) => {
    setFormData(prev => ({
      ...prev,
      opciones_bano: {
        ...prev.opciones_bano,
        [opcionId]: !prev.opciones_bano[opcionId]
      }
    }));
  };

  const handleFormaCocinaChange = (formaId) => {
    setFormData(prev => ({ ...prev, forma_cocina: formaId }));
  };

  const handlePlanPagoChange = (index, campo, valor) => {
    setFormData(prev => {
      const nuevos = [...prev.plan_pagos];
      nuevos[index] = { ...nuevos[index], [campo]: campo === 'porcentaje' ? (parseFloat(valor) || 0) : valor };
      return { ...prev, plan_pagos: nuevos };
    });
  };

  const agregarFilaPago = () => {
    if (formData.plan_pagos.length >= 8) return;
    setFormData(prev => ({
      ...prev,
      plan_pagos: [...prev.plan_pagos, { descripcion: '', porcentaje: 0 }]
    }));
  };

  const eliminarFilaPago = (index) => {
    if (formData.plan_pagos.length <= 1) return;
    setFormData(prev => ({
      ...prev,
      plan_pagos: prev.plan_pagos.filter((_, i) => i !== index)
    }));
  };

  const sumaPorcentajesPago = formData.plan_pagos.reduce((sum, p) => sum + (parseFloat(p.porcentaje) || 0), 0);

  // Resolver cantidad según cantidad_predeterminada
  const resolverCantidad = (cantidadPredeterminada, cantidadDefault, formDataActual) => {
    if (!cantidadPredeterminada || cantidadPredeterminada === '-') {
      return parseFloat(cantidadDefault) || 1;
    }
    const campoForm = CANT_PRED_MAPPING[cantidadPredeterminada];
    if (campoForm) {
      // Campos calculados (área = alto × ancho)
      if (campoForm === '_calc_area_bano_social') {
        return (parseFloat(formDataActual.bano_social_alto) || 0) * (parseFloat(formDataActual.bano_social_ancho) || 0);
      }
      if (campoForm === '_calc_area_bano_ppal') {
        return (parseFloat(formDataActual.bano_ppal_alto) || 0) * (parseFloat(formDataActual.bano_ppal_ancho) || 0);
      }
      return parseFloat(formDataActual[campoForm]) || 0;
    }
    const num = parseFloat(cantidadPredeterminada);
    return isNaN(num) ? (parseFloat(cantidadDefault) || 1) : num;
  };

  const agregarActividad = (actividad) => {
    const vu = parseFloat(actividad.valor_unitario) || 0;
    const inc = parseFloat(actividad.incremento_actividades) || 0;
    const desp = parseFloat(actividad.factor_desperdicio) || 1;
    const mult = parseFloat(actividad.factor_multiplicador) || 1;
    const cantidad = resolverCantidad(actividad.cantidad_predeterminada, actividad.cantidad_default, formData);
    const incGlobal = parseFloat(formData.incremento_global) || 0;

    // valor_unitario_base = valor unitario RAW de la matriz (sin ningún incremento)
    const vuBase = vu;
    // valor_unitario mostrado = base + incremento global
    const vuMostrado = Math.floor(vuBase * (1 + incGlobal / 100));
    // valor_total = valor_unitario con factores de la matriz (inc, desperdicio, multiplicador) * cantidad
    const valorTotal = Math.floor(vuMostrado * (1 + inc / 100) * desp * mult) * cantidad;

    const nuevaActividad = {
      actividad_id: actividad.id,
      codigo: actividad.codigo,
      descripcion: actividad.actividad,
      capitulo: actividad.capitulo,
      categoria: actividad.categoria,
      unidad: actividad.unidad,
      cantidad: cantidad,
      valor_unitario_base: vuBase,
      valor_unitario: vuMostrado,
      incremento: inc,
      factor_desperdicio: desp,
      factor_multiplicador: mult,
      valor_total: valorTotal,
      espacio: actividad.espacio,
      cantidad_predeterminada: actividad.cantidad_predeterminada || '',
      activo_matriz: 1
    };

    // Verificar duplicado dentro del setState para evitar race conditions.
    // En las especiales la misma actividad se puede agregar las veces que haga falta.
    setActividadesSeleccionadas(prev => {
      const yaExiste = !esEspecial && prev.some(a => a.actividad_id === actividad.id);
      if (yaExiste) {
        // Notificar al usuario (setTimeout para no llamar setState en otro setState)
        setTimeout(() => error('Esta actividad ya esta agregada'), 0);
        return prev;
      }
      return [...prev, nuevaActividad];
    });
    success('Actividad agregada');
  };

  const actualizarActividad = (index, campo, valor) => {
    setActividadesSeleccionadas(prev => {
      const nuevas = [...prev];
      nuevas[index] = { ...nuevas[index], [campo]: valor };

      if (campo === 'valor_unitario' || campo === 'cantidad') {
        const vu = parseFloat(nuevas[index].valor_unitario) || 0;
        const inc = parseFloat(nuevas[index].incremento) || 0;
        const desp = parseFloat(nuevas[index].factor_desperdicio) || 1;
        const mult = parseFloat(nuevas[index].factor_multiplicador) || 1;
        const cantidad = parseFloat(nuevas[index].cantidad) || 0;
        nuevas[index].valor_total = Math.floor(vu * (1 + inc / 100) * desp * mult) * cantidad;

        if (campo === 'valor_unitario') {
          nuevas[index].valor_unitario_base = parseFloat(valor) || 0;
        }
        if (campo === 'cantidad') {
          nuevas[index].cantidad_manual = true;
        }
      }

      return nuevas;
    });
  };

  // ===== Opciones por subcategoría =====
  const claveSubcat = (m) => `${normalizarTxt(m.espacio)}|${normalizarTxt(m.subcategoria_espacio)}`;
  const { subcatPorId, gruposSubcat } = useMemo(() => {
    const porId = new Map();
    const grupos = new Map();
    opcionesSubcat.forEach(m => {
      porId.set(m.id, m);
      const k = claveSubcat(m);
      if (!grupos.has(k)) grupos.set(k, []);
      grupos.get(k).push(m);
    });
    return { subcatPorId: porId, gruposSubcat: grupos };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opcionesSubcat]);
  // Otras opciones de la misma subcategoría (incluida la actual) o null si no tiene
  const opcionesDe = (act) => {
    const m = subcatPorId.get(act?.actividad_id);
    if (!m) return null;
    const grupo = gruposSubcat.get(claveSubcat(m)) || [];
    return grupo.length > 1 ? { actual: m, grupo } : null;
  };
  // Valor unitario como queda en la cotización (incremento global + factores de la matriz)
  const precioUnitarioCot = (m) => {
    const incGlobal = parseFloat(formData.incremento_global) || 0;
    const vu = Math.floor((parseFloat(m.valor_unitario) || 0) * (1 + incGlobal / 100));
    return Math.floor(vu * (1 + (parseFloat(m.incremento_actividades) || 0) / 100) * (parseFloat(m.factor_desperdicio) || 1) * (parseFloat(m.factor_multiplicador) || 1));
  };
  const intercambiarActividad = (index, m) => {
    const act = actividadesSeleccionadas[index];
    if (!act) return;
    if (act.bloqueada) { error('La actividad está bloqueada: desbloquéala para cambiarla'); return; }
    if (!esEspecial && actividadesSeleccionadas.some((a, i) => i !== index && a.actividad_id === m.id)) {
      error('Esa opción ya está agregada en la cotización'); return;
    }
    const vuBase = parseFloat(m.valor_unitario) || 0;
    const incGlobal = parseFloat(formData.incremento_global) || 0;
    const vuMostrado = Math.floor(vuBase * (1 + incGlobal / 100));
    const inc = parseFloat(m.incremento_actividades) || 0;
    const desp = parseFloat(m.factor_desperdicio) || 1;
    const mult = parseFloat(m.factor_multiplicador) || 1;
    // Misma unidad: se conserva la cantidad que ya tenía; si cambia la unidad, la de la matriz
    const mismaUnidad = normalizarTxt(m.unidad) === normalizarTxt(act.unidad);
    const cantidad = mismaUnidad ? (parseFloat(act.cantidad) || 0) : resolverCantidad(m.cantidad_predeterminada, m.cantidad_default, formData);
    setActividadesSeleccionadas(prev => prev.map((a, i) => (i !== index ? a : {
      actividad_id: m.id, codigo: m.codigo, descripcion: m.actividad, capitulo: m.capitulo, categoria: m.categoria,
      unidad: m.unidad, cantidad, valor_unitario_base: vuBase, valor_unitario: vuMostrado,
      incremento: inc, factor_desperdicio: desp, factor_multiplicador: mult,
      valor_total: Math.floor(vuMostrado * (1 + inc / 100) * desp * mult) * cantidad,
      espacio: a.espacio || m.espacio, orden: a.orden,
      cantidad_predeterminada: m.cantidad_predeterminada || '',
      ...(mismaUnidad && a.cantidad_manual ? { cantidad_manual: true } : {}),
      activo_matriz: 1,
    })));
    setModalOpciones(null);
    success(`Actividad cambiada por ${m.codigo}`);
  };

  const eliminarActividad = (index) => {
    const act = actividadesSeleccionadas[index];
    // El candado tambien se valida en el servidor al guardar; esto es para que el
    // usuario lo vea de una y no pierda el trabajo.
    if (act?.bloqueada && !puedeBloquear) {
      error('Esta actividad está bloqueada y no se puede eliminar');
      return;
    }
    setActividadesSeleccionadas(prev => prev.filter((_, i) => i !== index));
  };

  // Pone o quita el candado. Si la actividad ya existe en la BD se guarda de una;
  // si es nueva (aun sin id) solo se marca y viaja al guardar la cotizacion.
  const alternarBloqueo = async (index, act) => {
    const nuevo = !act.bloqueada;
    if (act.id) {
      try {
        const r = await client.mutate(
          `mutation($id: Int!, $b: Boolean!) { bloquearActividadCotizacion(id: $id, bloqueada: $b) { success message } }`,
          { id: act.id, b: nuevo });
        if (!r.bloquearActividadCotizacion.success) {
          error(r.bloquearActividadCotizacion.message || 'No se pudo cambiar el bloqueo');
          return;
        }
        success(r.bloquearActividadCotizacion.message);
      } catch { error('Error al cambiar el bloqueo'); return; }
    }
    setActividadesSeleccionadas(prev => prev.map((a, i) => i === index ? { ...a, bloqueada: nuevo } : a));
  };

  const resetForm = () => {
    setFormData({
      fecha: getFechaColombia(),
      nombre_cliente: '',
      cedula_cliente: '',
      telefono_cliente: '',
      correo_cliente: '',
      zona_barrio: '',
      zona: '',
      proyecto_nombre: '',
      apartamento: '',
      area_m2: '',
      direccion: '',
      habitaciones: '',
      banos: '',
      bano_social_alto: '',
      bano_social_ancho: '',
      bano_ppal_alto: '',
      bano_ppal_ancho: '',
      balcon: '',
      altura_ducha: '',
      numero_puertas: '',
      metros_zocalo: '',
      plano_id: '',
      forma_cocina: '',
      opciones_bano: {},
      incremento_global: incrementoDefecto,
      notas: '',
      descuento: 0,
      plan_pagos: [
        { descripcion: 'Separacion', porcentaje: 10 },
        { descripcion: 'Inicio de obra', porcentaje: 40 },
        { descripcion: 'Avance de obra', porcentaje: 20 },
        { descripcion: 'Saldo final', porcentaje: 20 },
        { descripcion: 'Entrega', porcentaje: 10 }
      ]
    });
    setActividadesSeleccionadas([]);
    setCotizacionEditando(null);
    setTipoCotizacion('nueva');
  };

  // Al crear se pregunta el tipo: normal o especial. La especial no lleva el
  // bloque "Baño que entrega la constructora" y permite repetir una actividad.
  const nuevaCotizacion = () => setModalNueva(true);

  const iniciarNueva = (tipo) => {
    resetForm();
    duplicadaDeRef.current = null; // nueva desde cero: no es copia
    setTipoCotizacion(tipo);
    setModalNueva(false);
    setVista('crear');
  };

  const editarCotizacion = async (id) => {
    try {
      setLoading(true);
      duplicadaDeRef.current = null;
      cargandoCotizacionRef.current = true; // Bloquear recálculos
      const gqlCotR = await client.query(`query($id: Int!) { cotizacion(id: $id) { id consecutivo tipo fecha estado nombre_cliente cedula_cliente telefono_cliente correo_cliente zona_barrio zona proyecto_nombre apartamento area_m2 direccion habitaciones banos area_banos numero_puertas metros_zocalo pregunta_agua_caliente pregunta_cambia_cocina pregunta_plafones_led pregunta_conserva_bano pregunta_cajones_drywall plano_id forma_cocina opciones_bano subtotal aiu_porcentaje aiu_valor iva_valor valor_final descuento valor_final_descuento incremento_global notas bano_social_alto bano_social_ancho bano_ppal_alto bano_ppal_ancho balcon altura_ducha avance_ffe proyecto_id plano_nombre plano_url actividades { id cotizacion_id actividad_id codigo descripcion capitulo categoria unidad cantidad valor_unitario valor_unitario_base valor_total espacio orden incremento factor_desperdicio factor_multiplicador activo_matriz cantidad_predeterminada bloqueada bloqueada_at } plan_pagos { id cotizacion_id descripcion porcentaje valor orden } } }`, { id });
      const data = { success: !!gqlCotR.cotizacion, cotizacion: gqlCotR.cotizacion };

      if (data.success && data.cotizacion) {
        const cot = data.cotizacion;
        setFormData({
          fecha: cot.fecha,
          nombre_cliente: cot.nombre_cliente || '',
          cedula_cliente: cot.cedula_cliente || '',
          telefono_cliente: cot.telefono_cliente || '',
          correo_cliente: cot.correo_cliente || '',
          zona_barrio: cot.zona_barrio || '',
          zona: cot.zona || '',
          proyecto_nombre: cot.proyecto_nombre || '',
          apartamento: cot.apartamento || '',
          area_m2: cot.area_m2 || '',
          direccion: cot.direccion || '',
          habitaciones: cot.habitaciones || '',
          banos: cot.banos || '',
          bano_social_alto: cot.bano_social_alto || '',
          bano_social_ancho: cot.bano_social_ancho || '',
          bano_ppal_alto: cot.bano_ppal_alto || '',
          bano_ppal_ancho: cot.bano_ppal_ancho || '',
          balcon: cot.balcon || '',
          altura_ducha: cot.altura_ducha || '',
          numero_puertas: cot.numero_puertas || '',
          metros_zocalo: cot.metros_zocalo || '',
          plano_id: cot.plano_id || '',
          forma_cocina: cot.forma_cocina || '',
          opciones_bano: typeof cot.opciones_bano === 'string' ? (() => { try { return JSON.parse(cot.opciones_bano); } catch { return {}; } })() : (cot.opciones_bano || {}),
          incremento_global: cot.incremento_global || 0,
          notas: cot.notas || '',
          descuento: cot.descuento || 0,
          plan_pagos: (cot.plan_pagos && cot.plan_pagos.length > 0)
            ? cot.plan_pagos.map(p => ({ descripcion: p.descripcion, porcentaje: parseFloat(p.porcentaje) || 0 }))
            : [
                { descripcion: 'Separacion', porcentaje: 10 },
                { descripcion: 'Inicio de obra', porcentaje: 40 },
                { descripcion: 'Avance de obra', porcentaje: 20 },
                { descripcion: 'Saldo final', porcentaje: 20 },
                { descripcion: 'Entrega', porcentaje: 10 }
              ]
        });

        // Cargar actividades
        const actividades = (cot.actividades || []).map(act => ({
          actividad_id: act.actividad_id,
          codigo: act.codigo,
          descripcion: act.descripcion,
          capitulo: act.capitulo,
          categoria: act.categoria,
          unidad: act.unidad,
          cantidad: parseFloat(act.cantidad) || 1,
          valor_unitario_base: parseFloat(act.valor_unitario_base) || parseFloat(act.valor_unitario) || 0,
          valor_unitario: parseFloat(act.valor_unitario) || 0,
          valor_total: parseFloat(act.valor_total) || 0,
          incremento: parseFloat(act.incremento) || 0,
          factor_desperdicio: parseFloat(act.factor_desperdicio) || 1,
          factor_multiplicador: parseFloat(act.factor_multiplicador) || 1,
          espacio: act.espacio,
          cantidad_predeterminada: act.cantidad_predeterminada || '',
          cantidad_manual: true, // Respetar cantidades guardadas en BD
          activo_matriz: act.activo_matriz !== undefined ? parseInt(act.activo_matriz) : 1,
          // El id es lo que permite guardar el candado en la BD al alternarlo.
          id: act.id,
          bloqueada: !!act.bloqueada
        }));
        setActividadesSeleccionadas(actividades);

        // Cargar totales desde la BD (no recalcular desde actividades)
        setTotales({
          subtotal: parseFloat(cot.subtotal) || 0,
          aiu_porcentaje: parseFloat(cot.aiu_porcentaje) || 15,
          aiu_valor: parseFloat(cot.aiu_valor) || 0,
          iva_valor: parseFloat(cot.iva_valor) || 0,
          valor_final: parseFloat(cot.valor_final) || 0,
          valor_final_descuento: parseFloat(cot.valor_final_descuento) || 0
        });

        setCotizacionEditando(cot);
        setTipoCotizacion(cot.tipo === 'especial' ? 'especial' : 'nueva');
        setVista('editar');
        // Desbloquear recálculos después de que React procese los cambios
        setTimeout(() => { cargandoCotizacionRef.current = false; }, 500);
      } else {
        cargandoCotizacionRef.current = false;
        error('No se pudo cargar la cotización');
      }
    } catch (err) {
      cargandoCotizacionRef.current = false;
      error('Error al cargar cotización');
    } finally {
      setLoading(false);
    }
  };

  const duplicarCotizacion = async (id) => {
    try {
      setLoading(true);
      cargandoCotizacionRef.current = true;
      const gqlCotR = await client.query(`query($id: Int!) { cotizacion(id: $id) { id consecutivo tipo fecha estado nombre_cliente cedula_cliente telefono_cliente correo_cliente zona_barrio zona proyecto_nombre apartamento area_m2 direccion habitaciones banos area_banos numero_puertas metros_zocalo pregunta_agua_caliente pregunta_cambia_cocina pregunta_plafones_led pregunta_conserva_bano pregunta_cajones_drywall plano_id forma_cocina opciones_bano subtotal aiu_porcentaje aiu_valor iva_valor valor_final descuento valor_final_descuento incremento_global notas bano_social_alto bano_social_ancho bano_ppal_alto bano_ppal_ancho balcon altura_ducha avance_ffe proyecto_id plano_nombre plano_url actividades { id cotizacion_id actividad_id codigo descripcion capitulo categoria unidad cantidad valor_unitario valor_unitario_base valor_total espacio orden incremento factor_desperdicio factor_multiplicador activo_matriz cantidad_predeterminada bloqueada bloqueada_at } plan_pagos { id cotizacion_id descripcion porcentaje valor orden } } }`, { id });
      const data = { success: !!gqlCotR.cotizacion, cotizacion: gqlCotR.cotizacion };

      if (data.success && data.cotizacion) {
        const cot = data.cotizacion;
        // Toda copia (de una Base o de una cotización normal) nace con el incremento
        // por defecto vigente y los precios se recalculan con él. Antes solo pasaba
        // con las Bases y las copias de cotizaciones viejas heredaban su incremento
        // (así nació COT-1574 con 15 % en octubre de 2026).
        const esBase = ESTADOS_BASE.includes(cot.estado);
        const incGlobal = incrementoDefecto;
        prevIncGlobalRef.current = incGlobal;   // el efecto del incremento no debe recalcular otra vez
        duplicadaDeRef.current = cot.consecutivo || null;
        setFormData({
          fecha: getFechaColombia(),
          nombre_cliente: cot.nombre_cliente || '',
          cedula_cliente: cot.cedula_cliente || '',
          telefono_cliente: cot.telefono_cliente || '',
          correo_cliente: cot.correo_cliente || '',
          zona_barrio: cot.zona_barrio || '',
          zona: cot.zona || '',
          proyecto_nombre: cot.proyecto_nombre || '',
          apartamento: cot.apartamento || '',
          area_m2: cot.area_m2 || '',
          direccion: cot.direccion || '',
          habitaciones: cot.habitaciones || '',
          banos: cot.banos || '',
          bano_social_alto: cot.bano_social_alto || '',
          bano_social_ancho: cot.bano_social_ancho || '',
          bano_ppal_alto: cot.bano_ppal_alto || '',
          bano_ppal_ancho: cot.bano_ppal_ancho || '',
          balcon: cot.balcon || '',
          altura_ducha: cot.altura_ducha || '',
          numero_puertas: cot.numero_puertas || '',
          metros_zocalo: cot.metros_zocalo || '',
          plano_id: cot.plano_id || '',
          forma_cocina: cot.forma_cocina || '',
          opciones_bano: typeof cot.opciones_bano === 'string' ? (() => { try { return JSON.parse(cot.opciones_bano); } catch { return {}; } })() : (cot.opciones_bano || {}),
          incremento_global: incGlobal,
          notas: cot.notas || '',
          descuento: cot.descuento || 0,
          plan_pagos: (cot.plan_pagos && cot.plan_pagos.length > 0)
            ? cot.plan_pagos.map(p => ({ descripcion: p.descripcion, porcentaje: parseFloat(p.porcentaje) || 0 }))
            : [
                { descripcion: 'Separacion', porcentaje: 10 },
                { descripcion: 'Inicio de obra', porcentaje: 40 },
                { descripcion: 'Avance de obra', porcentaje: 20 },
                { descripcion: 'Saldo final', porcentaje: 20 },
                { descripcion: 'Entrega', porcentaje: 10 }
              ]
        });

        const actividades = (cot.actividades || []).map(act => ({
          actividad_id: act.actividad_id,
          codigo: act.codigo,
          descripcion: act.descripcion,
          capitulo: act.capitulo,
          categoria: act.categoria,
          unidad: act.unidad,
          cantidad: parseFloat(act.cantidad) || 1,
          valor_unitario_base: parseFloat(act.valor_unitario_base) || parseFloat(act.valor_unitario) || 0,
          valor_unitario: parseFloat(act.valor_unitario) || 0,
          valor_total: parseFloat(act.valor_total) || 0,
          incremento: parseFloat(act.incremento) || 0,
          factor_desperdicio: parseFloat(act.factor_desperdicio) || 1,
          factor_multiplicador: parseFloat(act.factor_multiplicador) || 1,
          espacio: act.espacio,
          cantidad_predeterminada: act.cantidad_predeterminada || '',
          cantidad_manual: true, // Respetar cantidades guardadas en BD
          activo_matriz: act.activo_matriz !== undefined ? parseInt(act.activo_matriz) : 1,
          // Al duplicar, el candado viaja con la actividad (sin id: son filas nuevas).
          bloqueada: !!act.bloqueada
        })).map(act => {
          // Precio = base de la matriz con el incremento por defecto (la misma
          // fórmula que al agregar una actividad o cambiar el incremento).
          const vu = Math.floor(act.valor_unitario_base * (1 + incGlobal / 100));
          const vt = Math.floor(vu * (1 + act.incremento / 100) * act.factor_desperdicio * act.factor_multiplicador) * act.cantidad;
          return { ...act, valor_unitario: vu, valor_total: vt };
        });
        setActividadesSeleccionadas(actividades);

        // Cargar totales desde la BD
        setTotales({
          subtotal: parseFloat(cot.subtotal) || 0,
          aiu_porcentaje: parseFloat(cot.aiu_porcentaje) || 15,
          aiu_valor: parseFloat(cot.aiu_valor) || 0,
          iva_valor: parseFloat(cot.iva_valor) || 0,
          valor_final: parseFloat(cot.valor_final) || 0,
          valor_final_descuento: parseFloat(cot.valor_final_descuento) || 0
        });

        // No establecer cotizacionEditando para que se cree como nueva.
        // La copia conserva el tipo: una especial se duplica como especial.
        setCotizacionEditando(null);
        setTipoCotizacion(cot.tipo === 'especial' ? 'especial' : 'nueva');
        setVista('crear');
        setTimeout(() => {
          cargandoCotizacionRef.current = false;
          // Los precios cambiaron con el incremento: recalcular los totales
          setActividadesSeleccionadas(prev => [...prev]);
        }, 500);
        success(`Cotización duplicada de COT-${cot.consecutivo}${esBase ? ' (Base)' : ''} con el incremento por defecto (${incGlobal} %). Modifique los datos y guarde.`);
      } else {
        cargandoCotizacionRef.current = false;
        error('No se pudo cargar la cotización para duplicar');
      }
    } catch (err) {
      cargandoCotizacionRef.current = false;
      error('Error al duplicar cotización');
    } finally {
      setLoading(false);
    }
  };

  // Duplicar cotización vieja → abrir como cotización nueva para editar
  const duplicarViejaComoNueva = async (id) => {
    try {
      setLoading(true);
      cargandoCotizacionRef.current = true;
      const gqlViejaR = await client.query(`query($id: Int!) { cotizacionVieja(id: $id) { id consecutivo fecha nombre_cliente telefono_cliente correo_cliente proyecto_nombre apartamento area_m2 habitaciones banos subtotal aiu_porcentaje aiu_valor iva_valor valor_final estado avance_ffe proyecto_id actividades { id cotizacion_id codigo descripcion categoria unidad cantidad valor_unitario incremento factor_desperdicio factor_multiplicador valor_total ficha_ffe orden activo_matriz } } }`, { id });
      const data = { success: !!gqlViejaR.cotizacionVieja, cotizacion: gqlViejaR.cotizacionVieja };

      if (data.success && data.cotizacion) {
        const cot = data.cotizacion;
        const actividadesVieja = cot.actividades || [];

        // Resolver códigos → actividad_id desde matriz_actividades (una sola query)
        const codigos = [...new Set(actividadesVieja.map(a => a.codigo).filter(Boolean))];
        let mapaMatriz = {};
        if (codigos.length > 0) {
          try {
            const matData = await client.query(`query($codigos: [String!]!) { matrizPorCodigos(codigos: $codigos) { id codigo } }`, { codigos });
            (matData.matrizPorCodigos || []).forEach(m => { mapaMatriz[m.codigo] = m.id; });
          } catch (e) { console.error('Error resolviendo codigos:', e); }
        }

        // Cargar datos del cliente/inmueble en formData
        setFormData({
          fecha: getFechaColombia(),
          nombre_cliente: cot.nombre_cliente || '',
          cedula_cliente: '',
          telefono_cliente: cot.telefono_cliente || '',
          correo_cliente: cot.correo_cliente || '',
          zona_barrio: '',
          zona: '',
          proyecto_nombre: cot.proyecto_nombre || '',
          apartamento: cot.apartamento || '',
          area_m2: cot.area_m2 || '',
          direccion: '',
          habitaciones: cot.habitaciones || '',
          banos: cot.banos || '',
          bano_social_alto: '',
          bano_social_ancho: '',
          bano_ppal_alto: '',
          bano_ppal_ancho: '',
          balcon: '',
          altura_ducha: '',
          numero_puertas: '',
          metros_zocalo: '',
          plano_id: '',
          forma_cocina: '',
          opciones_bano: {},
          incremento_global: 0,
          notas: '',
          descuento: 0,
          plan_pagos: [
            { descripcion: 'Separacion', porcentaje: 10 },
            { descripcion: 'Inicio de obra', porcentaje: 40 },
            { descripcion: 'Avance de obra', porcentaje: 20 },
            { descripcion: 'Saldo final', porcentaje: 20 },
            { descripcion: 'Entrega', porcentaje: 10 }
          ]
        });

        // Mapear actividades de vieja al formato de cotización nueva (con actividad_id resuelto)
        // Los valores se copian TAL CUAL de la cotización vieja (sin recalcular)
        const actividades = actividadesVieja.map(act => {
          const matrizId = mapaMatriz[act.codigo] || null;
          return {
            actividad_id: matrizId ? parseInt(matrizId) : null,
            codigo: act.codigo || '',
            descripcion: act.descripcion || '',
            capitulo: act.categoria || '',
            categoria: act.categoria || '',
            unidad: act.unidad || '',
            cantidad: parseFloat(act.cantidad) || 1,
            valor_unitario_base: parseFloat(act.valor_unitario) || 0,
            valor_unitario: parseFloat(act.valor_unitario) || 0,
            valor_total: parseFloat(act.valor_total) || 0,
            incremento: parseFloat(act.incremento) || 0,
            factor_desperdicio: parseFloat(act.factor_desperdicio) || 1,
            factor_multiplicador: parseFloat(act.factor_multiplicador) || 1,
            espacio: act.categoria || '',
            cantidad_predeterminada: '',
            cantidad_manual: true,
            activo_matriz: act.activo_matriz !== undefined ? parseInt(act.activo_matriz) : 1
          };
        }).filter(act => act.actividad_id !== null); // Solo actividades con match en la matriz

        if (actividades.length === 0) {
          error('No se encontraron actividades con código válido en la matriz');
          setLoading(false);
          return;
        }

        const descartadas = actividadesVieja.length - actividades.length;
        setActividadesSeleccionadas(actividades);

        // Abrir como nueva cotización (sin cotizacionEditando). Las viejas viven en
        // otra tabla, así que no se registra como "duplicada de".
        duplicadaDeRef.current = null;
        setCotizacionEditando(null);
        setTipoCotizacion('nueva');
        setVista('crear');
        setTimeout(() => { cargandoCotizacionRef.current = false; }, 500);
        success(`Cotización cargada con ${actividades.length} actividades.${descartadas > 0 ? ` ${descartadas} sin match en matriz descartadas.` : ''} Modifique y guarde.`);
      } else {
        cargandoCotizacionRef.current = false;
        error('No se pudo cargar la cotización vieja');
      }
    } catch (err) {
      cargandoCotizacionRef.current = false;
      console.error('Error duplicando vieja:', err);
      error('Error al cargar cotización vieja');
    } finally {
      setLoading(false);
    }
  };

  const guardarCotizacion = async (esBorrador = false) => {
    // Cancelar autoguardado pendiente Y request en vuelo para evitar race conditions
    if (autoguardadoRef.current) clearTimeout(autoguardadoRef.current);
    if (autoguardadoAbortRef.current) autoguardadoAbortRef.current.abort();
    autoguardadoHabilitado.current = false;

    // Validaciones: relajadas para borrador, completas para guardar
    if (!esBorrador) {
      const camposObligatorios = [
        { campo: 'nombre_cliente', label: 'Nombre del cliente' },
        { campo: 'cedula_cliente', label: 'Cedula/NIT del cliente' },
        { campo: 'telefono_cliente', label: 'Telefono del cliente' },
        { campo: 'correo_cliente', label: 'Correo del cliente' },
        { campo: 'zona_barrio', label: 'Zona/Barrio' },
        { campo: 'proyecto_nombre', label: 'Nombre del proyecto' },
        { campo: 'apartamento', label: 'Apartamento' },
        { campo: 'area_m2', label: 'Area (m2)' },
        { campo: 'direccion', label: 'Direccion' },
        { campo: 'habitaciones', label: 'Habitaciones' },
        { campo: 'banos', label: 'Banos' },
        { campo: 'bano_social_alto', label: 'Bano Social - Lado A' },
        { campo: 'bano_social_ancho', label: 'Bano Social - Lado B' },
        { campo: 'bano_ppal_alto', label: 'Bano Principal - Lado A' },
        { campo: 'bano_ppal_ancho', label: 'Bano Principal - Lado B' },
        { campo: 'balcon', label: 'Balcon (m2)' },
        { campo: 'altura_ducha', label: 'Altura Ducha (m)' },
        { campo: 'numero_puertas', label: 'Numero de Puertas' },
        { campo: 'metros_zocalo', label: 'Metros de Zocalo' },
      ];
      for (const { campo, label } of camposObligatorios) {
        const val = formData[campo];
        if (val === undefined || val === null || val === '' || (typeof val === 'string' && !val.trim())) {
          error(`El campo "${label}" es obligatorio`);
          return;
        }
      }
    } else {
      // Para borrador solo necesitamos al menos un dato identificable
      if (!formData.nombre_cliente.trim() && !formData.proyecto_nombre.trim()) {
        error('Ingrese al menos el nombre del cliente o del proyecto para guardar el borrador');
        return;
      }
    }

    try {
      setLoading(true);

      // De-duplicar actividades por actividad_id (mantener primera ocurrencia).
      // En las especiales NO: ahí una actividad puede ir varias veces.
      const actividadesUnicas = esEspecial ? actividadesSeleccionadas : actividadesSeleccionadas.filter((act, idx, arr) =>
        !act.actividad_id || arr.findIndex(a => a.actividad_id === act.actividad_id) === idx
      );

      // Calcular totales inline desde las actividades para garantizar consistencia
      const subtotalCalc = actividadesUnicas.reduce((sum, act) => sum + (parseFloat(act.valor_total) || 0), 0);
      const aiuPorcentajeCalc = 15;
      const aiuValorCalc = subtotalCalc * (aiuPorcentajeCalc / 100);
      const ivaValorCalc = subtotalCalc * 0.19 * 0.05;
      const valorFinalCalc = subtotalCalc + aiuValorCalc + ivaValorCalc;
      const descuentoCalc = parseFloat(formData.descuento) || 0;
      const valorFinalDescuentoCalc = valorFinalCalc - descuentoCalc;
      const valorFinalRef = valorFinalDescuentoCalc || valorFinalCalc;

      // Si la cotización ya está en un estado terminal/contratado, NO bajarla a 'enviada' al guardar.
      const estadoActual = cotizacionEditando?.estado || formData.estado;
      // Estados que NO se pisan al guardar el formulario: los terminales y los de
      // base (una base editada debe seguir siendo base, no volver a 'enviada').
      const estadoTerminal = ['contratado', 'vendido', 'rechazada', 'base', 'base_cotizador_web'];
      const calcEstado = () => {
        if (esBorrador) return 'borrador';
        if (estadoActual && estadoTerminal.includes(estadoActual)) return estadoActual; // preservar
        if (estadoActual === 'borrador') return 'enviada';
        return formData.estado || 'enviada';
      };
      const payload = {
        ...formData,
        estado: calcEstado(),
        actividades: actividadesUnicas,
        subtotal: subtotalCalc,
        aiu_porcentaje: aiuPorcentajeCalc,
        aiu_valor: aiuValorCalc,
        iva_valor: ivaValorCalc,
        valor_final: valorFinalCalc,
        valor_final_descuento: valorFinalDescuentoCalc,
        user_id: user?.id || null,
        plan_pagos: formData.plan_pagos.map((p, i) => ({
          descripcion: p.descripcion,
          porcentaje: parseFloat(p.porcentaje) || 0,
          valor: Math.round(valorFinalRef * (parseFloat(p.porcentaje) || 0) / 100),
          orden: i
        }))
      };

      // Siempre usar update si ya existe un id
      const gqlInput = {
        tipo: tipoCotizacion,   // al editar el backend lo ignora
        fecha: payload.fecha, estado: payload.estado, nombre_cliente: payload.nombre_cliente,
        cedula_cliente: payload.cedula_cliente, telefono_cliente: payload.telefono_cliente,
        correo_cliente: payload.correo_cliente, zona_barrio: payload.zona_barrio, zona: payload.zona || null,
        proyecto_nombre: payload.proyecto_nombre,
        apartamento: payload.apartamento, area_m2: payload.area_m2 ? parseFloat(payload.area_m2) : null,
        direccion: payload.direccion, habitaciones: payload.habitaciones ? parseInt(payload.habitaciones) : null,
        banos: payload.banos ? parseInt(payload.banos) : null, area_banos: payload.area_banos ? parseFloat(payload.area_banos) : null,
        numero_puertas: payload.numero_puertas ? parseInt(payload.numero_puertas) : null,
        metros_zocalo: payload.metros_zocalo ? parseFloat(payload.metros_zocalo) : null,
        pregunta_agua_caliente: payload.pregunta_agua_caliente, pregunta_cambia_cocina: payload.pregunta_cambia_cocina,
        pregunta_plafones_led: payload.pregunta_plafones_led, pregunta_conserva_bano: payload.pregunta_conserva_bano,
        pregunta_cajones_drywall: payload.pregunta_cajones_drywall,
        plano_id: payload.plano_id ? parseInt(payload.plano_id) : null, forma_cocina: payload.forma_cocina,
        opciones_bano: typeof payload.opciones_bano === 'object' ? JSON.stringify(payload.opciones_bano) : payload.opciones_bano,
        subtotal: parseFloat(payload.subtotal) || 0, aiu_porcentaje: parseFloat(payload.aiu_porcentaje) || 0, aiu_valor: parseFloat(payload.aiu_valor) || 0,
        iva_valor: parseFloat(payload.iva_valor) || 0, valor_final: parseFloat(payload.valor_final) || 0, descuento: parseFloat(payload.descuento) || 0,
        valor_final_descuento: parseFloat(payload.valor_final_descuento) || 0, incremento_global: parseFloat(payload.incremento_global) || 0,
        notas: payload.notas,
        bano_social_alto: payload.bano_social_alto ? parseFloat(payload.bano_social_alto) : null,
        bano_social_ancho: payload.bano_social_ancho ? parseFloat(payload.bano_social_ancho) : null,
        bano_ppal_alto: payload.bano_ppal_alto ? parseFloat(payload.bano_ppal_alto) : null,
        bano_ppal_ancho: payload.bano_ppal_ancho ? parseFloat(payload.bano_ppal_ancho) : null,
        balcon: payload.balcon ? parseFloat(payload.balcon) : null, altura_ducha: payload.altura_ducha ? parseFloat(payload.altura_ducha) : null,
        // Solo se manda si se conoce: mandar null borraba el vínculo con el proyecto
        ...(payload.proyecto_id ? { proyecto_id: parseInt(payload.proyecto_id) } : {}),
        // Solo al crear una copia: de qué cotización salió (para el historial)
        ...(!cotizacionEditando && duplicadaDeRef.current ? { duplicada_de: parseInt(duplicadaDeRef.current) } : {}),
        user_id: payload.user_id,
        actividades: (payload.actividades || []).map(a => ({
          actividad_id: a.actividad_id, codigo: a.codigo, descripcion: a.descripcion,
          capitulo: a.capitulo || '', categoria: a.categoria || '', unidad: a.unidad || '',
          cantidad: parseFloat(a.cantidad) || 1, valor_unitario: parseFloat(a.valor_unitario) || 0,
          valor_unitario_base: parseFloat(a.valor_unitario_base) || 0, valor_total: parseFloat(a.valor_total) || 0,
          espacio: a.espacio || '', orden: a.orden, incremento: parseFloat(a.incremento) || 0,
          factor_desperdicio: parseFloat(a.factor_desperdicio) || 1, factor_multiplicador: parseFloat(a.factor_multiplicador) || 1,
          bloqueada: !!a.bloqueada,
        })),
        plan_pagos: (payload.plan_pagos || []).map(p => ({
          descripcion: p.descripcion, porcentaje: parseFloat(p.porcentaje) || 0, valor: parseFloat(p.valor) || 0, orden: p.orden,
        })),
      };

      let data;
      if (cotizacionEditando) {
        const r = await client.mutate(`mutation($id: Int!, $input: CotizacionSaveInput!) { actualizarCotizacion(id: $id, input: $input) { success message id consecutivo } }`, { id: cotizacionEditando.id, input: gqlInput });
        data = r.actualizarCotizacion;
      } else {
        const r = await client.mutate(`mutation($input: CotizacionSaveInput!) { crearCotizacion(input: $input) { success message id consecutivo } }`, { input: gqlInput });
        data = r.crearCotizacion;
        if (data?.success) duplicadaDeRef.current = null;
      }

      if (data.success) {
        if (esBorrador) {
          success(cotizacionEditando ? 'Borrador actualizado' : `Borrador #${data.consecutivo} guardado`);
        } else {
          success(cotizacionEditando ? 'Cotización actualizada' : `Cotización #${data.consecutivo} creada`);
        }
        resetForm();
        setVista('lista');
        cargarCotizaciones();
      } else {
        error(data.message || 'Error al guardar');
      }
    } catch (err) {
      error('Error al guardar cotización');
    } finally {
      setLoading(false);
    }
  };

  // Autoguardado silencioso
  const autoguardar = useCallback(async () => {
    // Evitar ejecuciones simultáneas
    if (autoguardadoEnCurso.current) return;
    // Necesitamos al menos un dato para guardar
    if (!formData.nombre_cliente.trim() && !formData.proyecto_nombre.trim()) return;

    autoguardadoEnCurso.current = true;
    setAutoguardando(true);
    // Crear AbortController para poder cancelar esta request
    const abortController = new AbortController();
    autoguardadoAbortRef.current = abortController;
    try {
      // De-duplicar actividades por actividad_id (en las especiales no se hace)
      const actividadesUnicas = esEspecial ? actividadesSeleccionadas : actividadesSeleccionadas.filter((act, idx, arr) =>
        !act.actividad_id || arr.findIndex(a => a.actividad_id === act.actividad_id) === idx
      );

      // Calcular totales inline desde las actividades para garantizar consistencia
      const subtotalAuto = actividadesUnicas.reduce((sum, act) => sum + (parseFloat(act.valor_total) || 0), 0);
      const aiuValorAuto = subtotalAuto * 0.15;
      const ivaValorAuto = subtotalAuto * 0.19 * 0.05;
      const valorFinalAuto = subtotalAuto + aiuValorAuto + ivaValorAuto;
      const descuentoAuto = parseFloat(formData.descuento) || 0;
      const valorFinalDescuentoAuto = valorFinalAuto - descuentoAuto;
      const valorFinalRef = valorFinalDescuentoAuto || valorFinalAuto;

      const payload = {
        ...formData,
        estado: cotizacionEditando?.estado || 'borrador',
        actividades: actividadesUnicas,
        subtotal: subtotalAuto,
        aiu_porcentaje: 15,
        aiu_valor: aiuValorAuto,
        iva_valor: ivaValorAuto,
        valor_final: valorFinalAuto,
        valor_final_descuento: valorFinalDescuentoAuto,
        plan_pagos: formData.plan_pagos.map((p, i) => ({
          descripcion: p.descripcion,
          porcentaje: parseFloat(p.porcentaje) || 0,
          valor: Math.round(valorFinalRef * (parseFloat(p.porcentaje) || 0) / 100),
          orden: i
        }))
      };

      const autoInput = {
        tipo: tipoCotizacion,
        fecha: payload.fecha, estado: payload.estado || 'borrador', nombre_cliente: payload.nombre_cliente,
        cedula_cliente: payload.cedula_cliente, telefono_cliente: payload.telefono_cliente,
        correo_cliente: payload.correo_cliente, zona_barrio: payload.zona_barrio, zona: payload.zona || null,
        proyecto_nombre: payload.proyecto_nombre,
        apartamento: payload.apartamento, area_m2: payload.area_m2 ? parseFloat(payload.area_m2) : null,
        habitaciones: payload.habitaciones ? parseInt(payload.habitaciones) : null,
        banos: payload.banos ? parseInt(payload.banos) : null,
        area_banos: payload.area_banos ? parseFloat(payload.area_banos) : null,
        numero_puertas: payload.numero_puertas ? parseInt(payload.numero_puertas) : null,
        metros_zocalo: payload.metros_zocalo ? parseFloat(payload.metros_zocalo) : null,
        bano_social_alto: payload.bano_social_alto ? parseFloat(payload.bano_social_alto) : null,
        bano_social_ancho: payload.bano_social_ancho ? parseFloat(payload.bano_social_ancho) : null,
        bano_ppal_alto: payload.bano_ppal_alto ? parseFloat(payload.bano_ppal_alto) : null,
        bano_ppal_ancho: payload.bano_ppal_ancho ? parseFloat(payload.bano_ppal_ancho) : null,
        balcon: payload.balcon ? parseFloat(payload.balcon) : null,
        altura_ducha: payload.altura_ducha ? parseFloat(payload.altura_ducha) : null,
        plano_id: payload.plano_id ? parseInt(payload.plano_id) : null,
        forma_cocina: payload.forma_cocina,
        direccion: payload.direccion,
        pregunta_agua_caliente: payload.pregunta_agua_caliente, pregunta_cambia_cocina: payload.pregunta_cambia_cocina,
        pregunta_plafones_led: payload.pregunta_plafones_led, pregunta_conserva_bano: payload.pregunta_conserva_bano,
        pregunta_cajones_drywall: payload.pregunta_cajones_drywall,
        opciones_bano: typeof payload.opciones_bano === 'object' ? JSON.stringify(payload.opciones_bano) : payload.opciones_bano,
        subtotal: parseFloat(payload.subtotal) || 0, aiu_porcentaje: 15, aiu_valor: parseFloat(payload.aiu_valor) || 0,
        iva_valor: parseFloat(payload.iva_valor) || 0, valor_final: parseFloat(payload.valor_final) || 0,
        descuento: parseFloat(payload.descuento) || 0, valor_final_descuento: parseFloat(payload.valor_final_descuento) || 0,
        incremento_global: parseFloat(payload.incremento_global) || 0,
        notas: payload.notas, user_id: user?.id,
        actividades: (payload.actividades || []).map(a => ({
          actividad_id: a.actividad_id, codigo: a.codigo, descripcion: a.descripcion,
          capitulo: a.capitulo || '', categoria: a.categoria || '', unidad: a.unidad || '',
          cantidad: parseFloat(a.cantidad) || 1, valor_unitario: parseFloat(a.valor_unitario) || 0,
          valor_unitario_base: parseFloat(a.valor_unitario_base) || 0, valor_total: parseFloat(a.valor_total) || 0,
          espacio: a.espacio || '', orden: a.orden, incremento: parseFloat(a.incremento) || 0,
          factor_desperdicio: parseFloat(a.factor_desperdicio) || 1, factor_multiplicador: parseFloat(a.factor_multiplicador) || 1,
          bloqueada: !!a.bloqueada,
        })),
        plan_pagos: (payload.plan_pagos || []).map(p => ({
          descripcion: p.descripcion, porcentaje: parseFloat(p.porcentaje) || 0, valor: parseFloat(p.valor) || 0, orden: p.orden,
        })),
      };

      let data;
      if (cotizacionEditando) {
        const r = await client.mutate(`mutation($id: Int!, $input: CotizacionSaveInput!) { actualizarCotizacion(id: $id, input: $input) { success message id consecutivo } }`, { id: cotizacionEditando.id, input: autoInput });
        data = r.actualizarCotizacion;
      } else {
        autoInput.estado = 'borrador';
        const r = await client.mutate(`mutation($input: CotizacionSaveInput!) { crearCotizacion(input: $input) { success message id consecutivo } }`, { input: autoInput });
        data = r.crearCotizacion;
      }

      if (data.success) {
        setUltimoAutoguardado(new Date());
        // Si era nueva, marcarla como editando para que todo lo siguiente sea PUT
        if (!cotizacionEditando && data.id) {
          setCotizacionEditando({ id: data.id, consecutivo: data.consecutivo, estado: 'borrador' });
          setVista('editar');
        }
      }
    } catch (err) {
      if (err.name === 'AbortError') return; // Cancelado intencionalmente, no es error
    } finally {
      autoguardadoEnCurso.current = false;
      setAutoguardando(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData, actividadesSeleccionadas, totales, cotizacionEditando]);

  const eliminarCotizacion = async (id) => {
    if (!window.confirm('¿Está seguro de eliminar esta cotización?')) return;

    try {
      const gqlDel = await client.mutate(`mutation($id: Int!) { eliminarCotizacion(id: $id) { success message } }`, { id });

      if (gqlDel.eliminarCotizacion.success) {
        success('Cotización eliminada');
        cargarCotizaciones();
      } else {
        error(gqlDel.eliminarCotizacion.message || 'Error al eliminar');
      }
    } catch (err) {
      error('Error al eliminar cotización');
    }
  };

  const eliminarCotizacionVieja = async (id) => {
    if (!window.confirm('¿Está seguro de eliminar esta cotización importada?')) return;
    try {
      const gqlDelV = await client.mutate(`mutation($id: Int!) { eliminarCotizacionVieja(id: $id) { success message } }`, { id });
      if (gqlDelV.eliminarCotizacionVieja.success) {
        success('Cotización eliminada');
        cargarCotizacionesViejas();
      } else {
        error(gqlDelV.eliminarCotizacionVieja.message || 'Error al eliminar');
      }
    } catch (err) {
      error('Error al eliminar cotización');
    }
  };

  // Motivos de perdida: al rechazar hay que elegir uno. Se guarda el codigo en la
  // BD para poder agrupar en el informe aunque cambie el texto que se muestra.
  const MOTIVOS_RECHAZO = [
    { codigo: 'precio_competencia', etiqueta: 'Precio competencia' },
    { codigo: 'alcance', etiqueta: 'Alcance' },
    { codigo: 'ya_contrato', etiqueta: 'Ya contrato' },
    { codigo: 'tiempo_ejecucion', etiqueta: 'Tiempo de ejecucion' },
    { codigo: 'sin_determinar', etiqueta: 'Sin determinar' },
    { codigo: 'desiste_proyecto', etiqueta: 'Desiste del proyecto' },
    { codigo: 'otros', etiqueta: 'Otros' },
  ];

  const iniciarRechazo = (id, tipo) => {
    setModalRechazo({ visible: true, id, tipo, motivo: '', detalle: '', guardando: false });
  };

  const confirmarRechazo = async () => {
    const { id, tipo, motivo, detalle } = modalRechazo;
    if (!motivo) { error('Selecciona el motivo de perdida'); return; }
    if (motivo === 'otros' && !detalle.trim()) { error('Escribe cual fue el motivo'); return; }
    setModalRechazo(prev => ({ ...prev, guardando: true }));
    const esVieja = tipo === 'vieja';
    const campo = esVieja ? 'cambiarEstadoCotizacionVieja' : 'cambiarEstadoCotizacion';
    try {
      const r = await client.mutate(
        `mutation($id: Int!, $estado: String!, $motivo: String, $detalle: String) { ${campo}(id: $id, estado: $estado, motivo_rechazo: $motivo, motivo_rechazo_otro: $detalle) { success message } }`,
        { id, estado: 'rechazada', motivo, detalle: motivo === 'otros' ? detalle.trim() : null }
      );
      if (r[campo].success) {
        success('Cotizacion rechazada');
        setModalRechazo({ visible: false, id: null, tipo: null, motivo: '', detalle: '', guardando: false });
        if (esVieja) cargarCotizacionesViejas(); else cargarCotizaciones();
      } else {
        error(r[campo].message || 'Error al rechazar');
        setModalRechazo(prev => ({ ...prev, guardando: false }));
      }
    } catch (err) {
      error('Error al rechazar');
      setModalRechazo(prev => ({ ...prev, guardando: false }));
    }
  };

  const cambiarEstadoVieja = async (id, nuevoEstado, fechaProbInicio = null) => {
    if (nuevoEstado === 'contratado' && !fechaProbInicio) {
      setModalContratado({ id, tipo: 'vieja' });
      setFechaInicioContratado('');
      return;
    }
    try {
      const gqlCE = await client.mutate(`mutation($id: Int!, $estado: String!, $fecha: String) { cambiarEstadoCotizacionVieja(id: $id, estado: $estado, fecha_probable_inicio: $fecha) { success message } }`, { id, estado: nuevoEstado, fecha: fechaProbInicio });
      if (gqlCE.cambiarEstadoCotizacionVieja.success) {
        success(`Cotización marcada como ${nuevoEstado}`);
        cargarCotizacionesViejas();
      } else {
        error(gqlCE.cambiarEstadoCotizacionVieja.message || 'Error al cambiar estado');
      }
    } catch (err) {
      error('Error al cambiar estado');
    }
  };

  // Etiqueta legible para el badge de estado
  const estadoLabel = (e) => {
    if (e === 'base_cotizador_web') return 'Base-Cotizador Web';
    if (e === 'cotizador_web') return 'Cotizador Web';
    return e || 'importada';
  };

  const cerrarModalBase = () => setModalBase({ visible: false, cotId: null, loading: false, tipo: null });

  // Fila de la tabla de cotizaciones (reutilizada por la pestaña "Cotizador Web")
  const renderFilaCotizacion = (cot) => (
    <tr key={cot.id}>
      <td>{cot.consecutivo}</td>
      <td>{cot.fecha}</td>
      <td>{cot.nombre_cliente}</td>
      <td>{cot.proyecto_nombre}</td>
      <td>{cot.plano_nombre || '-'}</td>
      <td>{formatCurrency(cot.valor_final_descuento || cot.valor_final)}</td>
      <td>
        <span className={`estado-badge estado-${cot.estado}`}>
          {estadoLabel(cot.estado)}
        </span>
        {renderAprobacionesBase(cot)}
      </td>
      <td>
        <div className="acciones-btns">
          <button className="cot-btn-icon cot-btn-editar" title="Editar" onClick={() => editarCotizacion(cot.id)}>✏️</button>
          <button className="cot-btn-icon cot-btn-duplicar" title="Duplicar" onClick={() => duplicarCotizacion(cot.id)}>⧉</button>
          <button className="cot-btn-icon cot-btn-descargar" title="Descargar PDF" onClick={() => setPdfModalCot(cot)} disabled={generandoPDF}>{generandoPDF ? '...' : '📥'}</button>
          <button className="cot-btn-icon cot-btn-excel" title="Descargar Excel" onClick={() => descargarCotizacionExcel(cot.id)} disabled={generandoExcel}>{generandoExcel ? '...' : '📊'}</button>
          {cot.estado !== 'vendido' && cot.estado !== 'contratado' && (
            <button className="cot-btn-icon cot-btn-aprobar" title="Marcar como Vendido" onClick={() => iniciarVenta(cot, 'nueva')}>✓</button>
          )}
          {cot.estado === 'vendido' && (
            <button className="cot-btn-icon cot-btn-firmar" title="Marcar como Contratado" onClick={() => cambiarEstado(cot.id, 'contratado')}>✍</button>
          )}
          {cot.estado !== 'rechazada' && (
            <button className="cot-btn-icon cot-btn-rechazar" title="Rechazar" onClick={() => iniciarRechazo(cot.id, 'nueva')}>✗</button>
          )}
          <button className="cot-btn-icon cot-btn-base" title={['base', 'base_cotizador_web'].includes(cot.estado) ? 'Cambiar tipo de Base' : 'Marcar como Base'} onClick={() => setModalBase({ visible: true, cotId: cot.id, loading: false, tipo: 'nueva' })}>⚑</button>
          <button className="cot-btn-icon cot-btn-historial" title="Ver historial de cambios" onClick={() => abrirHistorial(cot)}>🕘</button>
          <button className="cot-btn-icon cot-btn-eliminar" title="Eliminar" onClick={() => eliminarCotizacion(cot.id)}>🗑️</button>
        </div>
      </td>
      <td className="col-usuario">
        {cot.creado_por_nombre ? (
          <><span className="usuario-nombre">{cot.creado_por_nombre}</span><span className="usuario-fecha">{cot.created_at ? new Date(cot.created_at).toLocaleString('es-CO', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}</span></>
        ) : <span className="usuario-vacio">-</span>}
      </td>
      <td className="col-usuario">
        {cot.modificado_por_nombre ? (
          <><span className="usuario-nombre">{cot.modificado_por_nombre}</span><span className="usuario-fecha">{cot.updated_at ? new Date(cot.updated_at).toLocaleString('es-CO', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}</span></>
        ) : <span className="usuario-vacio">-</span>}
      </td>
    </tr>
  );

  // Confirmar marcar como base (Base del sistema): actualizar precios desde la matriz
  const confirmarMarcarBase = async () => {
    const cotId = modalBase.cotId;
    if (!cotId) return;
    setModalBase(prev => ({ ...prev, loading: true }));
    try {
      const gqlBase = await client.mutate(`mutation($id: Int!) { actualizarPreciosBaseCotizacionVieja(id: $id) { success message no_encontradas } }`, { id: cotId });
      if (gqlBase.actualizarPreciosBaseCotizacionVieja.success) {
        let msg = 'Cotización marcada como Base. ' + (gqlBase.actualizarPreciosBaseCotizacionVieja.message || '');
        if (gqlBase.actualizarPreciosBaseCotizacionVieja.no_encontradas?.length > 0) {
          msg += `\nCódigos no encontrados: ${gqlBase.actualizarPreciosBaseCotizacionVieja.no_encontradas.join(', ')}`;
        }
        success(msg);
        cargarCotizacionesViejas();
      } else {
        error(gqlBase.actualizarPreciosBaseCotizacionVieja.message || 'Error al actualizar precios');
      }
    } catch (err) {
      error('Error al actualizar precios desde la matriz');
    } finally {
      cerrarModalBase();
    }
  };

  // Elegir el tipo de base desde el modal: 'sistema' (base actual) o 'cotizador_web'
  const elegirBase = async (opcion) => {
    const { cotId, tipo } = modalBase;
    if (!cotId) return;

    if (opcion === 'sistema') {
      // Base del sistema: mantiene el comportamiento actual de cada pestaña
      if (tipo === 'vieja') {
        await confirmarMarcarBase(); // actualiza precios desde la matriz + estado 'base'
      } else {
        await cambiarEstado(cotId, 'base');
        cerrarModalBase();
      }
      return;
    }

    // Base para cotizador web: solo marca el estado (sin tocar precios)
    setModalBase(prev => ({ ...prev, loading: true }));
    try {
      if (tipo === 'vieja') {
        await cambiarEstadoVieja(cotId, 'base_cotizador_web');
      } else {
        await cambiarEstado(cotId, 'base_cotizador_web');
      }
    } finally {
      cerrarModalBase();
    }
  };

  const cambiarEstado = async (id, nuevoEstado, fechaProbInicio = null) => {
    if (nuevoEstado === 'contratado' && !fechaProbInicio) {
      setModalContratado({ id, tipo: 'nueva' });
      setFechaInicioContratado('');
      return;
    }
    try {
      const gqlCEN = await client.mutate(`mutation($id: Int!, $estado: String!, $fecha: String) { cambiarEstadoCotizacion(id: $id, estado: $estado, fecha_probable_inicio: $fecha) { success message } }`, { id, estado: nuevoEstado, fecha: fechaProbInicio });
      if (gqlCEN.cambiarEstadoCotizacion.success) {
        success(`Cotización marcada como ${nuevoEstado}`);
        cargarCotizaciones();
      } else {
        error(gqlCEN.cambiarEstadoCotizacion.message || 'Error al cambiar estado');
      }
    } catch (err) {
      error('Error al cambiar estado');
    }
  };

  // Al marcar como vendida se abre un modal: si la venta fue por canal propio, además de
  // marcar la cotización como vendida, se registra automáticamente en Marketing → Registro
  // de Ventas (editable/eliminable allí como cualquier venta manual).
  const iniciarVenta = (cot, tipo) => {
    setModalVenta({ visible: true, cot, tipo, loading: false });
  };

  const confirmarVenta = async (canalPropio) => {
    const { cot, tipo } = modalVenta;
    if (!cot) return;
    setModalVenta(prev => ({ ...prev, loading: true }));
    try {
      // 1. Marcar la cotización como vendida (flujo normal)
      if (tipo === 'vieja') await cambiarEstadoVieja(cot.id, 'vendido');
      else await cambiarEstado(cot.id, 'vendido');

      // 2. Si fue por canal propio → registrar la venta en Marketing
      if (canalPropio) {
        const ahora = new Date();
        const valor = parseFloat(cot.valor_final_descuento || cot.valor_final || 0) || 0;
        let dias = 0;
        if (cot.fecha) {
          const d = Math.round((ahora - new Date(cot.fecha + 'T00:00:00')) / 86400000);
          if (!isNaN(d)) dias = Math.max(0, d);
        }
        const r = await client.mutate(
          `mutation($anio: Int!, $input: VentaMarketingInput!) { crearVentaMarketing(anio: $anio, input: $input) { success message } }`,
          { anio: ahora.getFullYear(), input: {
            mes: ahora.getMonth() + 1,
            nombre_cliente: cot.nombre_cliente || '',
            proyecto: cot.proyecto_nombre || '',
            valor,
            dias_gestion: dias,
          } });
        if (r.crearVentaMarketing?.success) success('Venta registrada en Marketing (canal propio)');
        else error(r.crearVentaMarketing?.message || 'No se pudo registrar la venta en Marketing');
      }
      setModalVenta({ visible: false, cot: null, tipo: 'nueva', loading: false });
    } catch (err) {
      error('Error al procesar la venta');
      setModalVenta(prev => ({ ...prev, loading: false }));
    }
  };

  const descargarCotizacionPDF = async (id, modo = 'enviar') => {
    try {
      setGenerandoPDF(true);
      // 1. Fetch cotizacion completa
      const gqlCotData = await client.query(`query($id: Int!) { cotizacion(id: $id) { id consecutivo tipo fecha estado nombre_cliente cedula_cliente telefono_cliente correo_cliente zona_barrio zona proyecto_nombre apartamento area_m2 direccion habitaciones banos area_banos numero_puertas metros_zocalo pregunta_agua_caliente pregunta_cambia_cocina pregunta_plafones_led pregunta_conserva_bano pregunta_cajones_drywall plano_id forma_cocina opciones_bano subtotal aiu_porcentaje aiu_valor iva_valor valor_final descuento valor_final_descuento incremento_global notas bano_social_alto bano_social_ancho bano_ppal_alto bano_ppal_ancho balcon altura_ducha avance_ffe proyecto_id plano_nombre plano_url actividades { id cotizacion_id actividad_id codigo descripcion capitulo categoria unidad cantidad valor_unitario valor_unitario_base valor_total espacio orden incremento factor_desperdicio factor_multiplicador activo_matriz cantidad_predeterminada bloqueada bloqueada_at } plan_pagos { id cotizacion_id descripcion porcentaje valor orden } } }`, { id });
      const data = { success: !!gqlCotData.cotizacion, cotizacion: gqlCotData.cotizacion };

      if (!data.success || !data.cotizacion) {
        error('No se pudo cargar la cotizacion para generar el PDF');
        return;
      }

      // 2. Setear datos para renderizar
      setCotizacionPDF(data.cotizacion);

      // 3. Esperar render + carga de imagenes
      await new Promise(resolve => setTimeout(resolve, 2000));

      const container = pdfContainerRef.current;
      if (!container) {
        error('Error al preparar el PDF');
        return;
      }

      // 4. Generar PDF con jsPDF + html2canvas
      const { jsPDF } = await import('jspdf');
      const html2canvasModule = await import('html2canvas');
      const html2canvas = html2canvasModule.default;

      const page = container.querySelector('.cotizacion-page');
      if (!page) {
        error('No se encontró contenido del PDF');
        return;
      }

      // Capturar toda la hoja continua
      const canvas = await html2canvas(page, {
        scale: 2,
        useCORS: true,
        logging: false,
        letterRendering: true,
        width: 612,
        windowWidth: 612
      });

      const pageWidth = 612;
      const cot = data.cotizacion;

      if (modo === 'imprimir') {
        // ===== PARA IMPRIMIR: paginado en carta sin cortar contenido =====
        // Carta: 612 x 792 pt. Cortamos solo en limites seguros (entre bloques/filas).
        const pageHpt = 792;
        const usableHpx = (pageHpt / pageWidth) * canvas.width; // alto max de canvas por pagina

        // Recopilar bloques atomicos: sus bordes inferiores son los unicos cortes permitidos
        const totalH = page.scrollHeight;
        const scaleY = canvas.height / totalH;
        const pageTop = page.getBoundingClientRect().top;
        const selectores = [
          '.pdf-banner', '.pdf-fecha-cotizacion', '.pdf-datos-wrapper',
          '.cotizacion-pdf-section',
          '.pdf-actividades-table thead tr', '.pdf-actividades-table tbody tr',
          '.pdf-financial-table tr',
          '.pdf-plan-pagos-wrapper', '.pdf-plan-pagos-table tr',
          '.pdf-observaciones', '.pdf-consideraciones',
          '.pdf-imagen-final'
        ];
        const cortes = [];
        selectores.forEach(sel => {
          page.querySelectorAll(sel).forEach(el => {
            const b = (el.getBoundingClientRect().bottom - pageTop) * scaleY;
            if (b > 1 && b < canvas.height - 1) cortes.push(b);
          });
        });
        cortes.push(canvas.height);
        const cortesOrdenados = [...new Set(cortes.map(c => Math.round(c)))].sort((a, b) => a - b);

        // Distribuir en paginas: cada pagina termina en el mayor corte que quepa
        const segmentos = [];
        let inicio = 0;
        let guard = 0;
        while (inicio < canvas.height - 1 && guard++ < 500) {
          const objetivo = inicio + usableHpx;
          if (objetivo >= canvas.height) { segmentos.push([inicio, canvas.height]); break; }
          let elegido = -1;
          for (const c of cortesOrdenados) {
            if (c > inicio + 1 && c <= objetivo) elegido = c;
            else if (c > objetivo) break;
          }
          if (elegido < 0) elegido = Math.floor(objetivo); // bloque mas alto que una pagina: corte forzado
          segmentos.push([inicio, elegido]);
          inicio = elegido;
        }

        const pdf = new jsPDF({ unit: 'pt', format: [pageWidth, pageHpt], orientation: 'portrait' });
        segmentos.forEach(([s, e], idx) => {
          const chunkH = e - s;
          if (chunkH <= 0) return;
          const crop = document.createElement('canvas');
          crop.width = canvas.width;
          crop.height = chunkH;
          crop.getContext('2d').drawImage(canvas, 0, s, canvas.width, chunkH, 0, 0, canvas.width, chunkH);
          const displayedH = (chunkH / canvas.width) * pageWidth;
          if (idx > 0) pdf.addPage([pageWidth, pageHpt], 'portrait');
          pdf.addImage(crop.toDataURL('image/jpeg', 0.98), 'JPEG', 0, 0, pageWidth, displayedH);
        });
        pdf.save(`COTIZACION-${cot.consecutivo}-${cot.nombre_cliente}-${cot.proyecto_nombre}-IMPRIMIR.pdf`);
        success('PDF para imprimir descargado');
      } else {
        // ===== PARA ENVIAR: una sola hoja continua =====
        const imgData = canvas.toDataURL('image/jpeg', 0.98);
        const pageHeight = (canvas.height * pageWidth) / canvas.width;

        const pdf = new jsPDF({
          unit: 'px',
          format: [pageWidth, pageHeight],
          orientation: 'portrait',
          hotfixes: ['px_scaling']
        });

        pdf.addImage(imgData, 'JPEG', 0, 0, pageWidth, pageHeight);
        pdf.save(`COTIZACION-${cot.consecutivo}-${cot.nombre_cliente}-${cot.proyecto_nombre}.pdf`);
        success('PDF descargado correctamente');
      }
    } catch (err) {
      console.error('Error PDF:', err);
      error('Error al generar el PDF');
    } finally {
      setGenerandoPDF(false);
      setCotizacionPDF(null);
    }
  };

  // ======= DESCARGAR EXCEL =======
  const descargarCotizacionExcel = async (id) => {
    try {
      setGenerandoExcel(true);
      const gqlExcel = await client.query(`query($id: Int!) { cotizacion(id: $id) { id consecutivo fecha nombre_cliente cedula_cliente telefono_cliente correo_cliente proyecto_nombre apartamento area_m2 direccion habitaciones banos subtotal aiu_porcentaje aiu_valor iva_valor valor_final descuento valor_final_descuento actividades { id codigo descripcion capitulo categoria unidad cantidad valor_unitario valor_total espacio incremento factor_desperdicio factor_multiplicador } plan_pagos { descripcion porcentaje valor } } }`, { id });
      const data = { success: !!gqlExcel.cotizacion, cotizacion: gqlExcel.cotizacion };
      if (!data.success || !data.cotizacion) {
        error('No se pudo cargar la cotización');
        return;
      }

      const XLSX = await import('xlsx');
      const cot = data.cotizacion;
      const acts = cot.actividades || [];
      const ORDEN_ESP = ['Generales', 'Cocina', 'Habitaciones', 'Estudio', 'Baños', 'Zona de Ropas', 'Balcón / Terraza', 'Finales'];

      // Agrupar actividades por espacio
      const grupos = {};
      acts.forEach(act => {
        const esp = act.espacio || 'Sin espacio';
        if (!grupos[esp]) grupos[esp] = [];
        grupos[esp].push(act);
      });
      const espaciosOrdenados = Object.keys(grupos).sort((a, b) => {
        const idxA = ORDEN_ESP.findIndex(e => e.toLowerCase() === a.toLowerCase());
        const idxB = ORDEN_ESP.findIndex(e => e.toLowerCase() === b.toLowerCase());
        return (idxA >= 0 ? idxA : ORDEN_ESP.length) - (idxB >= 0 ? idxB : ORDEN_ESP.length);
      });

      const fmtNum = (v) => {
        const n = parseFloat(v) || 0;
        return n;
      };

      const rows = [];
      // Título
      rows.push(['COTIZACIÓN']);
      rows.push([]);
      // La fecha llega como ISO completo; mostrar solo YYYY-MM-DD
      rows.push(['Fecha:', cot.fecha ? String(cot.fecha).slice(0, 10) : '']);
      rows.push([]);

      // Datos del propietario (con la posible fecha de entrega al frente)
      rows.push(['DATOS DEL PROPIETARIO']);
      rows.push(['Nombre:', cot.nombre_cliente || '', '', 'Posible Fecha de Entrega:', cot.fecha ? String(cot.fecha).slice(0, 10) : '']);
      rows.push(['Cédula:', cot.cedula_cliente || '']);
      rows.push(['Teléfono:', cot.telefono_cliente || '']);
      rows.push(['Correo:', cot.correo_cliente || '']);
      rows.push([]);

      // Datos del inmueble
      rows.push(['DATOS DEL INMUEBLE']);
      rows.push(['Proyecto:', cot.proyecto_nombre || '']);
      rows.push(['Área (m²):', fmtNum(cot.area_m2), '', 'Habitaciones:', fmtNum(cot.habitaciones)]);
      rows.push(['Baños:', fmtNum(cot.banos), '', 'Apartamento:', cot.apartamento || '']);
      rows.push(['Dirección:', cot.direccion || '']);
      rows.push([]);

      // Header de actividades (columna G = ESPACIO, repetido en cada actividad)
      rows.push(['Item', 'Descripción', 'Unidad', 'Cantidad', 'V. Unitario', 'V. Total', 'ESPACIO']);

      let itemGlobal = 0;
      espaciosOrdenados.forEach(espacio => {
        const actsEsp = grupos[espacio] || [];
        const totalEspacio = actsEsp.reduce((sum, a) => sum + (parseFloat(a.valor_total) || 0), 0);

        // Header del espacio
        rows.push([espacio, '', '', '', '', totalEspacio, '']);

        actsEsp.forEach(act => {
          itemGlobal++;
          rows.push([
            itemGlobal,
            act.descripcion || '',
            act.unidad || '',
            fmtNum(act.cantidad),
            fmtNum(act.valor_unitario),
            fmtNum(act.valor_total),
            String(espacio).toUpperCase()
          ]);
        });
      });

      rows.push([]);

      // Resumen financiero
      const subtotal = fmtNum(cot.subtotal);
      const aiuPorcentaje = fmtNum(cot.aiu_porcentaje);
      const aiuValor = fmtNum(cot.aiu_valor);
      const ivaValor = fmtNum(cot.iva_valor);
      const descuento = fmtNum(cot.descuento);
      const valorFinal = descuento > 0 ? fmtNum(cot.valor_final_descuento) : fmtNum(cot.valor_final);

      rows.push(['', '', '', '', 'SUBTOTAL', subtotal]);
      rows.push(['', '', '', '', `AIU (${aiuPorcentaje}%)`, aiuValor]);
      rows.push(['', '', '', '', 'IVA', ivaValor]);
      if (descuento > 0) {
        rows.push(['', '', '', '', 'Descuento', -descuento]);
      }
      rows.push(['', '', '', '', 'VALOR FINAL', valorFinal]);

      // Plan de pagos
      if (cot.plan_pagos && cot.plan_pagos.length > 0) {
        rows.push([]);
        rows.push(['PLAN DE PAGOS']);
        cot.plan_pagos.forEach(pago => {
          const valorPago = pago.valor || (valorFinal * (parseFloat(pago.porcentaje) || 0) / 100);
          rows.push([`${pago.descripcion} - ${pago.porcentaje}%`, '', '', '', '', valorPago]);
        });
      }

      // Crear workbook
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.aoa_to_sheet(rows);

      // Anchos de columna
      ws['!cols'] = [
        { wch: 8 },   // Item
        { wch: 55 },  // Descripción
        { wch: 8 },   // Unidad
        { wch: 10 },  // Cantidad
        { wch: 18 },  // V. Unitario
        { wch: 18 },  // V. Total
        { wch: 18 }   // ESPACIO
      ];

      XLSX.utils.book_append_sheet(wb, ws, 'Cotización');
      XLSX.writeFile(wb, `COTIZACION-${cot.consecutivo}-${cot.nombre_cliente}-${cot.proyecto_nombre}.xlsx`);
      success('Excel descargado correctamente');
    } catch (err) {
      console.error('Error Excel:', err);
      error('Error al generar el Excel');
    } finally {
      setGenerandoExcel(false);
    }
  };

  const getPlanoSeleccionado = () => {
    return planos.find(p => String(p.id) === String(formData.plano_id));
  };

  // Orden definido de espacios para agrupar actividades
  const ORDEN_ESPACIOS = ['Generales', 'Cocina', 'Habitaciones', 'Estudio', 'Baños', 'Zona de Ropas', 'Balcón / Terraza', 'Finales'];

  // Agrupar actividades seleccionadas por espacio y ordenar
  // Cada actividad conserva su índice global original para editar/eliminar correctamente
  const actividadesPorEspacio = (() => {
    const grupos = actividadesSeleccionadas.reduce((acc, act, globalIdx) => {
      const espacio = act.espacio || 'Sin espacio';
      if (!acc[espacio]) {
        acc[espacio] = [];
      }
      acc[espacio].push({ ...act, _globalIndex: globalIdx });
      return acc;
    }, {});

    // Ordenar por el orden definido
    return Object.entries(grupos).sort(([a], [b]) => {
      const idxA = ORDEN_ESPACIOS.findIndex(e => e.toLowerCase() === a.toLowerCase());
      const idxB = ORDEN_ESPACIOS.findIndex(e => e.toLowerCase() === b.toLowerCase());
      const orderA = idxA >= 0 ? idxA : ORDEN_ESPACIOS.length;
      const orderB = idxB >= 0 ? idxB : ORDEN_ESPACIOS.length;
      return orderA - orderB;
    });
  })();

  // Parser de Excel viejo (cotizaciones anteriores) - Muestra preview antes de guardar
  const parsearExcelViejo = async (file) => {
    try {
      const XLSX = await import('xlsx');
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data, { type: 'array' });

      if (workbook.SheetNames.length < 2) {
        error('El archivo debe tener al menos 2 hojas (Cotización y Actividades)');
        return;
      }

      // Hoja 1: Datos del cliente e inmueble
      const hoja1 = workbook.Sheets[workbook.SheetNames[0]];
      const getCelda = (ref) => {
        const cell = hoja1[ref];
        return cell ? (cell.v !== undefined ? String(cell.v).trim() : '') : '';
      };

      const clienteNombre = getCelda('C9') || getCelda('D9');
      const clienteTelefono = getCelda('C11') || getCelda('D11');
      const clienteCorreo = getCelda('C12') || getCelda('D12');
      const proyectoNombre = getCelda('F9') || getCelda('E9');
      const areaM2 = getCelda('F11') || getCelda('E11');
      const habitaciones = getCelda('H11') || getCelda('G11');
      const banos = getCelda('F12') || getCelda('E12');
      const apartamento = getCelda('H12') || getCelda('G12');

      // Hoja 2: Actividades detalladas
      const hoja2 = workbook.Sheets[workbook.SheetNames[1]];
      const actividadesData = XLSX.utils.sheet_to_json(hoja2, { defval: '' });

      const actividades = actividadesData
        .filter(row => row['Codigo'] || row['codigo'] || Object.values(row)[0])
        .map(row => {
          const codigo = row['Codigo'] || row['codigo'] || Object.values(row)[0] || '';
          const descripcion = row['Actividad'] || row['actividad'] || Object.values(row)[1] || '';
          const categoria = row['Categoria'] || row['categoria'] || Object.values(row)[2] || '';
          const unidad = row['Unidad'] || row['unidad'] || Object.values(row)[3] || '';
          const cantidad = parseFloat(row['Cantidad'] || row['cantidad'] || Object.values(row)[4]) || 1;
          const valorUnitario = parseFloat(row['Valor Unitario + Incremento'] || row['valor_unitario'] || Object.values(row)[5]) || 0;
          const incremento = parseFloat(row['Incremento Actividades (%)'] || row['incremento'] || Object.values(row)[6]) || 0;
          const desperdicio = parseFloat(row['Factor de Desperdicio'] || row['factor_desperdicio'] || Object.values(row)[7]) || 1;
          const multiplicador = parseFloat(row['Factor Multiplicador'] || row['factor_multiplicador'] || Object.values(row)[8]) || 1;
          const valorTotal = parseFloat(row['Valor Total'] || row['valor_total'] || Object.values(row)[9]) || 0;

          return {
            codigo,
            descripcion,
            categoria,
            unidad,
            cantidad,
            valor_unitario: valorUnitario,
            incremento,
            factor_desperdicio: desperdicio,
            factor_multiplicador: multiplicador,
            valor_total: valorTotal || Math.floor(valorUnitario * (1 + incremento / 100) * desperdicio * multiplicador) * cantidad,
            ficha_ffe: 'NO' // Se llenará con la búsqueda en matriz
          };
        })
        .filter(act => act.codigo && act.descripcion);

      if (actividades.length === 0) {
        error('No se encontraron actividades en la hoja 2');
        return;
      }

      // Buscar FFE en matriz_actividades para cada código
      const codigos = [...new Set(actividades.map(a => a.codigo))];
      try {
        // Buscar FFE por código en la matriz
        for (const codigo of codigos) {
          try {
            const ffeData = await client.query(`query($buscar: String) { matrizActividadesBuscar(buscar: $buscar) { id codigo } }`, { buscar: codigo });
            // Si el código existe en la matriz, marcar como SI (simplificado)
            const found = (ffeData.matrizActividadesBuscar || []).find(a => a.codigo === codigo);
            if (found) {
              actividades.filter(a => a.codigo === codigo).forEach(a => { a.ficha_ffe = 'SI'; });
            }
          } catch(e) {}
        }
      } catch (ffeErr) {
        console.warn('No se pudo consultar FFE:', ffeErr);
      }

      // Calcular totales
      const subtotal = actividades.reduce((sum, act) => sum + (parseFloat(act.valor_total) || 0), 0);
      const aiu_valor = subtotal * 0.15;
      const iva_valor = subtotal * 0.19 * 0.05;
      const valor_final = subtotal + aiu_valor + iva_valor;

      // Mostrar preview en vez de guardar directamente
      setPreviewVieja({
        cliente: {
          nombre_cliente: clienteNombre || '',
          telefono_cliente: clienteTelefono || '',
          correo_cliente: clienteCorreo || '',
          proyecto_nombre: proyectoNombre || '',
          apartamento: apartamento || '',
          area_m2: areaM2 || null,
          habitaciones: habitaciones || null,
          banos: banos || null
        },
        actividades,
        totales: { subtotal, aiu_valor, iva_valor, valor_final }
      });

    } catch (err) {
      console.error('Error parseando Excel:', err);
      error('Error al leer el archivo Excel');
    }
  };

  // Guardar cotización vieja desde preview (nueva o actualización)
  const guardarCotizacionVieja = async () => {
    if (!previewVieja) return;
    try {
      setGuardandoVieja(true);
      const { cliente, actividades, totales: tot } = previewVieja;

      const payload = {
        fecha: getFechaColombia(),
        nombre_cliente: cliente.nombre_cliente,
        telefono_cliente: cliente.telefono_cliente,
        correo_cliente: cliente.correo_cliente,
        proyecto_nombre: cliente.proyecto_nombre,
        apartamento: cliente.apartamento,
        area_m2: cliente.area_m2,
        habitaciones: cliente.habitaciones,
        banos: cliente.banos,
        subtotal: tot.subtotal,
        aiu_porcentaje: 15,
        aiu_valor: tot.aiu_valor,
        iva_valor: tot.iva_valor,
        valor_final: tot.valor_final,
        actividades
      };

      const viejaInput = {
        consecutivo: payload.consecutivo, fecha: payload.fecha,
        nombre_cliente: payload.nombre_cliente, telefono_cliente: payload.telefono_cliente,
        correo_cliente: payload.correo_cliente, zona_barrio: payload.zona_barrio, zona: payload.zona || null,
        proyecto_nombre: payload.proyecto_nombre,
        apartamento: payload.apartamento, area_m2: payload.area_m2 ? parseFloat(payload.area_m2) : null,
        habitaciones: payload.habitaciones ? parseInt(payload.habitaciones) : null,
        banos: payload.banos ? parseInt(payload.banos) : null,
        subtotal: payload.subtotal, aiu_porcentaje: payload.aiu_porcentaje || 15,
        aiu_valor: payload.aiu_valor, iva_valor: payload.iva_valor, valor_final: payload.valor_final,
        estado: payload.estado || 'importada',
        actividades: (payload.actividades || []).map((a, i) => ({
          codigo: a.codigo || '', descripcion: a.descripcion || '', categoria: a.categoria || '',
          unidad: a.unidad || '', cantidad: parseFloat(a.cantidad) || 1,
          valor_unitario: parseFloat(a.valor_unitario) || 0, incremento: parseFloat(a.incremento) || 0,
          factor_desperdicio: parseFloat(a.factor_desperdicio) || 1, factor_multiplicador: parseFloat(a.factor_multiplicador) || 1,
          // Sin "bloqueada": el bloqueo de actividades solo existe en cotizaciones nuevas
          valor_total: parseFloat(a.valor_total) || 0, ficha_ffe: a.ficha_ffe || 'NO', orden: i,
        })),
      };

      let result;
      if (actualizandoViejaId) {
        const r = await client.mutate(`mutation($id: Int!, $input: CotizacionViejaSaveInput!) { actualizarCotizacionVieja(id: $id, input: $input) { success message id consecutivo } }`, { id: actualizandoViejaId, input: viejaInput });
        result = r.actualizarCotizacionVieja;
      } else {
        const r = await client.mutate(`mutation($input: CotizacionViejaSaveInput!) { crearCotizacionVieja(input: $input) { success message id consecutivo } }`, { input: viejaInput });
        result = r.crearCotizacionVieja;
      }

      if (result.success) {
        success(actualizandoViejaId
          ? `Cotización #${result.consecutivo} actualizada: ${actividades.length} actividades`
          : `Cotización vieja #${result.consecutivo} importada: ${actividades.length} actividades`
        );
        setPreviewVieja(null);
        setActualizandoViejaId(null);
        cargarCotizacionesViejas();
      } else {
        error(result.message || 'Error al guardar cotización importada');
      }
    } catch (err) {
      console.error('Error guardando cotización vieja:', err);
      error('Error al guardar cotización importada');
    } finally {
      setGuardandoVieja(false);
    }
  };

  // Manejar re-subida de Excel para actualizar cotización vieja existente
  const handleUpdateExcel = (cotId, e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.name.match(/\.(xlsx|xls)$/i)) {
      error('Solo se permiten archivos Excel (.xlsx, .xls)');
      return;
    }
    setActualizandoViejaId(cotId);
    parsearExcelViejo(file);
    e.target.value = '';
  };

  const descargarCotizacionViejaPDF = async (id) => {
    try {
      setGenerandoPDFVieja(true);
      const gqlViejaR = await client.query(`query($id: Int!) { cotizacionVieja(id: $id) { id consecutivo fecha nombre_cliente telefono_cliente correo_cliente proyecto_nombre apartamento area_m2 habitaciones banos subtotal aiu_porcentaje aiu_valor iva_valor valor_final estado avance_ffe proyecto_id actividades { id cotizacion_id codigo descripcion categoria unidad cantidad valor_unitario incremento factor_desperdicio factor_multiplicador valor_total ficha_ffe orden activo_matriz } } }`, { id });
      const data = { success: !!gqlViejaR.cotizacionVieja, cotizacion: gqlViejaR.cotizacionVieja };

      if (!data.success || !data.cotizacion) {
        error('No se pudo cargar la cotización para generar el PDF');
        return;
      }

      setCotizacionViejaPDF(data.cotizacion);
      await new Promise(resolve => setTimeout(resolve, 2000));

      const container = pdfViejaContainerRef.current;
      if (!container) {
        error('Error al preparar el PDF');
        return;
      }

      const page = container.querySelector('.cotizacion-page');
      if (!page) {
        error('No se encontró contenido del PDF');
        return;
      }

      const { jsPDF } = await import('jspdf');
      const html2canvasModule = await import('html2canvas');
      const html2canvas = html2canvasModule.default;

      const canvas = await html2canvas(page, {
        scale: 2,
        useCORS: true,
        logging: false,
        letterRendering: true,
        width: 612,
        windowWidth: 612
      });

      const imgData = canvas.toDataURL('image/jpeg', 0.98);
      const pageWidth = 612;
      const pageHeight = (canvas.height * pageWidth) / canvas.width;

      const pdf = new jsPDF({
        unit: 'px',
        format: [pageWidth, pageHeight],
        orientation: 'portrait',
        hotfixes: ['px_scaling']
      });

      pdf.addImage(imgData, 'JPEG', 0, 0, pageWidth, pageHeight);

      const cot = data.cotizacion;
      pdf.save(`COTIZACION-VIEJA-${cot.consecutivo}-${cot.nombre_cliente}-${cot.proyecto_nombre}.pdf`);
      success('PDF descargado correctamente');
    } catch (err) {
      console.error('Error PDF vieja:', err);
      error('Error al generar el PDF');
    } finally {
      setGenerandoPDFVieja(false);
      setCotizacionViejaPDF(null);
    }
  };

  // Ver detalle de cotización vieja
  const verDetalleVieja = async (id) => {
    setLoadingDetalleVieja(true);
    try {
      const gqlViejaR = await client.query(`query($id: Int!) { cotizacionVieja(id: $id) { id consecutivo fecha nombre_cliente telefono_cliente correo_cliente proyecto_nombre apartamento area_m2 habitaciones banos subtotal aiu_porcentaje aiu_valor iva_valor valor_final estado avance_ffe proyecto_id actividades { id cotizacion_id codigo descripcion categoria unidad cantidad valor_unitario incremento factor_desperdicio factor_multiplicador valor_total ficha_ffe orden activo_matriz } } }`, { id });
      const data = { success: !!gqlViejaR.cotizacionVieja, cotizacion: gqlViejaR.cotizacionVieja };
      if (data.success && data.cotizacion) {
        setDetalleVieja(data.cotizacion);
      } else {
        error('No se pudo cargar el detalle de la cotización');
      }
    } catch (err) {
      console.error('Error cargando detalle vieja:', err);
      error('Error de conexión al cargar detalle');
    } finally {
      setLoadingDetalleVieja(false);
    }
  };

  const handleExcelUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.name.match(/\.(xlsx|xls)$/i)) {
      error('Solo se permiten archivos Excel (.xlsx, .xls)');
      return;
    }
    parsearExcelViejo(file);
    e.target.value = '';
  };

  // Vista de lista
  if (vista === 'actualizar-bases') {
    return <ActualizarBases onVolver={() => { setVista('lista'); cargarCotizaciones(); }} />;
  }

  if (vista === 'lista') {
    return (
      <div className="cotizaciones-container">
        <div className="cotizaciones-header">
          <h2>Cotizaciones</h2>
          <div style={{display:'flex',gap:'8px'}}>
            <button
              className="btn-exportar-excel"
              style={{ background: '#fff', border: '1.5px solid #3b82f6', color: '#3b82f6' }}
              title="Identifica proyectos sin cotización asociada (extras) y duplicados"
              onClick={async () => {
                try {
                  const r = await client.query(`{ analizarProyectosVsCotizaciones }`);
                  const data = r.analizarProyectosVsCotizaciones;
                  console.log('=== ANÁLISIS PROYECTOS vs COTIZACIONES ===');
                  console.log(JSON.stringify(data, null, 2));

                  // Abrir en ventana nueva con formato legible
                  const w = window.open('', '_blank', 'width=1100,height=800');
                  let html = `<html><head><title>Análisis Proyectos vs Cotizaciones</title>
                    <style>
                      body { font-family: -apple-system, sans-serif; padding: 20px; background: #f8fafc; }
                      h2 { color: #1e293b; border-bottom: 2px solid #e2e8f0; padding-bottom: 8px; margin-top: 24px; }
                      .resumen { background: #fff; padding: 16px; border-radius: 8px; box-shadow: 0 1px 3px rgba(0,0,0,0.1); }
                      .resumen p { margin: 4px 0; }
                      table { width: 100%; border-collapse: collapse; background: #fff; border-radius: 8px; overflow: hidden; box-shadow: 0 1px 3px rgba(0,0,0,0.1); }
                      th { background: #1e293b; color: #fff; padding: 10px; text-align: left; font-size: 0.85rem; }
                      td { padding: 10px; border-bottom: 1px solid #f1f5f9; font-size: 0.85rem; }
                      tr:hover { background: #f8fafc; }
                      .vendido { color: #22c55e; font-weight: 600; }
                      .contratado_no_iniciado { color: #f59e0b; font-weight: 600; }
                      .grupo-dup { background: #fef2f2; }
                      .grupo-dup td { border-bottom: 1px solid #fecaca; }
                      .cotizaciones { color: #6b7280; font-size: 0.78rem; }
                      .empty { color: #94a3b8; padding: 20px; text-align: center; font-style: italic; }
                    </style>
                    </head><body>
                    <h1>Análisis Proyectos vs Cotizaciones</h1>
                    <div class="resumen">
                      <p><strong>Total proyectos vendido/contratado:</strong> ${data.resumen.total_proyectos}</p>
                      <p><strong>Vendido:</strong> ${data.resumen.proyectos_vendido} | <strong>Contratado:</strong> ${data.resumen.proyectos_contratado}</p>
                      <p><strong>Huérfanos (sin cotización):</strong> ${data.resumen.huerfanos_count}</p>
                      <p><strong>Duplicados (mismo nombre+apto):</strong> ${data.resumen.duplicados_count} grupos</p>
                      <p><strong>Cotizaciones sin proyecto:</strong> ${data.resumen.cotizaciones_sin_proyecto_count}</p>
                    </div>`;

                  html += `<h2>Huérfanos (${data.huerfanos.length})</h2>`;
                  if (data.huerfanos.length === 0) {
                    html += '<div class="empty">No hay huérfanos</div>';
                  } else {
                    html += `<table><thead><tr><th>ID</th><th>Estado</th><th>Proyecto</th><th>Apto</th><th>Cliente</th><th>Valor</th></tr></thead><tbody>`;
                    data.huerfanos.forEach(p => {
                      html += `<tr><td>${p.id}</td><td class="${p.estado}">${p.estado}</td><td>${p.proyecto_nombre || '-'}</td><td>${p.apartamento || '-'}</td><td>${p.nombre_cliente || '-'}</td><td>$${(p.valor_final||0).toLocaleString()}</td></tr>`;
                    });
                    html += '</tbody></table>';
                  }

                  html += `<h2>Duplicados (${data.duplicados.length} grupos)</h2>`;
                  if (data.duplicados.length === 0) {
                    html += '<div class="empty">No hay duplicados</div>';
                  } else {
                    html += `<table><thead><tr><th>Grupo</th><th>ID</th><th>Estado</th><th>Proyecto</th><th>Apto</th><th>Cliente</th><th>Valor</th><th>Cotizaciones</th></tr></thead><tbody>`;
                    data.duplicados.forEach((grupo, idx) => {
                      grupo.forEach((p, i) => {
                        html += `<tr class="grupo-dup">${i === 0 ? `<td rowspan="${grupo.length}"><strong>${idx+1}</strong></td>` : ''}<td>${p.id}</td><td class="${p.estado}">${p.estado}</td><td>${p.proyecto_nombre || '-'}</td><td>${p.apartamento || '-'}</td><td>${p.nombre_cliente || '-'}</td><td>$${(p.valor_final||0).toLocaleString()}</td><td class="cotizaciones">${(p.cotizaciones||[]).map(c => `#${c.consecutivo} (${c.estado})`).join(', ') || 'NINGUNA'}</td></tr>`;
                      });
                    });
                    html += '</tbody></table>';
                  }

                  html += `<h2>Cotizaciones sin proyecto (${data.cotizaciones_sin_proyecto.length})</h2>`;
                  if (data.cotizaciones_sin_proyecto.length === 0) {
                    html += '<div class="empty">Todas las cotizaciones vendido/contratado tienen proyecto</div>';
                  } else {
                    html += `<table><thead><tr><th>Cot #</th><th>Estado</th><th>Proyecto</th><th>Apto</th><th>Cliente</th></tr></thead><tbody>`;
                    data.cotizaciones_sin_proyecto.forEach(c => {
                      html += `<tr><td>${c.consecutivo}</td><td>${c.estado}</td><td>${c.proyecto_nombre || '-'}</td><td>${c.apartamento || '-'}</td><td>${c.nombre_cliente || '-'}</td></tr>`;
                    });
                    html += '</tbody></table>';
                  }

                  html += '</body></html>';
                  w.document.write(html);
                  w.document.close();
                } catch (e) { alert('Error: ' + e.message); }
              }}
            >
              🔍 Analizar
            </button>
            <button
              className="btn-exportar-excel"
              style={{ background: '#fff', border: '1.5px solid #f59e0b', color: '#f59e0b' }}
              title="Crea proyectos faltantes para cotizaciones marcadas como vendido/contratado"
              onClick={async () => {
                if (!window.confirm('Esto creará en Lista de Proyectos y Seguimiento Comercial todas las cotizaciones marcadas como vendido/contratado que aún no tengan proyecto asociado. ¿Continuar?')) return;
                try {
                  const r = await client.mutate(`mutation { repararProyectosDeCotizaciones { success message } }`);
                  alert(r.repararProyectosDeCotizaciones.message);
                  cargarCotizaciones();
                } catch { alert('Error al reparar'); }
              }}
            >
              🔧 Reparar Proyectos
            </button>
            <button
              className="btn-exportar-excel"
              style={{ background: '#fff', border: '1.5px solid #6366f1', color: '#6366f1' }}
              title="Actualizar los precios de las cotizaciones base con la matriz de actividades"
              onClick={() => setVista('actualizar-bases')}
            >
              🔄 Actualizar Bases
            </button>
            <button
              className="btn-exportar-excel"
              style={{ background: '#fff', border: '1.5px solid #0891b2', color: '#0891b2' }}
              title="Administrar las zonas del formulario de cotización"
              onClick={abrirModalZonas}
            >
              📍 Zonas
            </button>
            <button className="btn-exportar-excel" onClick={exportarExcelCotizaciones} title="Exportar a Excel">
              📊 Exportar Excel
            </button>
            <button className="btn-primary" onClick={nuevaCotizacion}>
              + Nueva Cotización
            </button>
          </div>
        </div>

        {/* Incremento con el que nacen las cotizaciones nuevas (solo con permiso) */}
        {puedeEditarIncrementoDefecto && (
          <div className="cot-inc-defecto">
            <span className="cot-inc-defecto-label">
              Incremento de actividades por defecto para cotizaciones nuevas y copias de una Base:
            </span>
            {incDefectoEditando === null ? (
              <>
                <strong className="cot-inc-defecto-valor">{incrementoDefecto} %</strong>
                <button className="cot-inc-defecto-btn" onClick={() => setIncDefectoEditando(String(incrementoDefecto))}>
                  Cambiar
                </button>
              </>
            ) : (
              <>
                <input
                  type="number" min="0" max="100" step="0.5"
                  className="cot-inc-defecto-input"
                  value={incDefectoEditando}
                  onChange={(e) => setIncDefectoEditando(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') guardarIncrementoDefecto(); if (e.key === 'Escape') setIncDefectoEditando(null); }}
                  autoFocus
                /> %
                <button className="cot-inc-defecto-btn guardar" onClick={guardarIncrementoDefecto} disabled={guardandoIncDefecto}>
                  {guardandoIncDefecto ? 'Guardando...' : 'Guardar'}
                </button>
                <button className="cot-inc-defecto-btn" onClick={() => setIncDefectoEditando(null)} disabled={guardandoIncDefecto}>
                  Cancelar
                </button>
              </>
            )}
            <span className="cot-inc-defecto-nota">Las cotizaciones que ya existen conservan el suyo.</span>
          </div>
        )}

        {/* Pestañas */}
        <div className="cotizaciones-tabs">
          <button
            className={`tab-btn ${tabLista === 'nuevas' ? 'active' : ''}`}
            onClick={() => setTabLista('nuevas')}
          >
            Cotizaciones
          </button>
          <button
            className={`tab-btn ${tabLista === 'viejas' ? 'active' : ''}`}
            onClick={() => setTabLista('viejas')}
          >
            Cotizaciones Viejas
          </button>
          <button
            className={`tab-btn ${tabLista === 'web' ? 'active' : ''}`}
            onClick={() => setTabLista('web')}
          >
            Cotizador Web
          </button>
          <button
            className={`tab-btn ${tabLista === 'especiales' ? 'active' : ''}`}
            onClick={() => setTabLista('especiales')}
          >
            Cotizaciones Especiales
          </button>
        </div>

        {tabLista === 'viejas' ? (
          <div className="cotizaciones-viejas">
            {/* Detalle de cotización vieja */}
            {detalleVieja ? (
              <div className="preview-vieja">
                <div className="preview-header">
                  <h3>Detalle Cotización #{detalleVieja.consecutivo}</h3>
                  <div className="preview-actions">
                    <button className="btn-secondary" onClick={() => setDetalleVieja(null)}>← Volver</button>
                    <button
                      className="btn-primary"
                      onClick={() => descargarCotizacionViejaPDF(detalleVieja.id)}
                      disabled={generandoPDFVieja}
                    >
                      {generandoPDFVieja ? 'Generando...' : '📥 Descargar PDF'}
                    </button>
                  </div>
                </div>

                <div className="preview-datos-cliente">
                  <h4>Datos del Cliente / Inmueble</h4>
                  <div className="preview-grid">
                    <div><strong>Cliente:</strong> {detalleVieja.nombre_cliente || '-'}</div>
                    <div><strong>Proyecto:</strong> {detalleVieja.proyecto_nombre || '-'}</div>
                    <div><strong>Teléfono:</strong> {detalleVieja.telefono_cliente || '-'}</div>
                    <div><strong>Correo:</strong> {detalleVieja.correo_cliente || '-'}</div>
                    <div><strong>Apartamento:</strong> {detalleVieja.apartamento || '-'}</div>
                    <div><strong>Área (m²):</strong> {detalleVieja.area_m2 || '-'}</div>
                    <div><strong>Habitaciones:</strong> {detalleVieja.habitaciones || '-'}</div>
                    <div><strong>Baños:</strong> {detalleVieja.banos || '-'}</div>
                    <div><strong>Fecha:</strong> {detalleVieja.fecha || '-'}</div>
                    <div><strong>Estado:</strong> <span className={`estado-badge estado-${detalleVieja.estado || 'importada'}`}>{estadoLabel(detalleVieja.estado)}</span></div>
                  </div>
                </div>

                <div className="preview-actividades">
                  <h4>
                    Actividades ({(detalleVieja.actividades || []).length})
                    {' '}<span className="preview-ffe-count">
                      FFE: {(detalleVieja.actividades || []).filter(a => a.ficha_ffe === 'SI').length}
                    </span>
                  </h4>
                  <div className="preview-tabla-wrap">
                    <table className="preview-tabla">
                      <thead>
                        <tr>
                          <th>Código</th>
                          <th>Actividad</th>
                          <th>Categoría</th>
                          <th>Und</th>
                          <th>Cant</th>
                          <th>V. Unit</th>
                          <th>V. Total</th>
                          <th>FFE</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(detalleVieja.actividades || []).map((act, idx) => (
                          <tr key={idx} className={act.ficha_ffe === 'SI' ? 'row-ffe-si' : ''}>
                            <td>{act.codigo}</td>
                            <td>{act.descripcion}</td>
                            <td>{act.categoria}</td>
                            <td>{act.unidad}</td>
                            <td>{act.cantidad}</td>
                            <td className="text-right">{formatCurrency(act.valor_unitario)}</td>
                            <td className="text-right">{formatCurrency(act.valor_total)}</td>
                            <td>
                              <span className={`ffe-badge ${act.ficha_ffe === 'SI' ? 'ffe-si' : 'ffe-no'}`}>
                                {act.ficha_ffe}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="preview-totales">
                  <div className="preview-total-row"><span>Subtotal:</span><span>{formatCurrency(detalleVieja.subtotal)}</span></div>
                  <div className="preview-total-row"><span>AIU ({detalleVieja.aiu_porcentaje || 15}%):</span><span>{formatCurrency(detalleVieja.aiu_valor)}</span></div>
                  <div className="preview-total-row"><span>IVA:</span><span>{formatCurrency(detalleVieja.iva_valor)}</span></div>
                  <div className="preview-total-row preview-total-final"><span>VALOR FINAL:</span><span>{formatCurrency(detalleVieja.valor_final)}</span></div>
                </div>
              </div>
            ) : loadingDetalleVieja ? (
              <div className="loading">Cargando detalle...</div>
            ) : previewVieja ? (
              <div className="preview-vieja">
                <div className="preview-header">
                  <h3>{actualizandoViejaId ? 'Actualizar Cotización' : 'Vista Previa - Cotización Importada'}</h3>
                  <div className="preview-actions">
                    <button className="btn-secondary" onClick={() => { setPreviewVieja(null); setActualizandoViejaId(null); }}>Cancelar</button>
                    <button className="btn-primary" onClick={guardarCotizacionVieja} disabled={guardandoVieja}>
                      {guardandoVieja ? 'Guardando...' : (actualizandoViejaId ? 'Confirmar Actualización' : 'Confirmar e Importar')}
                    </button>
                  </div>
                </div>

                <div className="preview-datos-cliente">
                  <h4>Datos del Cliente / Inmueble</h4>
                  <div className="preview-grid">
                    <div><strong>Cliente:</strong> {previewVieja.cliente.nombre_cliente || '-'}</div>
                    <div><strong>Proyecto:</strong> {previewVieja.cliente.proyecto_nombre || '-'}</div>
                    <div><strong>Teléfono:</strong> {previewVieja.cliente.telefono_cliente || '-'}</div>
                    <div><strong>Correo:</strong> {previewVieja.cliente.correo_cliente || '-'}</div>
                    <div><strong>Apartamento:</strong> {previewVieja.cliente.apartamento || '-'}</div>
                    <div><strong>Área (m²):</strong> {previewVieja.cliente.area_m2 || '-'}</div>
                    <div><strong>Habitaciones:</strong> {previewVieja.cliente.habitaciones || '-'}</div>
                    <div><strong>Baños:</strong> {previewVieja.cliente.banos || '-'}</div>
                  </div>
                </div>

                <div className="preview-actividades">
                  <h4>
                    Actividades ({previewVieja.actividades.length})
                    {' '}<span className="preview-ffe-count">
                      FFE: {previewVieja.actividades.filter(a => a.ficha_ffe === 'SI').length}
                    </span>
                  </h4>
                  <div className="preview-tabla-wrap">
                    <table className="preview-tabla">
                      <thead>
                        <tr>
                          <th>Código</th>
                          <th>Actividad</th>
                          <th>Categoría</th>
                          <th>Und</th>
                          <th>Cant</th>
                          <th>V. Unit</th>
                          <th>V. Total</th>
                          <th>FFE</th>
                        </tr>
                      </thead>
                      <tbody>
                        {previewVieja.actividades.map((act, idx) => (
                          <tr key={idx} className={act.ficha_ffe === 'SI' ? 'row-ffe-si' : ''}>
                            <td>{act.codigo}</td>
                            <td>{act.descripcion}</td>
                            <td>{act.categoria}</td>
                            <td>{act.unidad}</td>
                            <td>{act.cantidad}</td>
                            <td className="text-right">{formatCurrency(act.valor_unitario)}</td>
                            <td className="text-right">{formatCurrency(act.valor_total)}</td>
                            <td>
                              <span className={`ffe-badge ${act.ficha_ffe === 'SI' ? 'ffe-si' : 'ffe-no'}`}>
                                {act.ficha_ffe}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="preview-totales">
                  <div className="preview-total-row"><span>Subtotal:</span><span>{formatCurrency(previewVieja.totales.subtotal)}</span></div>
                  <div className="preview-total-row"><span>AIU (15%):</span><span>{formatCurrency(previewVieja.totales.aiu_valor)}</span></div>
                  <div className="preview-total-row"><span>IVA:</span><span>{formatCurrency(previewVieja.totales.iva_valor)}</span></div>
                  <div className="preview-total-row preview-total-final"><span>VALOR FINAL:</span><span>{formatCurrency(previewVieja.totales.valor_final)}</span></div>
                </div>
              </div>
            ) : (
              <>
                <div className="viejas-upload-row">
                  <label className="btn-upload-excel">
                    + Importar Excel
                    <input
                      type="file"
                      accept=".xlsx,.xls"
                      onChange={handleExcelUpload}
                      style={{ display: 'none' }}
                    />
                  </label>
                  <input
                    type="text"
                    placeholder="Buscar por cliente o proyecto..."
                    value={buscarViejas}
                    onChange={(e) => setBuscarViejas(e.target.value)}
                    className="input-buscar-viejas"
                  />
                  <select
                    value={filtroViejasEstado}
                    onChange={(e) => { setFiltroViejasEstado(e.target.value); setPageViejas(1); }}
                    className="select-estado-viejas"
                  >
                    <option value="">Todos los estados</option>
                    <option value="importada">Importada</option>
                    <option value="vendido">Vendido</option>
                    <option value="contratado">Contratado</option>
                    <option value="base">Base</option>
                    <option value="base_cotizador_web">Base-Cotizador Web</option>
                  </select>
                </div>

                {loadingViejas ? (
                  <div className="loading">Cargando...</div>
                ) : cotizacionesViejas.length === 0 ? (
                  <div className="viejas-empty">
                    <div className="upload-icon">📄</div>
                    <h3>No hay cotizaciones viejas importadas</h3>
                    <p>Sube un archivo Excel con formato de cotización anterior (2 hojas: datos + actividades)</p>
                  </div>
                ) : (
                  <>
                    <div className="cotizaciones-tabla">
                      <table>
                        <thead>
                          <tr>
                            <th>#</th>
                            <th>Fecha</th>
                            <th>Cliente</th>
                            <th>Proyecto</th>
                            <th>Actividades</th>
                            <th>Valor Final</th>
                            <th>Estado</th>
                            <th>Acciones</th>
                          </tr>
                        </thead>
                        <tbody>
                          {cotizacionesViejas.map(cot => (
                            <tr key={cot.id}>
                              <td>{cot.consecutivo}</td>
                              <td>{cot.fecha}</td>
                              <td>{cot.nombre_cliente || '-'}</td>
                              <td>{cot.proyecto_nombre || '-'}</td>
                              <td>{cot.total_actividades || '-'}</td>
                              <td>{formatCurrency(cot.valor_final)}</td>
                              <td>
                                <span className={`estado-badge estado-${cot.estado || 'importada'}`}>
                                  {estadoLabel(cot.estado)}
                                </span>
                              </td>
                              <td>
                                <div className="acciones-btns">
                                  <button
                                    className="cot-btn-icon cot-btn-ver-detalle"
                                    title="Ver detalle"
                                    onClick={() => verDetalleVieja(cot.id)}
                                  >
                                    👁
                                  </button>
                                  <button
                                    className="cot-btn-icon cot-btn-descargar"
                                    title="Descargar PDF"
                                    onClick={() => descargarCotizacionViejaPDF(cot.id)}
                                    disabled={generandoPDFVieja}
                                  >
                                    {generandoPDFVieja ? '...' : '📥'}
                                  </button>
                                  <label
                                    className="cot-btn-icon cot-btn-actualizar-vieja"
                                    title="Actualizar desde Excel"
                                  >
                                    🔄
                                    <input
                                      type="file"
                                      accept=".xlsx,.xls"
                                      style={{ display: 'none' }}
                                      ref={el => { updateFileRefs.current[cot.id] = el; }}
                                      onChange={(e) => handleUpdateExcel(cot.id, e)}
                                    />
                                  </label>
                                  {cot.estado !== 'vendido' && cot.estado !== 'contratado' && (
                                    <button
                                      className="cot-btn-icon cot-btn-aprobar"
                                      title="Marcar como Vendido"
                                      onClick={() => iniciarVenta(cot, 'vieja')}
                                    >
                                      ✓
                                    </button>
                                  )}
                                  {cot.estado === 'vendido' && (
                                    <button
                                      className="cot-btn-icon cot-btn-firmar"
                                      title="Marcar como Contratado"
                                      onClick={() => cambiarEstadoVieja(cot.id, 'contratado')}
                                    >
                                      ✍
                                    </button>
                                  )}
                                  {cot.estado === 'contratado' && (
                                    <button
                                      className="cot-btn-icon cot-btn-regresar-estado"
                                      title="Regresar a Vendido"
                                      onClick={() => cambiarEstadoVieja(cot.id, 'vendido')}
                                    >
                                      ↩
                                    </button>
                                  )}
                                  <button
                                    className="cot-btn-icon cot-btn-base"
                                    title={['base', 'base_cotizador_web'].includes(cot.estado) ? 'Cambiar tipo de Base' : 'Marcar como Base'}
                                    onClick={() => setModalBase({ visible: true, cotId: cot.id, loading: false, tipo: 'vieja' })}
                                  >
                                    ⚑
                                  </button>
                                  <button
                                    className="cot-btn-icon cot-btn-duplicar"
                                    title="Duplicar como Cotización Nueva"
                                    onClick={() => duplicarViejaComoNueva(cot.id)}
                                  >
                                    ⧉
                                  </button>
                                  <button
                                    className="cot-btn-icon cot-btn-eliminar"
                                    title="Eliminar"
                                    onClick={() => eliminarCotizacionVieja(cot.id)}
                                  >
                                    🗑️
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {totalPagesViejas > 1 && (
                      <div className="paginacion">
                        <button disabled={pageViejas <= 1} onClick={() => setPageViejas(1)} title="Primera">«</button>
                        <button disabled={pageViejas <= 1} onClick={() => setPageViejas(p => p - 1)}>Anterior</button>
                        <span className="paginacion-info">Página</span>
                        <select className="paginacion-select" value={pageViejas} onChange={(e) => setPageViejas(parseInt(e.target.value))}>
                          {Array.from({ length: totalPagesViejas }, (_, i) => (
                            <option key={i + 1} value={i + 1}>{i + 1}</option>
                          ))}
                        </select>
                        <span className="paginacion-info">de {totalPagesViejas}</span>
                        <button disabled={pageViejas >= totalPagesViejas} onClick={() => setPageViejas(p => p + 1)}>Siguiente</button>
                        <button disabled={pageViejas >= totalPagesViejas} onClick={() => setPageViejas(totalPagesViejas)} title="Última">»</button>
                      </div>
                    )}
                  </>
                )}
              </>
            )}
          </div>
        ) : tabLista === 'web' ? (
        <>
        <div className="cotizaciones-filtros">
          <input
            type="text"
            placeholder="Buscar por cliente, proyecto o consecutivo..."
            value={buscarWeb}
            onChange={(e) => setBuscarWeb(e.target.value)}
          />
        </div>

        {loadingWeb ? (
          <div className="loading">Cargando...</div>
        ) : (
          <>
            <div className="cotizaciones-tabla">
              <table>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Fecha</th>
                    <th>Cliente</th>
                    <th>Proyecto</th>
                    <th>Plano</th>
                    <th>Valor Final</th>
                    <th>Estado</th>
                    <th>Acciones</th>
                    <th>Creado por</th>
                    <th>Última Modificación</th>
                  </tr>
                </thead>
                <tbody>
                  {cotizacionesWeb.length === 0 ? (
                    <tr>
                      <td colSpan="10" className="sin-datos">No hay cotizaciones del cotizador web</td>
                    </tr>
                  ) : (
                    cotizacionesWeb.map(renderFilaCotizacion)
                  )}
                </tbody>
              </table>
            </div>

            {totalPagesWeb > 1 && (
              <div className="paginacion">
                <button disabled={pageWeb <= 1} onClick={() => setPageWeb(1)} title="Primera página">«</button>
                <button disabled={pageWeb <= 1} onClick={() => setPageWeb(p => p - 1)}>Anterior</button>
                <span className="paginacion-info">Página</span>
                <select className="paginacion-select" value={pageWeb} onChange={(e) => setPageWeb(parseInt(e.target.value))}>
                  {Array.from({ length: totalPagesWeb }, (_, i) => (
                    <option key={i + 1} value={i + 1}>{i + 1}</option>
                  ))}
                </select>
                <span className="paginacion-info">de {totalPagesWeb}</span>
                <button disabled={pageWeb >= totalPagesWeb} onClick={() => setPageWeb(p => p + 1)}>Siguiente</button>
                <button disabled={pageWeb >= totalPagesWeb} onClick={() => setPageWeb(totalPagesWeb)} title="Última página">»</button>
              </div>
            )}
          </>
        )}
        </>
        ) : tabLista === 'especiales' ? (
        <>
        <div className="cotizaciones-filtros">
          <input
            type="text"
            placeholder="Buscar por cliente, proyecto o consecutivo..."
            value={buscarEsp}
            onChange={(e) => setBuscarEsp(e.target.value)}
          />
        </div>

        {loadingEsp ? (
          <div className="loading">Cargando...</div>
        ) : (
          <>
            <div className="cotizaciones-tabla">
              <table>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Fecha</th>
                    <th>Cliente</th>
                    <th>Proyecto</th>
                    <th>Plano</th>
                    <th>Valor Final</th>
                    <th>Estado</th>
                    <th>Acciones</th>
                    <th>Creado por</th>
                    <th>Última Modificación</th>
                  </tr>
                </thead>
                <tbody>
                  {cotizacionesEspeciales.length === 0 ? (
                    <tr>
                      <td colSpan="10" className="sin-datos">No hay cotizaciones especiales</td>
                    </tr>
                  ) : (
                    cotizacionesEspeciales.map(renderFilaCotizacion)
                  )}
                </tbody>
              </table>
            </div>

            {totalPagesEsp > 1 && (
              <div className="paginacion">
                <button disabled={pageEsp <= 1} onClick={() => setPageEsp(1)} title="Primera página">«</button>
                <button disabled={pageEsp <= 1} onClick={() => setPageEsp(p => p - 1)}>Anterior</button>
                <span className="paginacion-info">Página</span>
                <select className="paginacion-select" value={pageEsp} onChange={(e) => setPageEsp(parseInt(e.target.value))}>
                  {Array.from({ length: totalPagesEsp }, (_, i) => (
                    <option key={i + 1} value={i + 1}>{i + 1}</option>
                  ))}
                </select>
                <span className="paginacion-info">de {totalPagesEsp}</span>
                <button disabled={pageEsp >= totalPagesEsp} onClick={() => setPageEsp(p => p + 1)}>Siguiente</button>
                <button disabled={pageEsp >= totalPagesEsp} onClick={() => setPageEsp(totalPagesEsp)} title="Última página">»</button>
              </div>
            )}
          </>
        )}
        </>
        ) : (
        <>
        <div className="cotizaciones-filtros">
          <input
            type="text"
            placeholder="Buscar por cliente, proyecto o consecutivo..."
            value={buscarTexto}
            onChange={(e) => setBuscarTexto(e.target.value)}
          />
          <select
            value={filtros.estado}
            onChange={(e) => setFiltros(prev => ({ ...prev, estado: e.target.value }))}
          >
            <option value="">Todos los estados</option>
            <option value="borrador">Borrador</option>
            <option value="enviada">Enviada</option>
            <option value="vendido">Vendido</option>
            <option value="contratado">Contratado</option>
            <option value="rechazada">Rechazada</option>
            <option value="base">Base</option>
            <option value="base_cotizador_web">Base-Cotizador Web</option>
          </select>
        </div>

        {loading ? (
          <div className="loading">Cargando...</div>
        ) : (
          <>
            <div className="cotizaciones-tabla">
              <table>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Fecha</th>
                    <th>Cliente</th>
                    <th>Proyecto</th>
                    <th>Plano</th>
                    <th>Valor Final</th>
                    <th>Estado</th>
                    <th>Acciones</th>
                    <th>Creado por</th>
                    <th>Última Modificación</th>
                  </tr>
                </thead>
                <tbody>
                  {cotizaciones.length === 0 ? (
                    <tr>
                      <td colSpan="10" className="sin-datos">No hay cotizaciones registradas</td>
                    </tr>
                  ) : (
                    cotizaciones.map(renderFilaCotizacion)
                  )}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="paginacion">
                <button
                  disabled={page <= 1}
                  onClick={() => setPage(1)}
                  title="Primera página"
                >
                  «
                </button>
                <button
                  disabled={page <= 1}
                  onClick={() => setPage(p => p - 1)}
                >
                  Anterior
                </button>
                <span className="paginacion-info">Página</span>
                <select
                  className="paginacion-select"
                  value={page}
                  onChange={(e) => setPage(parseInt(e.target.value))}
                >
                  {Array.from({ length: totalPages }, (_, i) => (
                    <option key={i + 1} value={i + 1}>{i + 1}</option>
                  ))}
                </select>
                <span className="paginacion-info">de {totalPages}</span>
                <button
                  disabled={page >= totalPages}
                  onClick={() => setPage(p => p + 1)}
                >
                  Siguiente
                </button>
                <button
                  disabled={page >= totalPages}
                  onClick={() => setPage(totalPages)}
                  title="Última página"
                >
                  »
                </button>
              </div>
            )}
          </>
        )}
        </>
        )}

        {/* Contenedor oculto para generar PDF */}
        {cotizacionPDF && (
          <div className="pdf-hidden-container">
            <CotizacionPDF ref={pdfContainerRef} cotizacion={cotizacionPDF} todosLosEspacios={todosLosEspacios} />
          </div>
        )}

        {/* Modal: ¿Para qué necesitas el PDF? */}
        {pdfModalCot && (
          <div className="cot-pdf-modal-overlay" onClick={() => !generandoPDF && setPdfModalCot(null)}>
            <div className="cot-pdf-modal" onClick={e => e.stopPropagation()}>
              <h3>Descargar PDF</h3>
              <p className="cot-pdf-modal-sub">¿Para qué necesitas el PDF?</p>
              <div className="cot-pdf-options">
                <button
                  className="cot-pdf-option enviar"
                  disabled={generandoPDF}
                  onClick={() => {
                    const c = pdfModalCot;
                    setPdfModalCot(null);
                    descargarCotizacionPDF(c.id, 'enviar');
                  }}
                >
                  <span className="cot-pdf-option-icon">📧</span>
                  <span className="cot-pdf-option-title">Para Enviar</span>
                  <span className="cot-pdf-option-desc">PDF continuo, ideal para compartir digitalmente</span>
                </button>
                <button
                  className="cot-pdf-option imprimir"
                  disabled={generandoPDF}
                  onClick={() => {
                    const c = pdfModalCot;
                    setPdfModalCot(null);
                    descargarCotizacionPDF(c.id, 'imprimir');
                  }}
                >
                  <span className="cot-pdf-option-icon">🖨️</span>
                  <span className="cot-pdf-option-title">Para Imprimir</span>
                  <span className="cot-pdf-option-desc">PDF paginado, sin contenido cortado entre páginas</span>
                </button>
              </div>
              <button className="cot-pdf-cancel" disabled={generandoPDF} onClick={() => setPdfModalCot(null)}>Cancelar</button>
            </div>
          </div>
        )}
        {cotizacionViejaPDF && (
          <div className="pdf-hidden-container">
            <CotizacionViejaPDF ref={pdfViejaContainerRef} cotizacion={cotizacionViejaPDF} />
          </div>
        )}

        {/* Administrar zonas (CRUD de la lista del formulario) */}
        {modalZonas.visible && (
          <div className="modal-base-overlay" onClick={() => setModalZonas(m => ({ ...m, visible: false }))}>
            <div className="zonas-modal" onClick={e => e.stopPropagation()}>
              <div className="zonas-header">
                <div>
                  <h3>Zonas</h3>
                  <p>Estas son las opciones que aparecen en el campo <strong>Zona</strong> de la cotización.</p>
                </div>
                <button className="zonas-close" onClick={() => setModalZonas(m => ({ ...m, visible: false }))}>&times;</button>
              </div>

              <div className="zonas-nueva">
                <input
                  type="text"
                  placeholder="Nombre de la nueva zona"
                  value={modalZonas.nueva}
                  onChange={e => setModalZonas(m => ({ ...m, nueva: e.target.value }))}
                  onKeyDown={e => { if (e.key === 'Enter') crearZona(); }}
                />
                <button className="btn-primary" onClick={crearZona} disabled={!modalZonas.nueva.trim()}>+ Agregar</button>
              </div>

              <div className="zonas-body">
                {modalZonas.cargando ? (
                  <div className="zonas-vacio">Cargando...</div>
                ) : modalZonas.filas.length === 0 ? (
                  <div className="zonas-vacio">Todavía no hay zonas. Agrega la primera arriba.</div>
                ) : (
                  <table className="zonas-tabla">
                    <thead>
                      <tr><th>ZONA</th><th>EN USO</th><th>ESTADO</th><th>ACCIONES</th></tr>
                    </thead>
                    <tbody>
                      {modalZonas.filas.map(z => (
                        <tr key={z.id} className={!z.activo ? 'zona-inactiva' : ''}>
                          <td>
                            {modalZonas.editandoId === z.id ? (
                              <input
                                className="zonas-input-edit"
                                autoFocus
                                defaultValue={z.nombre}
                                onBlur={e => renombrarZona(z, e.target.value)}
                                onKeyDown={e => {
                                  if (e.key === 'Enter') e.target.blur();
                                  if (e.key === 'Escape') setModalZonas(m => ({ ...m, editandoId: null }));
                                }}
                              />
                            ) : (
                              <span className="zona-nombre">{z.nombre}</span>
                            )}
                          </td>
                          <td className="zona-uso">
                            {z.en_uso > 0 ? `${z.en_uso} cotización(es)` : <span className="zona-sin-uso">—</span>}
                          </td>
                          <td>
                            <span className={`zona-badge ${z.activo ? 'zona-badge-on' : 'zona-badge-off'}`}>
                              {z.activo ? 'Activa' : 'Inactiva'}
                            </span>
                          </td>
                          <td className="zona-acciones">
                            <button className="zona-btn" onClick={() => setModalZonas(m => ({ ...m, editandoId: z.id }))}>✎ Renombrar</button>
                            <button className="zona-btn" onClick={() => toggleZona(z)}>
                              {z.activo ? 'Desactivar' : 'Activar'}
                            </button>
                            <button
                              className="zona-btn zona-btn-danger"
                              onClick={() => eliminarZona(z)}
                              title={z.en_uso > 0 ? 'No se puede eliminar: está en uso' : 'Eliminar'}
                              disabled={z.en_uso > 0}
                            >🗑</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              <div className="zonas-footer">
                <p className="zonas-nota">
                  Una zona <strong>en uso</strong> no se puede eliminar. Desactívala para que deje de aparecer
                  en el desplegable sin afectar las cotizaciones que ya la tienen.
                </p>
                <button className="btn-secondary" onClick={() => setModalZonas(m => ({ ...m, visible: false }))}>Cerrar</button>
              </div>
            </div>
          </div>
        )}

        {/* Historial de cambios de la cotización (auditoría) */}
        {histModal.visible && (
          <div className="modal-base-overlay" onClick={() => setHistModal({ visible: false, cot: null, filas: [], cargando: false })}>
            <div className="hist-modal" onClick={e => e.stopPropagation()}>
              <div className="hist-header">
                <div>
                  <h3>Historial de cambios</h3>
                  <p>
                    COT-{String(histModal.cot?.consecutivo || '').padStart(4, '0')} · {histModal.cot?.nombre_cliente}
                    {histModal.cot?.proyecto_nombre ? ` · ${histModal.cot.proyecto_nombre}` : ''}
                  </p>
                </div>
                <button className="hist-close" onClick={() => setHistModal({ visible: false, cot: null, filas: [], cargando: false })}>&times;</button>
              </div>

              {histModal.cargando ? (
                <div className="hist-vacio">Cargando historial...</div>
              ) : histModal.filas.length === 0 ? (
                <div className="hist-vacio">
                  <p>Todavía no hay cambios registrados para esta cotización.</p>
                  <p className="hist-nota">
                    La auditoría registra los cambios a partir de su instalación; lo anterior a esa fecha no quedó guardado.
                  </p>
                </div>
              ) : (
                <div className="hist-body">
                  <table className="hist-tabla">
                    <thead>
                      <tr>
                        <th>FECHA</th>
                        <th>USUARIO</th>
                        <th>ORIGEN</th>
                        <th>CAMPO</th>
                        <th>ANTES</th>
                        <th>DESPUÉS</th>
                      </tr>
                    </thead>
                    <tbody>
                      {histModal.filas.map(h => (
                        <tr key={h.id}>
                          <td className="hist-fecha">
                            {h.fecha_cambio ? new Date(h.fecha_cambio).toLocaleString('es-CO', { day: '2-digit', month: 'short', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : '-'}
                          </td>
                          <td className="hist-user">{h.usuario_nombre || 'Sistema'}</td>
                          <td><span className={`hist-origen hist-origen-${h.origen || 'otro'}`}>{ETIQUETA_ORIGEN[h.origen] || h.origen || '—'}</span></td>
                          <td className="hist-campo" title={h.nota || ''}>{ETIQUETA_CAMPO[h.campo] || h.campo}</td>
                          <td className="hist-antes">{formatValorHist(h.campo, h.valor_anterior)}</td>
                          <td className="hist-despues">{formatValorHist(h.campo, h.valor_nuevo)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Modal de confirmación para marcar como Base */}
        {/* Al crear: normal o especial */}
        {modalNueva && (
          <div className="modal-base-overlay" onClick={() => setModalNueva(false)}>
            <div className="modal-base-content" onClick={e => e.stopPropagation()}>
              <div className="modal-base-header">
                <h3>Nueva Cotización</h3>
              </div>
              <div className="modal-base-body">
                <p className="modal-base-detail" style={{ marginBottom: 14 }}>
                  ¿Qué tipo de cotización vas a crear?
                </p>
                <div className="modal-base-options">
                  <button className="modal-base-option" onClick={() => iniciarNueva('nueva')}>
                    <span className="modal-base-option-title">📄 Cotización normal</span>
                    <span className="modal-base-option-desc">
                      La de siempre, con el baño que entrega la constructora. Cada actividad va una sola vez.
                    </span>
                  </button>
                  <button className="modal-base-option modal-base-option-web" onClick={() => iniciarNueva('especial')}>
                    <span className="modal-base-option-title">✨ Cotización especial</span>
                    <span className="modal-base-option-desc">
                      Sin el baño que entrega la constructora y con la misma actividad repetible.
                      Se guarda en la pestaña Cotizaciones Especiales.
                    </span>
                  </button>
                </div>
              </div>
              <div className="modal-base-footer">
                <button className="btn-secondary" onClick={() => setModalNueva(false)}>Cancelar</button>
              </div>
            </div>
          </div>
        )}

        {modalBase.visible && (
          <div className="modal-base-overlay" onClick={() => !modalBase.loading && cerrarModalBase()}>
            <div className="modal-base-content" onClick={e => e.stopPropagation()}>
              <div className="modal-base-header">
                <h3>Marcar como Base</h3>
              </div>
              <div className="modal-base-body">
                <p className="modal-base-detail" style={{ marginBottom: 14 }}>
                  ¿Qué tipo de base deseas asignar a esta cotización?
                </p>
                <div className="modal-base-options">
                  <button
                    className="modal-base-option"
                    onClick={() => elegirBase('sistema')}
                    disabled={modalBase.loading}
                  >
                    <span className="modal-base-option-title">⚑ Base del sistema</span>
                    <span className="modal-base-option-desc">
                      La base actual del sistema.
                      {modalBase.tipo === 'vieja'
                        ? ' Actualiza los precios unitarios de las actividades con los valores actuales de la matriz.'
                        : ''}
                    </span>
                  </button>
                  <button
                    className="modal-base-option modal-base-option-web"
                    onClick={() => elegirBase('cotizador_web')}
                    disabled={modalBase.loading}
                  >
                    <span className="modal-base-option-title">🌐 Base para cotizador web</span>
                    <span className="modal-base-option-desc">
                      Marca esta cotización como base para el cotizador web. No modifica los precios.
                    </span>
                  </button>
                </div>
              </div>
              <div className="modal-base-actions">
                <button
                  className="btn-secondary"
                  onClick={cerrarModalBase}
                  disabled={modalBase.loading}
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal motivo de pérdida al rechazar */}
        {modalRechazo.visible && (
          <div className="modal-base-overlay" onClick={() => !modalRechazo.guardando && setModalRechazo(m => ({ ...m, visible: false }))}>
            <div className="modal-base-content cot-rechazo-modal" onClick={e => e.stopPropagation()}>
              <div className="modal-base-header"><h3>Motivo de pérdida</h3></div>
              <div className="modal-base-body">
                <p className="cot-rechazo-ayuda">
                  Indica por qué se perdió esta cotización. Queda registrado para el informe de motivos de pérdida.
                </p>
                <div className="cot-rechazo-lista">
                  {MOTIVOS_RECHAZO.map(m => (
                    <label
                      key={m.codigo}
                      className={`cot-rechazo-op ${modalRechazo.motivo === m.codigo ? 'activa' : ''}`}
                    >
                      <input
                        type="radio"
                        name="motivo-rechazo"
                        value={m.codigo}
                        checked={modalRechazo.motivo === m.codigo}
                        onChange={() => setModalRechazo(prev => ({ ...prev, motivo: m.codigo }))}
                      />
                      <span>{m.etiqueta}</span>
                    </label>
                  ))}
                </div>
                {modalRechazo.motivo === 'otros' && (
                  <textarea
                    className="cot-rechazo-detalle"
                    rows={3}
                    autoFocus
                    maxLength={1000}
                    placeholder="Escribe cuál fue el motivo..."
                    value={modalRechazo.detalle}
                    onChange={e => setModalRechazo(prev => ({ ...prev, detalle: e.target.value }))}
                  />
                )}
              </div>
              <div className="modal-base-footer">
                <button
                  className="btn-secundario"
                  onClick={() => setModalRechazo(m => ({ ...m, visible: false }))}
                  disabled={modalRechazo.guardando}
                >Cancelar</button>
                <button
                  className="cot-rechazo-confirmar"
                  onClick={confirmarRechazo}
                  disabled={modalRechazo.guardando || !modalRechazo.motivo || (modalRechazo.motivo === 'otros' && !modalRechazo.detalle.trim())}
                >{modalRechazo.guardando ? 'Guardando...' : 'Rechazar cotización'}</button>
              </div>
            </div>
          </div>
        )}

        {/* Modal fecha probable inicio al contratado */}
        {modalContratado && (
          <div className="modal-base-overlay" onClick={() => setModalContratado(null)}>
            <div className="modal-base-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 420 }}>
              <div className="modal-base-header"><h3>Fecha Probable de Inicio</h3></div>
              <div className="modal-base-body">
                <p>Al marcar como <strong>contratado</strong>, debe indicar la fecha probable de inicio del proyecto:</p>
                <div style={{ margin: '16px 0' }}>
                  <label style={{ display: 'block', fontWeight: 600, fontSize: '0.85rem', marginBottom: 4 }}>Fecha de inicio *</label>
                  <input type="date" value={fechaInicioContratado} onChange={e => setFechaInicioContratado(e.target.value)}
                    style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: '0.9rem' }} />
                </div>
              </div>
              <div className="modal-base-actions">
                <button className="btn-secondary" onClick={() => setModalContratado(null)}>Cancelar</button>
                <button className="btn-primary" disabled={!fechaInicioContratado}
                  onClick={() => {
                    if (!fechaInicioContratado) return;
                    const { id, tipo } = modalContratado;
                    setModalContratado(null);
                    if (tipo === 'vieja') cambiarEstadoVieja(id, 'contratado', fechaInicioContratado);
                    else cambiarEstado(id, 'contratado', fechaInicioContratado);
                  }}>
                  Confirmar
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal: ¿venta por canal propio? (al marcar como vendida) */}
        {modalVenta.visible && (
          <div className="modal-base-overlay" onClick={() => { if (!modalVenta.loading) setModalVenta({ visible: false, cot: null, tipo: 'nueva', loading: false }); }}>
            <div className="modal-base-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 460 }}>
              <div className="modal-base-header"><h3>Registrar Venta</h3></div>
              <div className="modal-base-body">
                <p>Vas a marcar como <strong>vendida</strong> la cotización de <strong>{modalVenta.cot?.nombre_cliente || ''}</strong>{modalVenta.cot?.proyecto_nombre ? ` — ${modalVenta.cot.proyecto_nombre}` : ''}.</p>
                <p style={{ marginTop: 12 }}>¿La venta fue por <strong>canal propio</strong>?</p>
                <p style={{ fontSize: '0.8rem', color: '#6b7280', marginTop: 6 }}>
                  Si es por canal propio, además se registrará automáticamente en <strong>Marketing → Registro de Ventas</strong> (podrás editarla o eliminarla allí). Si no, solo se marca como vendida.
                </p>
              </div>
              <div className="modal-base-actions">
                <button className="btn-secondary" disabled={modalVenta.loading}
                  onClick={() => setModalVenta({ visible: false, cot: null, tipo: 'nueva', loading: false })}>Cancelar</button>
                <button className="btn-secondary" disabled={modalVenta.loading}
                  onClick={() => confirmarVenta(false)}>No</button>
                <button className="btn-primary" disabled={modalVenta.loading}
                  onClick={() => confirmarVenta(true)}>{modalVenta.loading ? 'Procesando...' : 'Sí, canal propio'}</button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Vista de crear/editar
  return (
    <div className="cotizaciones-container">
      <div className="cotizaciones-header">
        <div className="cotizaciones-header-left">
          <h2>
            {cotizacionEditando
              ? `Editar Cotización${esEspecial ? ' Especial' : ''} #${cotizacionEditando.consecutivo}`
              : `Nueva Cotización${esEspecial ? ' Especial' : ''}`}
          </h2>
          {(vista === 'crear' || vista === 'editar') && (
            <span className={`autosave-indicator ${autoguardando ? 'saving' : ultimoAutoguardado ? 'saved' : ''}`}>
              {autoguardando ? 'Guardando...' : ultimoAutoguardado ? `Guardado ${ultimoAutoguardado.toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit' })}` : ''}
            </span>
          )}
        </div>
        <button className="btn-secondary" onClick={() => { resetForm(); setVista('lista'); }}>
          ← Volver
        </button>
      </div>

      <div className="cotizacion-form">
        {/* Sección: Datos del Cliente */}
        <section className="form-section">
          <h3>Datos del Cliente</h3>
          <div className="form-grid">
            <div className="form-group">
              <label>Nombre del Cliente *</label>
              <input type="text" name="nombre_cliente" value={formData.nombre_cliente} onChange={handleInputChange} placeholder="Nombre completo" />
            </div>
            <div className="form-group">
              <label>Cédula/NIT *</label>
              <input type="text" name="cedula_cliente" value={formData.cedula_cliente} onChange={handleInputChange} placeholder="Documento de identidad" />
            </div>
            <div className="form-group">
              <label>Teléfono *</label>
              <input type="text" name="telefono_cliente" value={formData.telefono_cliente} onChange={handleInputChange} placeholder="Numero de contacto" />
            </div>
            <div className="form-group">
              <label>Correo Electrónico *</label>
              <input type="email" name="correo_cliente" value={formData.correo_cliente} onChange={handleInputChange} placeholder="correo@ejemplo.com" />
            </div>
            <div className="form-group">
              <label>Zona / Barrio *</label>
              <input type="text" name="zona_barrio" value={formData.zona_barrio} onChange={handleInputChange} placeholder="Ej: Fatima, Itagui" />
            </div>
          </div>
        </section>

        {/* Sección: Datos del Proyecto */}
        <section className="form-section">
          <h3>Datos del Proyecto</h3>
          <div className="form-grid">
            <div className="form-group">
              <label>Nombre del Proyecto *</label>
              <input
                type="text"
                name="proyecto_nombre"
                value={formData.proyecto_nombre}
                onChange={handleInputChange}
                placeholder="Nombre del proyecto"
              />
            </div>
            <div className="form-group">
              <label>Apartamento</label>
              <input
                type="text"
                name="apartamento"
                value={formData.apartamento}
                onChange={handleInputChange}
                placeholder="Número de apartamento"
              />
            </div>
            <div className="form-group">
              <label>Área (m²)</label>
              <input
                type="number"
                name="area_m2"
                value={formData.area_m2}
                onChange={handleInputChange}
                placeholder="Metros cuadrados"
                step="0.01"
              />
            </div>
            <div className="form-group">
              <label>Dirección</label>
              <input
                type="text"
                name="direccion"
                value={formData.direccion}
                onChange={handleInputChange}
                placeholder="Dirección del proyecto"
              />
            </div>
            <div className="form-group">
              <label>Habitaciones</label>
              <input
                type="number"
                name="habitaciones"
                value={formData.habitaciones}
                onChange={handleInputChange}
                placeholder="0"
                min="0"
              />
            </div>
            <div className="form-group">
              <label>Baños</label>
              <input
                type="number"
                name="banos"
                value={formData.banos}
                onChange={handleInputChange}
                placeholder="0"
                min="0"
              />
            </div>
            <div className="form-group">
              <label>Zona</label>
              <select name="zona" value={formData.zona || ''} onChange={handleInputChange}>
                <option value="">Seleccione...</option>
                {zonas.map(z => <option key={z.id} value={z.nombre}>{z.nombre}</option>)}
                {/* Si la cotizacion trae una zona que ya no esta activa, se sigue mostrando */}
                {formData.zona && !zonas.some(z => z.nombre === formData.zona) && (
                  <option value={formData.zona}>{formData.zona} (inactiva)</option>
                )}
              </select>
            </div>
            <div className="form-group form-group-full medidas-banos-section">
              <label className="medidas-banos-titulo">Baño Social <span style={{ fontSize: '0.72rem', color: '#6366f1', fontWeight: 500, fontStyle: 'italic', marginLeft: 8 }}>Lado A = lado mas grande</span></label>
              <div className="medidas-banos-row">
                <div className="medidas-banos-campo">
                  <span className="medidas-banos-label">Lado A</span>
                  <input
                    type="number"
                    name="bano_social_alto"
                    value={formData.bano_social_alto}
                    onChange={handleInputChange}
                    placeholder="0"
                    step="0.01"
                    min="0"
                  />
                </div>
                <div className="medidas-banos-campo">
                  <span className="medidas-banos-label">Lado B</span>
                  <input
                    type="number"
                    name="bano_social_ancho"
                    value={formData.bano_social_ancho}
                    onChange={handleInputChange}
                    placeholder="0"
                    step="0.01"
                    min="0"
                  />
                </div>
                <div className="medidas-banos-area">
                  Área: <strong>{((parseFloat(formData.bano_social_alto) || 0) * (parseFloat(formData.bano_social_ancho) || 0)).toFixed(2)} m²</strong>
                </div>
              </div>
            </div>
            <div className="form-group form-group-full medidas-banos-section">
              <label className="medidas-banos-titulo">Baño Principal <span style={{ fontSize: '0.72rem', color: '#6366f1', fontWeight: 500, fontStyle: 'italic', marginLeft: 8 }}>Lado A = lado mas grande</span></label>
              <div className="medidas-banos-row">
                <div className="medidas-banos-campo">
                  <span className="medidas-banos-label">Lado A</span>
                  <input
                    type="number"
                    name="bano_ppal_alto"
                    value={formData.bano_ppal_alto}
                    onChange={handleInputChange}
                    placeholder="0"
                    step="0.01"
                    min="0"
                  />
                </div>
                <div className="medidas-banos-campo">
                  <span className="medidas-banos-label">Lado B</span>
                  <input
                    type="number"
                    name="bano_ppal_ancho"
                    value={formData.bano_ppal_ancho}
                    onChange={handleInputChange}
                    placeholder="0"
                    step="0.01"
                    min="0"
                  />
                </div>
                <div className="medidas-banos-area">
                  Área: <strong>{((parseFloat(formData.bano_ppal_alto) || 0) * (parseFloat(formData.bano_ppal_ancho) || 0)).toFixed(2)} m²</strong>
                </div>
              </div>
            </div>
            <div className="form-group">
              <label>Balcón (m²)</label>
              <input
                type="number"
                name="balcon"
                value={formData.balcon}
                onChange={handleInputChange}
                placeholder="0"
                step="0.01"
                min="0"
              />
            </div>
            <div className="form-group">
              <label>Altura Ducha (m)</label>
              <input
                type="number"
                name="altura_ducha"
                value={formData.altura_ducha}
                onChange={handleInputChange}
                placeholder="0"
                step="0.01"
                min="0"
              />
            </div>
            <div className="form-group">
              <label>Número de Puertas</label>
              <input
                type="number"
                name="numero_puertas"
                value={formData.numero_puertas}
                onChange={handleInputChange}
                placeholder="0"
                min="0"
              />
            </div>
            <div className="form-group">
              <label>Metros de Zócalo</label>
              <input
                type="number"
                name="metros_zocalo"
                value={formData.metros_zocalo}
                onChange={handleInputChange}
                placeholder="0"
                step="0.01"
                min="0"
              />
              {formData.area_m2 && (
                <span className="valor-sugerido">Valor sugerido: Área + 12 = {(parseFloat(formData.area_m2) + 12).toFixed(1)}</span>
              )}
            </div>
            <div className="form-group">
              <label>Posible Fecha de Entrega</label>
              <input
                type="date"
                name="fecha"
                value={formData.fecha}
                onChange={handleInputChange}
              />
            </div>
            {/* El incremento se sigue aplicando igual; solo se muestra a quien
                tenga el permiso "Cotizador - Ver Incremento Actividades". */}
            {puedeVerIncremento && (
              <div className="form-group">
                <label>Incremento Actividades (%) *</label>
                <input
                  type="number"
                  name="incremento_global"
                  value={formData.incremento_global}
                  onChange={handleInputChange}
                  min="0"
                  step="0.01"
                  placeholder="0"
                />
              </div>
            )}
          </div>
        </section>


        {/* Sección: Selector de Plano */}
        <section className="form-section">
          <h3>Plano del Proyecto</h3>
          <div className="plano-selector">
            <div className="form-group">
              <label>Seleccionar Plano</label>
              <PlanoSelect
                value={formData.plano_id}
                onChange={(id) => setFormData(prev => ({ ...prev, plano_id: id }))}
                planos={planos}
              />
            </div>
            {getPlanoSeleccionado() && (
              <div className="plano-preview">
                <h4>{getPlanoSeleccionado().nombre}</h4>
                <img
                  src={(() => {
                    const url = getPlanoSeleccionado().url_imagen || '';
                    if (url.startsWith('minio:')) {
                      const key = url.replace('minio:planos/', '');
                      return `https://api.tuvisistema.com/upload/plano-img/${encodeURIComponent(key)}`;
                    }
                    return `https://tuvisistema.com/backend/api/documentos/proxy-imagen.php?plano_id=${getPlanoSeleccionado().id}&url=${encodeURIComponent(url)}`;
                  })()}
                  alt={getPlanoSeleccionado().nombre}
                  onError={(e) => {
                    e.target.style.display = 'none';
                    if (e.target.nextSibling) e.target.nextSibling.style.display = 'block';
                  }}
                />
                <div className="plano-error" style={{display: 'none'}}>
                  <p>No se pudo cargar la imagen</p>
                  <a
                    href={getPlanoSeleccionado().url_imagen}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-ver-plano"
                  >
                    📷 Ver Plano en nueva pestaña
                  </a>
                </div>
              </div>
            )}
          </div>
        </section>

        {/* Sección: Forma de Cocina */}
        <section className="form-section">
          <h3>Forma de Cocina</h3>
          <div className="formas-cocina-grid">
            {FORMAS_COCINA.map(forma => (
              <div
                key={forma.id}
                className={`forma-cocina-item ${formData.forma_cocina === forma.id ? 'selected' : ''}`}
                onClick={() => handleFormaCocinaChange(forma.id)}
              >
                <img src={forma.imagen} alt={forma.label} />
                <span>{forma.label}</span>
              </div>
            ))}
          </div>
        </section>

        {/* Sección: Baño que entrega la constructora (no va en las especiales) */}
        {!esEspecial && (
        <section className="form-section">
          <h3>Baño que Entrega la Constructora</h3>
          <p className="section-help">Seleccione los elementos que incluye el baño entregado por la constructora</p>
          <div className="opciones-bano-grid">
            {OPCIONES_BANO.map(opcion => (
              <label key={opcion.id} className="checkbox-item">
                <input
                  type="checkbox"
                  checked={formData.opciones_bano[opcion.id] || false}
                  onChange={() => handleOpcionBanoChange(opcion.id)}
                />
                <span>{opcion.label}</span>
              </label>
            ))}
          </div>
        </section>
        )}

        {/* Sección: Selección de Actividades */}
        <section className="form-section">
          <h3>Actividades de la Cotización</h3>

          <div className="actividades-selector">
            <div className="selector-controles selector-3-niveles">
              <div className="selector-nivel">
                <label>Categoría</label>
                <select
                  value={categoriaActiva}
                  onChange={(e) => setCategoriaActiva(e.target.value)}
                >
                  <option value="">Seleccionar categoría</option>
                  {ORDEN_CATEGORIAS.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>

              {categoriaActiva && (
                <div className="selector-nivel">
                  <label>Espacio</label>
                  <select
                    value={espacioActivo}
                    onChange={(e) => {
                      setEspacioActivo(e.target.value);
                      setBuscarActividad('');
                    }}
                  >
                    <option value="">Seleccionar espacio</option>
                    {espaciosDisponibles.map(esp => (
                      <option key={esp} value={esp}>{esp}</option>
                    ))}
                  </select>
                </div>
              )}

              {categoriaActiva && espacioActivo && (
                <div className="selector-nivel selector-buscar">
                  <label>Buscar</label>
                  <input
                    type="text"
                    placeholder="Filtrar actividades..."
                    value={buscarActividad}
                    onChange={(e) => setBuscarActividad(e.target.value)}
                  />
                </div>
              )}
            </div>

            {categoriaActiva && espacioActivo && (
              <div className="actividades-disponibles">
                {loadingActividades ? (
                  <div className="loading-small">Cargando actividades...</div>
                ) : actividadesDisponibles.length === 0 ? (
                  <div className="sin-actividades">No se encontraron actividades</div>
                ) : (
                  <table className="tabla-actividades-disponibles">
                    <thead>
                      <tr>
                        <th>Código</th>
                        <th>Actividad</th>
                        <th>Unidad</th>
                        <th>Valor Final</th>
                        <th>Acción</th>
                      </tr>
                    </thead>
                    <tbody>
                      {actividadesDisponibles.map(act => {
                        const vf = calcularValorFinalActividad(act);
                        return (
                          <tr key={act.id}>
                            <td>{act.codigo}</td>
                            <td>{act.actividad}</td>
                            <td>{act.unidad}</td>
                            <td className="valor-final-celda">
                              {vf > 0 ? `$${Math.floor(vf).toLocaleString('es-CO')}` : '-'}
                            </td>
                            <td>
                              <button
                                className="btn-agregar-actividad"
                                onClick={() => agregarActividad(act)}
                              >
                                + Agregar
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            )}
          </div>

          {/* Actividades Seleccionadas */}
          <div className="actividades-seleccionadas">
            <h4>Actividades Agregadas ({actividadesSeleccionadas.length})</h4>

            {modalOpciones !== null && actividadesSeleccionadas[modalOpciones] && (() => {
              const act = actividadesSeleccionadas[modalOpciones];
              const ops = opcionesDe(act);
              if (!ops) return null;
              const cant = parseFloat(act.cantidad) || 0;
              const totalActual = parseFloat(act.valor_total) || 0;
              const lista = [...ops.grupo].sort((a, b) => precioUnitarioCot(a) - precioUnitarioCot(b) || String(a.codigo).localeCompare(String(b.codigo)));
              return (
                <div className="cot-opc-overlay" onClick={() => setModalOpciones(null)}>
                  <div className="cot-opc-modal" onClick={e => e.stopPropagation()}>
                    <div className="cot-opc-header">
                      <div>
                        <h3>Opciones de {ops.actual.subcategoria_espacio}</h3>
                        <p>{ops.actual.espacio} · {lista.length} opciones de menor a mayor valor · cantidad en la cotización: {cant} {act.unidad}</p>
                      </div>
                      <button className="cot-opc-cerrar" onClick={() => setModalOpciones(null)}>×</button>
                    </div>
                    <div className="cot-opc-body">
                      <table className="cot-opc-tabla">
                        <thead>
                          <tr><th>Código</th><th>Actividad</th><th>Und</th><th>Valor unit.</th><th>Total en la cotización</th><th>Diferencia</th><th></th></tr>
                        </thead>
                        <tbody>
                          {lista.map(m => {
                            const esActual = m.id === act.actividad_id;
                            const yaEsta = !esActual && !esEspecial && actividadesSeleccionadas.some(a => a.actividad_id === m.id);
                            const mismaUnidad = normalizarTxt(m.unidad) === normalizarTxt(act.unidad);
                            const c = mismaUnidad ? cant : resolverCantidad(m.cantidad_predeterminada, m.cantidad_default, formData);
                            const total = precioUnitarioCot(m) * c;
                            const dif = total - totalActual;
                            return (
                              <tr key={m.id} className={esActual ? 'cot-opc-actual' : ''}>
                                <td className="cot-opc-codigo">{m.codigo}</td>
                                <td>{m.actividad}{!mismaUnidad && <div className="cot-opc-nota">Otra unidad: cantidad {c} {m.unidad}</div>}</td>
                                <td>{m.unidad}</td>
                                <td className="cot-opc-num">{formatCurrency(precioUnitarioCot(m))}</td>
                                <td className="cot-opc-num"><strong>{formatCurrency(total)}</strong></td>
                                <td className={`cot-opc-num ${esActual ? '' : dif > 0 ? 'cot-opc-mas' : dif < 0 ? 'cot-opc-menos' : ''}`}>
                                  {esActual ? '—' : `${dif > 0 ? '+' : dif < 0 ? '−' : ''}${formatCurrency(Math.abs(dif))}`}
                                </td>
                                <td>
                                  {esActual ? <span className="cot-opc-tag">Actual</span>
                                    : yaEsta ? <span className="cot-opc-tag gris" title="Ya está en la cotización">Ya agregada</span>
                                    : <button className="cot-opc-usar" onClick={() => intercambiarActividad(modalOpciones, m)}>Usar esta</button>}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              );
            })()}

            {actividadesPorEspacio.length === 0 ? (
              <div className="sin-actividades">
                No hay actividades agregadas. Seleccione un capítulo y agregue actividades.
              </div>
            ) : (
              actividadesPorEspacio.map(([espacio, actividades]) => (
                <div key={espacio} className="cap-acts-agregadas">
                  <h5>{espacio}</h5>
                  <table>
                    <thead>
                      <tr>
                        <th>Código</th>
                        <th>Descripción</th>
                        <th>Espacio</th>
                        <th>Unidad</th>
                        <th>Cantidad</th>
                        <th>Valor Unit.</th>
                        <th>Valor Total</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {actividades.map((act, idx) => {
                        const gi = act._globalIndex;
                        return (
                          <tr key={`${act.actividad_id}-${gi}`} className={act.activo_matriz === 0 ? 'actividad-inactiva' : ''} title={act.activo_matriz === 0 ? 'Actividad inactiva en la matriz' : ''}>
                            <td>{act.codigo}</td>
                            <td>
                              <textarea
                                value={act.descripcion}
                                onChange={(e) => actualizarActividad(gi, 'descripcion', e.target.value)}
                                className="input-descripcion"
                                rows={1}
                                onInput={(e) => { e.target.style.height = 'auto'; e.target.style.height = e.target.scrollHeight + 'px'; }}
                                ref={(el) => { if (el) { el.style.height = 'auto'; el.style.height = el.scrollHeight + 'px'; } }}
                              />
                            </td>
                            <td>
                              <select
                                value={act.espacio || ''}
                                onChange={(e) => actualizarActividad(gi, 'espacio', e.target.value)}
                                className="select-espacio"
                              >
                                <option value="">Sin espacio</option>
                                {todosLosEspacios.map(esp => (
                                  <option key={esp} value={esp}>{esp}</option>
                                ))}
                              </select>
                            </td>
                            <td>{act.unidad}</td>
                            <td>
                              <input
                                type="number"
                                value={act.cantidad}
                                onChange={(e) => actualizarActividad(gi, 'cantidad', e.target.value)}
                                min="0"
                                step="0.01"
                                className="input-cantidad"
                              />
                            </td>
                            {/* El valor unitario viene de la matriz de actividades (con
                                el incremento global aplicado) y normalmente no se toca.
                                Solo con el permiso especial se puede sobreescribir. */}
                            {puedeEditarValorUnitario ? (
                              <td>
                                <input
                                  type="number"
                                  className="input-valor-unitario"
                                  value={act.valor_unitario ?? ''}
                                  min="0"
                                  title="Valor de la matriz. Puedes cambiarlo solo para esta cotización."
                                  onChange={(e) => actualizarActividad(gi, 'valor_unitario', e.target.value)}
                                />
                              </td>
                            ) : (
                              <td className="valor-unitario-fijo" title="Este valor viene de la matriz de actividades">
                                {formatCurrency(act.valor_unitario)}
                              </td>
                            )}
                            <td className="valor-total">{formatCurrency(act.valor_total)}</td>
                            <td className="cot-acciones-act">
                              {opcionesDe(act) && (
                                <button
                                  className="btn-opciones-actividad"
                                  onClick={() => setModalOpciones(gi)}
                                  title={`Ver otras opciones de ${opcionesDe(act).actual.subcategoria_espacio} (${opcionesDe(act).grupo.length - 1} más) para cambiarla`}
                                >
                                  ⇄
                                </button>
                              )}
                              {puedeBloquear && (
                                <button
                                  className={`btn-bloquear-actividad ${act.bloqueada ? 'activo' : ''}`}
                                  onClick={() => alternarBloqueo(gi, act)}
                                  title={act.bloqueada ? 'Bloqueada: nadie la puede eliminar. Clic para desbloquear.' : 'Bloquear para que no se pueda eliminar'}
                                >
                                  {act.bloqueada ? '🔒' : '🔓'}
                                </button>
                              )}
                              {act.bloqueada && !puedeBloquear ? (
                                <span className="cot-candado" title="Actividad bloqueada: no se puede eliminar">🔒</span>
                              ) : (
                                <button
                                  className="btn-eliminar-actividad"
                                  onClick={() => eliminarActividad(gi)}
                                  title="Eliminar"
                                >
                                  ×
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ))
            )}
          </div>
        </section>

        {/* Sección: Totales */}
        <section className="form-section totales-section">
          <h3>Resumen de Valores</h3>
          <div className="totales-grid">
            <div className="total-row">
              <span>Subtotal:</span>
              <span className="valor">{formatCurrency(totales.subtotal)}</span>
            </div>
            <div className="total-row">
              <span>AIU ({totales.aiu_porcentaje}%):</span>
              <span className="valor">{formatCurrency(totales.aiu_valor)}</span>
            </div>
            <div className="total-row">
              <span>IVA (19% x 5%):</span>
              <span className="valor">{formatCurrency(totales.iva_valor)}</span>
            </div>
            <div className="total-row total-principal">
              <span>Valor Final:</span>
              <span className="valor">{formatCurrency(totales.valor_final)}</span>
            </div>
            <div className="total-row descuento-row">
              <span>
                Descuento:
                {!puedeDescuentoLibre && (
                  <small className="descuento-tope">máx. 3% ({formatCurrency(topeDescuento)})</small>
                )}
              </span>
              <input
                type="number"
                name="descuento"
                value={formData.descuento}
                onChange={handleDescuentoChange}
                min="0"
                max={puedeDescuentoLibre ? undefined : topeDescuento}
                className={`input-descuento${descuentoExcedido ? ' input-descuento-error' : ''}`}
              />
            </div>
            {descuentoExcedido && (
              <div className="descuento-aviso">
                El descuento no puede superar el 3% del subtotal ({formatCurrency(topeDescuento)}).
                Se requiere permiso especial para dar más.
              </div>
            )}
            <div className="total-row total-final">
              <span>VALOR FINAL CON DESCUENTO:</span>
              <span className="valor">{formatCurrency(totales.valor_final_descuento)}</span>
            </div>
          </div>
        </section>

        {/* Plan de Pagos */}
        <section className="form-section">
          <h3>Plan de Pagos</h3>
          <div className="plan-pagos-editor">
            <table className="plan-pagos-table">
              <thead>
                <tr>
                  <th>Descripcion</th>
                  <th>Porcentaje (%)</th>
                  <th>Valor</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {formData.plan_pagos.map((pago, index) => {
                  const valorRef = totales.valor_final_descuento || totales.valor_final;
                  const valorPago = Math.round(valorRef * (parseFloat(pago.porcentaje) || 0) / 100);
                  return (
                    <tr key={index}>
                      <td>
                        <input
                          type="text"
                          value={pago.descripcion}
                          onChange={(e) => handlePlanPagoChange(index, 'descripcion', e.target.value)}
                          placeholder="Descripcion del pago"
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          value={pago.porcentaje}
                          onChange={(e) => handlePlanPagoChange(index, 'porcentaje', e.target.value)}
                          min="0"
                          max="100"
                          step="1"
                        />
                      </td>
                      <td className="valor-pago">{formatCurrency(valorPago)}</td>
                      <td>
                        {formData.plan_pagos.length > 1 && (
                          <button
                            type="button"
                            className="btn-remove-pago"
                            onClick={() => eliminarFilaPago(index)}
                          >
                            x
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr>
                  <td><strong>Total</strong></td>
                  <td><strong className={sumaPorcentajesPago !== 100 ? 'porcentaje-error' : 'porcentaje-ok'}>{sumaPorcentajesPago}%</strong></td>
                  <td><strong>{formatCurrency(totales.valor_final_descuento || totales.valor_final)}</strong></td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
            {sumaPorcentajesPago !== 100 && (
              <div className="plan-pagos-warning">Los porcentajes deben sumar exactamente 100% (actual: {sumaPorcentajesPago}%)</div>
            )}
            {formData.plan_pagos.length < 8 && (
              <button type="button" className="btn-add-pago" onClick={agregarFilaPago}>
                + Agregar fila
              </button>
            )}
          </div>
        </section>

        {/* Notas */}
        <section className="form-section">
          <h3>Notas Adicionales</h3>
          <textarea
            name="notas"
            value={formData.notas}
            onChange={handleInputChange}
            rows="4"
            placeholder="Observaciones, condiciones especiales, etc."
          />
        </section>

        {/* Botones de acción */}
        <div className="form-actions">
          <button
            className="btn-secondary"
            onClick={() => { resetForm(); setVista('lista'); }}
          >
            Cancelar
          </button>
          <button
            className="btn-borrador"
            onClick={() => guardarCotizacion(true)}
            disabled={loading}
          >
            {loading ? 'Guardando...' : 'Guardar Borrador'}
          </button>
          <button
            className="btn-primary"
            onClick={() => guardarCotizacion(false)}
            disabled={loading}
          >
            {loading ? 'Guardando...' : (cotizacionEditando ? 'Actualizar Cotización' : 'Guardar Cotización')}
          </button>
        </div>
      </div>

    </div>
  );
};

export default Cotizaciones;
